// ERComex Data Service Layer — Extended with Checklist, Cobranzas, Clientes full CRUD
import { createClient } from '@/lib/supabase/client';

// ============================================================
// TYPES
// ============================================================

export type ServiceType =
  | 'Solo despacho' | 'Solo flete internacional' | 'Despacho + flete internacional'
  | 'Asesoramiento' | 'Otro';

export type OperationStatus =
  | 'apertura-incompleta' | 'esperando-documentacion' | 'documentacion-observada'
  | 'esperando-draft' | 'intervencion-pendiente' | 'esperando-proforma' | 'esperando-fondos'
  | 'listo-para-oficializar' | 'oficializado' | 'pendiente-de-entrega'
  | 'entregado-falta-facturar' | 'facturado-falta-cobrar' | 'cobrado-listo-para-cerrar'
  | 'cerrado' | 'demorado' | 'observado' | 'cancelado';

export type InvoiceStatus =
  | 'emitida' | 'enviada' | 'pendiente-cobro' | 'pendiente' | 'cobrada-parcial' | 'cobrada-total' | 'anulada';

export type ChecklistState = 'pendiente' | 'en-proceso' | 'hecho' | 'no-aplica' | 'observado';

// Quick View value type: SI / NO / N/A / OBS
export type QuickValue = 'si' | 'no' | 'na' | 'obs';

// Quick view column keys
export type QuickViewField =
  | 'qvMandadoCotizar' | 'qvCotizacionEnviada' | 'qvCotizacionAceptada'
  | 'qvCarpetaTransporte'| 'qvEnRecoleccion' | 'qvDraft' | 'qvAnmat' |'qvProforma' | 'qvArribo' | 'qvOficializacion' | 'qvRemito' | 'qvEntregadoCliente';

// Extra data fields for quick view columns that require additional info
export interface QuickViewExtraData {
  // Oficialización
  nDespacho?: string;
  fechaOficializacion?: string;
  canalOficializacion?: string;
  obsOficializacion?: string;
  // Remito
  nRemito?: string;
  // Entregado al cliente
  fechaEntrega?: string;
  obsEntregado?: string;
  // Carpeta transporte
  nCarpetaTransporteNro?: string;
  // Arribo
  fechaArribo?: string;
  // Draft OBS
  obsDraft?: string;
  // ANMAT OBS
  obsAnmat?: string;
}

// ============================================================
// MULTI-PAYMENT TYPES
// ============================================================

export type MedioCobro = 'Transferencia' | 'E-cheq' | 'Cheque físico' | 'Efectivo ARS' | 'Efectivo USD' | 'Mercado Pago' | 'Otro';

export interface CobroMedioPago {
  id?: string;
  cobranzaId?: string;
  medio: MedioCobro | string;
  cuentaDestino?: string;
  monto: number;
  moneda: string;
  tipoCambio?: number;
  observaciones?: string;
  // E-cheq detail (if medio === 'E-cheq')
  echeqs?: EcheqDetalle[];
}

export type EcheqEstado = 'en-cartera' | 'recibido' | 'depositado' | 'cedido' | 'cobrado' | 'rechazado' | 'anulado';

export interface EcheqDetalle {
  id?: string;
  cobranzaId?: string;
  cobroMedioId?: string;
  bancoEmisor?: string;
  nEcheq?: string;
  cuitLibrador?: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  monto: number;
  moneda: string;
  estado: EcheqEstado;
  clienteNombre?: string;
  nCarpeta?: string;
  facturaId?: string;
  observaciones?: string;
  // ID of an existing echeq in the echeqs table (cartera) — when set, update monto_usado instead of inserting
  echeqCarteraId?: string;
}

export interface ChecklistItemDB {
  id: string;
  operacionId: string;
  bloque: string;
  itemKey: string;
  label: string;
  estado: ChecklistState;
  observacion?: string;
  updatedAt?: string;
}

export interface Operation {
  id: string;
  nOperacion: string;
  nCarpetaDespacho: string;
  nCarpetaTransporte?: string;
  clienteId?: string;
  clienteNombre: string;
  referencia: string;
  proveedor: string;
  tipoOperacion: string;
  servicio: ServiceType;
  responsable: string;
  fechaApertura: string;
  estado: OperationStatus;
  estadoManual?: EstadoManual;
  proximoPaso: string;
  linkDrive?: string;
  fechaArriboEstimado?: string;
  fechaOficializacion?: string;
  nDespacho?: string;
  nRemito?: string;
  fechaEntrega?: string;
  facturaDespacho?: string;
  facturaTransporte?: string;
  reciboDespacho?: string;
  reciboTransporte?: string;
  fechaCierre?: string;
  observaciones?: string;
  checklistProgress: number;
  ultimaActualizacion: string;
  // Legacy boolean fields (kept for backward compat)
  mandadoCotizar: boolean;
  cotizacionEnviada: boolean;
  cotizacionAceptada: boolean;
  enRecoleccion: boolean;
  draft: boolean;
  anmat: boolean;
  proforma: boolean;
  arribo: boolean;
  remito: boolean;
  entregadoCliente: boolean;
  // Quick view text fields (SI/NO/N/A/OBS)
  qvMandadoCotizar?: QuickValue | null;
  qvCotizacionEnviada?: QuickValue | null;
  qvCotizacionAceptada?: QuickValue | null;
  qvCarpetaTransporte?: QuickValue | null;
  qvEnRecoleccion?: QuickValue | null;
  qvDraft?: QuickValue | null;
  qvAnmat?: QuickValue | null;
  qvProforma?: QuickValue | null;
  qvArribo?: QuickValue | null;
  qvOficializacion?: QuickValue | null;
  qvRemito?: QuickValue | null;
  qvEntregadoCliente?: QuickValue | null;
  // Extra data for quick view
  qvObsDraft?: string | null;
  qvObsAnmat?: string | null;
  qvObsCarpetaTransporte?: string | null;
  qvNCarpetaTransporteNro?: string | null;
  qvFechaArribo?: string | null;
  qvCanalOficializacion?: string | null;
  qvObsEntregado?: string | null;
  deletedAt?: string;
}

export interface Invoice {
  id: string;
  fecha: string;
  clienteId?: string;
  clienteNombre: string;
  puntoVenta: string;
  tipoComprobante: string;
  nFactura: string;
  nCarpeta: string;
  operacionId?: string;
  concepto: string;
  importe: number;
  base: number;
  baseExenta: number;
  alicuotaIva: number;
  iva21: number;
  moneda: string;
  tipoOperativa: string;
  nRecibo?: string;
  fechaCobro?: string;
  recibido: number;
  retencionGanancias: number;
  retencionIIBB: number;
  otrasRetenciones: number;
  retencionesTotales: number;
  mesAnio?: string;
  neto: number;
  saldo: number;
  estado: InvoiceStatus;
  observaciones?: string;
  // Historical tracking
  origenRegistro?: 'historico_importado' | 'operativo_real' | 'historico' | 'nuevo';
  origen?: string;
  incluirEnSeguimientoCobranza?: boolean;
  impactaCaja?: boolean;
  motivoAnulacion?: string;
  deletedAt?: string;
}

export interface Cliente {
  id: string;
  razonSocial: string;
  cuit?: string;
  contacto?: string;
  email?: string;
  telefono?: string;
  direccion?: string;
  observaciones?: string;
}

export interface Cobranza {
  id: string;
  clienteId?: string;
  clienteNombre: string;
  facturaId?: string;
  nFactura?: string;
  nCarpeta: string;
  operacionId?: string;
  fechaCobro: string;
  monto: number;
  moneda: string;
  medioCobro: string;
  cuentaIngreso: string;
  nRecibo: string;
  retencionGanancias: number;
  retencionIIBB: number;
  otrasRetenciones: number;
  comprobante?: string;
  observaciones?: string;
  noImputado?: boolean;
  // Multi-payment lines (loaded separately)
  mediosPago?: CobroMedioPago[];
  deletedAt?: string;
}

export interface Proveedor {
  id: string;
  nombre: string;
  cuit?: string;
  tipo?: string;
  contacto?: string;
  email?: string;
  telefono?: string;
  condicionPago?: string;
  cuentaBancaria?: string;
  observaciones?: string;
}

export interface CuentaCaja {
  id: string;
  nombre: string;
  tipo?: string;
  moneda: string;
  saldoInicial: number;
  saldoActual: number;
  activa: boolean;
}

export interface AuditEntry {
  usuarioNombre?: string;
  modulo: string;
  accion: string;
  registroId?: string;
  registroTipo?: string;
  campoModificado?: string;
  valorAnterior?: string;
  valorNuevo?: string;
  motivo?: string;
}

export interface OperacionHistorial {
  id: string;
  operacionId: string;
  usuarioId?: string;
  usuarioNombre?: string;
  accion: string;
  campoModificado?: string;
  valorAnterior?: string;
  valorNuevo?: string;
  observacion?: string;
  createdAt: string;
}

// ============================================================
// CHECKLIST DEFINITION
// ============================================================

export interface ChecklistBlockDef {
  key: string;
  label: string;
  items: { key: string; label: string }[];
}

export const CHECKLIST_BLOCKS: ChecklistBlockDef[] = [
  {
    key: 'cotizacion',
    label: 'Cotización',
    items: [
      { key: 'mandado_cotizar', label: 'Mandado a cotizar' },
      { key: 'cotizacion_enviada', label: 'Cotización enviada' },
      { key: 'cotizacion_aceptada', label: 'Cotización aceptada' },
    ],
  },
  {
    key: 'transporte',
    label: 'Transporte',
    items: [
      { key: 'carpeta_transporte', label: 'Carpeta transporte creada (si aplica)' },
      { key: 'en_recoleccion', label: 'En recolección (si aplica)' },
      { key: 'draft_recibido', label: 'Draft recibido' },
      { key: 'draft_controlado', label: 'Draft controlado' },
    ],
  },
  {
    key: 'intervenciones',
    label: 'Intervenciones',
    items: [
      { key: 'int_anmat', label: 'ANMAT' },
      { key: 'int_inal', label: 'INAL' },
      { key: 'int_senasa', label: 'SENASA' },
      { key: 'int_seg_electrica', label: 'Seguridad Eléctrica' },
      { key: 'int_otro', label: 'Otro organismo' },
    ],
  },
  {
    key: 'proforma',
    label: 'Proforma',
    items: [
      { key: 'proforma_preparada', label: 'Proforma preparada' },
      { key: 'proforma_enviada', label: 'Proforma enviada' },
      { key: 'fondos_confirmados', label: 'Fondos confirmados' },
    ],
  },
  {
    key: 'aduana',
    label: 'Aduana',
    items: [
      { key: 'arribo', label: 'Arribo' },
      { key: 'despacho_oficializado', label: 'Oficialización' },
      { key: 'canal_asignado', label: 'Canal asignado' },
      { key: 'liberado', label: 'Liberado' },
    ],
  },
  {
    key: 'entrega',
    label: 'Entrega',
    items: [
      { key: 'remito_emitido', label: 'Remito emitido' },
      { key: 'entregado_cliente', label: 'Entregado al cliente' },
    ],
  },
  {
    key: 'costos',
    label: 'Costos proveedor',
    items: [
      { key: 'costos_proveedor_revisados', label: 'Costos proveedor revisados' },
    ],
  },
];

// Build flat list of all items
export function buildDefaultChecklist(operacionId: string): Omit<ChecklistItemDB, 'id'>[] {
  const items: Omit<ChecklistItemDB, 'id'>[] = [];
  for (const block of CHECKLIST_BLOCKS) {
    for (const item of block.items) {
      items.push({
        operacionId,
        bloque: block.key,
        itemKey: item.key,
        label: item.label,
        estado: 'pendiente',
      });
    }
  }
  return items;
}

// Auto-suggest operation status from checklist (manual steps only up to "Entregado al cliente")
export function suggestStatusFromChecklist(items: ChecklistItemDB[]): OperationStatus {
  const byKey = Object.fromEntries(items.map(i => [i.itemKey, i.estado]));

  const isHecho = (k: string) => byKey[k] === 'hecho';
  const isNA = (k: string) => byKey[k] === 'no-aplica';
  const isDone = (k: string) => isHecho(k) || isNA(k);

  // Apertura
  const aperturaKeys = ['carpeta_fisica', 'carpeta_drive', 'invoice_recibida', 'cliente_cargado', 'referencia_cargada', 'servicio_definido', 'responsable_asignado'];
  const aperturaComplete = aperturaKeys.every(isDone);
  if (!aperturaComplete) return 'apertura-incompleta';

  // Documentación
  const docKeys = ['invoice_controlada', 'packing_recibido', 'packing_controlado', 'bl_awb_recibido', 'draft_recibido', 'draft_controlado'];
  const docObservado = docKeys.some(k => byKey[k] === 'observado');
  const docComplete = docKeys.every(isDone);
  if (docObservado) return 'documentacion-observada';
  if (!docComplete) return 'esperando-documentacion';

  // Intervenciones
  const intKeys = ['int_anmat', 'int_inal', 'int_senasa', 'int_seg_electrica', 'int_otro'];
  const intPending = intKeys.some(k => byKey[k] === 'pendiente' || byKey[k] === 'en-proceso');
  if (intPending) return 'intervencion-pendiente';

  // Proforma
  if (!isDone('proforma_preparada') || !isDone('proforma_enviada')) return 'esperando-proforma';
  if (!isDone('fondos_confirmados')) return 'esperando-fondos';

  // Aduana
  if (!isDone('despacho_cargado')) return 'listo-para-oficializar';
  if (!isDone('despacho_oficializado')) return 'listo-para-oficializar';
  if (!isDone('liberado')) return 'oficializado';

  // Entrega
  if (!isDone('entregado_cliente')) return 'pendiente-de-entrega';

  // After delivery: billing/receipt is auto-detected — mark as pending billing
  return 'entregado-falta-facturar';
}

// ============================================================
// AUTO STATUS CALCULATION — 18-step priority order
// ============================================================

export type AutoStatusResult = {
  label: string;        // e.g. "Falta ANMAT", "Falta Proforma", "Cerrada"
  color: 'red' | 'yellow' | 'green' | 'blue';
  operationStatus: OperationStatus;
};

/**
 * Calculates the automatic status label based on checklist items and service type.
 * Steps 1-12: manual checklist items (up to "Entregado al cliente")
 * Steps 13-17: auto-detected from invoices/receipts passed in (from Facturación/Cobranzas)
 * - "Hecho" and "No aplica" = complete (don't block)
 * - "Pendiente", "En proceso", "Observado" = blocking
 */
export function calculateAutoStatus(
  items: ChecklistItemDB[],
  servicio: ServiceType,
  invoices?: Invoice[]
): AutoStatusResult {
  const byKey = Object.fromEntries(items.map(i => [i.itemKey, i.estado]));

  const isDone = (k: string) => byKey[k] === 'hecho' || byKey[k] === 'no-aplica';
  const isBlocking = (k: string) => {
    let s = byKey[k];
    return s === 'pendiente' || s === 'en-proceso' || s === 'observado';
  };
  const exists = (k: string) => k in byKey;

  const includesFlete = servicio === 'Solo flete internacional' || servicio === 'Despacho + flete internacional';
  const includesDespacho = servicio === 'Solo despacho' || servicio === 'Despacho + flete internacional';

  // Helper: check if item applies and is blocking
  const blocks = (k: string, applies = true): boolean => {
    if (!applies) return false;
    if (!exists(k)) return false;
    return isBlocking(k);
  };

  // Step 1: Mandado a cotizar
  if (blocks('mandado_cotizar')) {
    return { label: 'Falta cotizar', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }

  // Step 2: Cotización enviada
  if (blocks('cotizacion_enviada')) {
    return { label: 'Falta enviar cotización', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }

  // Step 3: Cotización aceptada
  if (blocks('cotizacion_aceptada')) {
    return { label: 'Falta aceptar cotización', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }

  // Apertura básica
  const aperturaKeys = ['carpeta_fisica', 'carpeta_drive', 'invoice_recibida', 'cliente_cargado', 'referencia_cargada', 'servicio_definido', 'responsable_asignado'];
  for (const k of aperturaKeys) {
    if (blocks(k)) {
      const labelMap: Record<string, string> = {
        carpeta_fisica: 'Falta carpeta física',
        carpeta_drive: 'Falta link Drive',
        invoice_recibida: 'Falta invoice',
        cliente_cargado: 'Falta cliente',
        referencia_cargada: 'Falta referencia',
        servicio_definido: 'Falta definir servicio',
        responsable_asignado: 'Falta responsable',
      };
      return { label: labelMap[k] ?? 'Apertura incompleta', color: 'red', operationStatus: 'apertura-incompleta' };
    }
  }

  // Step 4: Carpeta transporte (si incluye flete)
  if (blocks('carpeta_transporte', includesFlete)) {
    return { label: 'Falta carpeta transporte', color: 'yellow', operationStatus: 'esperando-documentacion' };
  }

  // Step 5: En recolección (si incluye flete)
  if (blocks('en_recoleccion', includesFlete)) {
    return { label: 'Falta confirmar recolección', color: 'yellow', operationStatus: 'esperando-documentacion' };
  }

  // Documentación
  const docKeys = ['invoice_controlada', 'packing_recibido', 'packing_controlado', 'bl_awb_recibido'];
  for (const k of docKeys) {
    if (blocks(k)) {
      const labelMap: Record<string, string> = {
        invoice_controlada: 'Falta controlar invoice',
        packing_recibido: 'Falta Packing List',
        packing_controlado: 'Falta controlar Packing List',
        bl_awb_recibido: 'Falta BL/AWB',
      };
      return { label: labelMap[k] ?? 'Falta documentación', color: 'red', operationStatus: 'esperando-documentacion' };
    }
  }

  // Step 6: Draft
  if (blocks('draft_recibido') || blocks('draft_controlado')) {
    return { label: 'Falta Draft', color: 'red', operationStatus: 'esperando-documentacion' };
  }

  // Step 7: ANMAT / intervenciones
  const intKeys = ['int_anmat', 'int_inal', 'int_senasa', 'int_seg_electrica', 'int_otro'];
  for (const k of intKeys) {
    if (blocks(k)) {
      const labelMap: Record<string, string> = {
        int_anmat: 'Falta ANMAT',
        int_inal: 'Falta INAL',
        int_senasa: 'Falta SENASA',
        int_seg_electrica: 'Falta Seguridad Eléctrica',
        int_otro: 'Falta intervención organismo',
      };
      return { label: labelMap[k] ?? 'Falta intervención', color: 'red', operationStatus: 'intervencion-pendiente' };
    }
  }

  // Step 8: Proforma
  if (blocks('proforma_preparada') || blocks('proforma_enviada') || blocks('pago_impuestos_solicitado') || blocks('fondos_confirmados')) {
    if (blocks('proforma_preparada') || blocks('proforma_enviada')) {
      return { label: 'Falta Proforma', color: 'red', operationStatus: 'esperando-proforma' };
    }
    return { label: 'Falta confirmar fondos', color: 'yellow', operationStatus: 'esperando-fondos' };
  }

  // Step 9: Arribo / Aduana
  if (blocks('despacho_cargado') || blocks('despacho_oficializado') || blocks('canal_asignado') || blocks('documentacion_presentada') || blocks('verificacion') || blocks('liberado')) {
    if (!isDone('despacho_cargado') || !isDone('despacho_oficializado')) {
      return { label: 'Falta Arribo / Oficialización', color: 'yellow', operationStatus: 'listo-para-oficializar' };
    }
    return { label: 'Falta Arribo', color: 'yellow', operationStatus: 'oficializado' };
  }

  // Step 10: Oficialización
  if (blocks('despacho_oficializado')) {
    return { label: 'Falta Oficialización', color: 'red', operationStatus: 'listo-para-oficializar' };
  }

  // Step 11: Remito — skip if nRemito is already filled (number loaded = remito exists)
  if (blocks('retiro_coordinado') || blocks('remito_emitido')) {
    return { label: 'Falta Remito', color: 'yellow', operationStatus: 'pendiente-de-entrega' };
  }

  // Step 12: Entregado al cliente
  if (blocks('entregado_cliente')) {
    return { label: 'Falta Entrega', color: 'yellow', operationStatus: 'pendiente-de-entrega' };
  }

  // ---- From here: auto-detect from invoices (no manual checklist) ----
  const invList = invoices ?? [];
  const facturaDespacho = invList.find(inv =>
    inv.tipoOperativa === 'Despacho' && !inv.deletedAt
  );
  const facturaTransporte = invList.find(inv =>
    (inv.tipoOperativa === 'Transporte' || inv.tipoOperativa === 'Flete' || inv.tipoOperativa === 'Transporte / Flete') && !inv.deletedAt
  );

  // Step 13: Factura despacho (si incluye despacho)
  if (includesDespacho && !facturaDespacho) {
    return { label: 'Falta Factura despacho', color: 'red', operationStatus: 'entregado-falta-facturar' };
  }

  // Step 14: Recibo/cobro despacho
  if (includesDespacho && facturaDespacho) {
    const cobrada = facturaDespacho.saldo === 0 && (facturaDespacho.recibido > 0 || (facturaDespacho.retencionGanancias + facturaDespacho.retencionIIBB + facturaDespacho.otrasRetenciones) > 0);
    if (!cobrada) {
      return { label: 'Falta Recibo despacho', color: 'red', operationStatus: 'facturado-falta-cobrar' };
    }
  }

  // Step 15: Factura transporte (si incluye flete)
  if (includesFlete && !facturaTransporte) {
    return { label: 'Falta Factura transporte', color: 'red', operationStatus: 'entregado-falta-facturar' };
  }

  // Step 16: Recibo/cobro transporte
  if (includesFlete && facturaTransporte) {
    const cobrada = facturaTransporte.saldo === 0 && (facturaTransporte.recibido > 0 || (facturaTransporte.retencionGanancias + facturaTransporte.retencionIIBB + facturaTransporte.otrasRetenciones) > 0);
    if (!cobrada) {
      return { label: 'Falta Recibo transporte', color: 'red', operationStatus: 'facturado-falta-cobrar' };
    }
  }

  // Step 17: Costos proveedor revisados (si incluye flete)
  if (blocks('costos_proveedor_revisados', includesFlete)) {
    return { label: 'Falta revisar costos proveedor', color: 'yellow', operationStatus: 'cobrado-listo-para-cerrar' };
  }

  // All done
  return { label: 'Lista para cerrar', color: 'green', operationStatus: 'cobrado-listo-para-cerrar' };
}

/**
 * Returns the display label for the "Próximo paso" column:
 * - If manual next step exists → show it
 * - Otherwise → show the auto status label
 */
export function getProximoPasoDisplay(proximoPasoManual: string | undefined, autoStatus: AutoStatusResult): string {
  if (proximoPasoManual && proximoPasoManual.trim()) return proximoPasoManual.trim();
  return autoStatus.label;
}

/**
 * Calculates auto status from the Operation's boolean fields (for table display).
 * Steps 1-12: from operation boolean fields.
 * Steps 13+: auto-detected from invoices array (from Facturación/Cobranzas).
 */
export function calculateAutoStatusFromOperation(op: Operation, invoices?: Invoice[]): AutoStatusResult {
  // RULE: estadoManual takes absolute priority
  if (op.estadoManual && op.estadoManual !== 'automatico') {
    switch (op.estadoManual) {
      case 'cerrada':
        return { label: 'Cerrada', color: 'green', operationStatus: 'cerrado' };
      case 'cancelada':
        return { label: 'Cancelada', color: 'red', operationStatus: 'cancelado' };
      case 'abierta':
        return { label: 'Abierta', color: 'yellow', operationStatus: 'apertura-incompleta' };
      case 'en_curso':
        return { label: 'En curso', color: 'blue', operationStatus: 'esperando-documentacion' };
      case 'lista_para_cerrar':
        return { label: 'Lista para cerrar', color: 'green', operationStatus: 'cobrado-listo-para-cerrar' };
    }
  }
  // RULE: Closed/cancelled operations are ALWAYS shown as closed — never recalculate
  if (op.estado === 'cerrado' || isEstadoCerrado(op.estado)) {
    return { label: 'Cerrado', color: 'green', operationStatus: 'cerrado' };
  }
  if (op.estado === 'cancelado' || isEstadoCancelado(op.estado)) {
    return { label: 'Cancelado', color: 'red', operationStatus: 'cancelado' };
  }

  const includesFlete = op.servicio === 'Solo flete internacional' || op.servicio === 'Despacho + flete internacional';
  const includesDespacho = op.servicio === 'Solo despacho' || op.servicio === 'Despacho + flete internacional';

  // Step 1-3: Cotización
  if (!op.mandadoCotizar) {
    return { label: 'Falta cotizar', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }
  if (!op.cotizacionEnviada) {
    return { label: 'Falta enviar cotización', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }
  if (!op.cotizacionAceptada) {
    return { label: 'Falta aceptar cotización', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }

  // Apertura básica
  if (!op.clienteNombre || !op.referencia) {
    return { label: 'Apertura incompleta', color: 'red', operationStatus: 'apertura-incompleta' };
  }
  if (!op.linkDrive) {
    return { label: 'Falta link Drive', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }

  // Step 4-5: Flete — SOLO si incluye flete
  if (includesFlete && !op.enRecoleccion) {
    return { label: 'Falta confirmar recolección', color: 'yellow', operationStatus: 'esperando-documentacion' };
  }

  // Step 6: Draft
  if (!op.draft) {
    return { label: 'Falta Draft', color: 'red', operationStatus: 'esperando-documentacion' };
  }

  // Step 7: ANMAT — SOLO si está explícitamente marcado como requerido (qvAnmat = 'no')
  // For boolean field: only block if qvAnmat is explicitly 'no' (not null/na)
  if (op.qvAnmat !== null && op.qvAnmat !== undefined && op.qvAnmat === 'no') {
    return { label: 'Falta ANMAT', color: 'red', operationStatus: 'intervencion-pendiente' };
  }

  // Step 8: Proforma
  if (!op.proforma) {
    return { label: 'Falta Proforma', color: 'red', operationStatus: 'esperando-proforma' };
  }

  // Step 9: Arribo
  if (!op.arribo) {
    return { label: 'Falta Arribo', color: 'yellow', operationStatus: 'oficializado' };
  }

  // Step 10: Oficialización
  if (!op.fechaOficializacion) {
    return { label: 'Falta Oficialización', color: 'red', operationStatus: 'listo-para-oficializar' };
  }

  // Step 11: Remito
  if (!op.remito && !op.nRemito) {
    return { label: 'Falta Remito', color: 'yellow', operationStatus: 'pendiente-de-entrega' };
  }

  // Step 12: Entregado al cliente
  if (!op.entregadoCliente) {
    return { label: 'Falta Entrega', color: 'yellow', operationStatus: 'pendiente-de-entrega' };
  }

  // ---- From here: auto-detect from invoices (no manual fields) ----
  const invList = invoices ?? [];
  const facturaDespacho = invList.find(inv =>
    inv.tipoOperativa === 'Despacho' && !inv.deletedAt
  );
  const facturaTransporte = invList.find(inv =>
    (inv.tipoOperativa === 'Transporte' || inv.tipoOperativa === 'Flete' || inv.tipoOperativa === 'Transporte / Flete') && !inv.deletedAt
  );

  // Step 13: Factura despacho — SOLO si incluye despacho
  if (includesDespacho && !facturaDespacho && !op.facturaDespacho) {
    return { label: 'Falta Facturar', color: 'red', operationStatus: 'entregado-falta-facturar' };
  }

  // Step 14: Recibo/cobro despacho — SOLO si incluye despacho
  if (includesDespacho && (facturaDespacho || op.facturaDespacho)) {
    const cobrada = facturaDespacho
      ? (facturaDespacho.saldo === 0 && (facturaDespacho.recibido > 0 || (facturaDespacho.retencionGanancias + facturaDespacho.retencionIIBB + facturaDespacho.otrasRetenciones) > 0))
      : !!op.reciboDespacho;
    if (!cobrada) {
      return { label: 'Falta Cobrar', color: 'red', operationStatus: 'facturado-falta-cobrar' };
    }
  }

  // Step 15: Factura transporte — SOLO si incluye flete
  if (includesFlete && !facturaTransporte && !op.facturaTransporte) {
    return { label: 'Falta Facturar', color: 'red', operationStatus: 'entregado-falta-facturar' };
  }

  // Step 16: Recibo/cobro transporte — SOLO si incluye flete
  if (includesFlete && (facturaTransporte || op.facturaTransporte)) {
    const cobrada = facturaTransporte
      ? (facturaTransporte.saldo === 0 && (facturaTransporte.recibido > 0 || (facturaTransporte.retencionGanancias + facturaTransporte.retencionIIBB + facturaTransporte.otrasRetenciones) > 0))
      : !!op.reciboTransporte;
    if (!cobrada) {
      return { label: 'Falta Cobrar', color: 'red', operationStatus: 'facturado-falta-cobrar' };
    }
  }

  return { label: 'Lista para cerrar', color: 'green', operationStatus: 'cobrado-listo-para-cerrar' };
}

/**
 * Returns the display status for an operation, respecting estadoManual priority.
 * If estadoManual is set and not 'automatico', it takes absolute priority.
 * Otherwise, falls back to calculateAutoStatusFromQuickView.
 */
export function getDisplayStatus(op: Operation, invoices?: Invoice[]): AutoStatusResult {
  const manual = op.estadoManual;
  if (manual && manual !== 'automatico') {
    switch (manual) {
      case 'cerrada':
        return { label: 'Cerrada', color: 'green', operationStatus: 'cerrado' };
      case 'cancelada':
        return { label: 'Cancelada', color: 'red', operationStatus: 'cancelado' };
      case 'abierta':
        return { label: 'Abierta', color: 'yellow', operationStatus: 'apertura-incompleta' };
      case 'en_curso':
        return { label: 'En curso', color: 'blue', operationStatus: 'esperando-documentacion' };
      case 'lista_para_cerrar':
        return { label: 'Lista para cerrar', color: 'green', operationStatus: 'cobrado-listo-para-cerrar' };
    }
  }
  return calculateAutoStatusFromQuickView(op, invoices);
}

/**
 * Returns true if the operation is effectively closed (via estadoManual or estado field).
 */
export function isEfectivamenteCerrada(op: Operation): boolean {
  if (op.estadoManual && op.estadoManual !== 'automatico') {
    return op.estadoManual === 'cerrada';
  }
  return isEstadoCerrado(op.estado);
}

/**
 * Returns true if the operation is effectively cancelled (via estadoManual or estado field).
 */
export function isEfectivamenteCancelada(op: Operation): boolean {
  if (op.estadoManual && op.estadoManual !== 'automatico') {
    return op.estadoManual === 'cancelada';
  }
  return isEstadoCancelado(op.estado);
}

/**
 * Returns a detailed human-readable explanation of WHY an open operation is still open.
 * Returns null for closed/cancelled operations.
 */
export function getMotivoPendiente(op: Operation, invoices?: Invoice[]): string | null {
  // estadoManual takes priority — if manually closed/cancelled, no pending reason
  if (op.estadoManual && op.estadoManual !== 'automatico') {
    if (op.estadoManual === 'cerrada') return null;
    if (op.estadoManual === 'cancelada') return null;
  }
  // Closed/cancelled: no pending reason
  if (op.estado === 'cerrado' || isEstadoCerrado(op.estado)) return null;
  if (op.estado === 'cancelado' || isEstadoCancelado(op.estado)) return null;

  const autoStatus = calculateAutoStatusFromQuickView(op, invoices);

  const motivoMap: Record<string, string> = {
    'Falta cotizar': 'La operación aún no fue mandada a cotizar.',
    'Falta enviar cotización': 'La cotización fue preparada pero todavía no fue enviada al cliente.',
    'Falta aceptar cotización': 'La cotización fue enviada pero no figura como aceptada por el cliente.',
    'Falta carpeta transporte': 'La operación incluye flete internacional pero no tiene carpeta de transporte asignada.',
    'Falta confirmar recolección': 'La operación incluye flete pero no se confirmó la recolección de la mercadería.',
    'Falta Draft': 'La operación tiene cotización aceptada pero no tiene draft cargado o aprobado.',
    'Falta ANMAT': 'La operación requiere intervención ANMAT y no figura como completada.',
    'Falta INAL': 'La operación requiere intervención INAL y no figura como completada.',
    'Falta SENASA': 'La operación requiere intervención SENASA y no figura como completada.',
    'Falta Seguridad Eléctrica': 'La operación requiere certificación de Seguridad Eléctrica y no figura como completada.',
    'Falta intervención organismo': 'La operación requiere intervención de un organismo externo y no figura como completada.',
    'Falta Proforma': 'La operación tiene draft cargado pero no tiene proforma preparada o enviada.',
    'Falta Arribo / Oficialización': 'La operación está en proceso de aduana pero no figura arribo ni oficialización completados.',
    'Falta Arribo': 'La operación está oficializada pero no figura el arribo de la mercadería.',
    'Falta Oficialización': 'La operación tiene cotización aceptada y draft cargado, pero no figura oficialización completada.',
    'Falta Remito': 'La operación está oficializada pero no tiene remito emitido para la entrega.',
    'Falta Entrega': 'La operación tiene remito emitido pero no figura como entregada al cliente.',
    'Falta Factura despacho': 'La operación ya fue entregada pero no tiene factura de despacho emitida.',
    'Falta Recibo despacho': 'La operación tiene factura de despacho pero no tiene recibo/cobro imputado.',
    'Falta Factura transporte': 'La operación ya fue entregada pero no tiene factura de transporte emitida.',
    'Falta Recibo transporte': 'La operación tiene factura de transporte pero no tiene recibo/cobro imputado.',
    'Lista para cerrar': 'La operación tiene todas las etapas completas. Solo falta marcarla como cerrada.',
    'Apertura incompleta': 'La operación no tiene todos los datos básicos de apertura cargados (cliente, referencia, etc.).',
    'Falta link Drive': 'La operación no tiene link de carpeta Drive asignado.',
  };

  return motivoMap[autoStatus.label] ?? `La operación está pendiente: ${autoStatus.label}.`;
}

/**
 * Calculates auto status from the Operation's qv_ quick-view fields.
 * Falls back to boolean fields if qv_ fields are null.
 * IMPORTANT: If the operation's estado is 'cerrado' or 'cancelado', return "Cerrado" immediately.
 * For open operations, checks 13 steps in priority order, only checking steps that apply to the operation.
 */
export function calculateAutoStatusFromQuickView(op: Operation, invoices?: Invoice[]): AutoStatusResult {
  // RULE: estadoManual takes absolute priority over any auto calculation
  if (op.estadoManual && op.estadoManual !== 'automatico') {
    switch (op.estadoManual) {
      case 'cerrada':
        return { label: 'Cerrada', color: 'green', operationStatus: 'cerrado' };
      case 'cancelada':
        return { label: 'Cancelada', color: 'red', operationStatus: 'cancelado' };
      case 'abierta':
        return { label: 'Abierta', color: 'yellow', operationStatus: 'apertura-incompleta' };
      case 'en_curso':
        return { label: 'En curso', color: 'blue', operationStatus: 'esperando-documentacion' };
      case 'lista_para_cerrar':
        return { label: 'Lista para cerrar', color: 'green', operationStatus: 'cobrado-listo-para-cerrar' };
    }
  }
  // RULE: Closed/cancelled operations are ALWAYS shown as closed — never recalculate
  if (op.estado === 'cerrado' || isEstadoCerrado(op.estado)) {
    return { label: 'Cerrado', color: 'green', operationStatus: 'cerrado' };
  }
  if (op.estado === 'cancelado' || isEstadoCancelado(op.estado)) {
    return { label: 'Cancelado', color: 'red', operationStatus: 'cancelado' };
  }

  const includesFlete = op.servicio === 'Solo flete internacional' || op.servicio === 'Despacho + flete internacional';
  const includesDespacho = op.servicio === 'Solo despacho' || op.servicio === 'Despacho + flete internacional';

  // Helper: get effective value from qv_ field or fall back to boolean
  // If qvField is null/undefined (not explicitly set), treat as 'no' (blocking/missing)
  // unless a data-driven fallback confirms it's done (e.g. a date or number was entered)
  const getQv = (qvField: QuickValue | null | undefined, boolFallback: boolean): QuickValue => {
    if (qvField !== null && qvField !== undefined) return qvField;
    // Null/undefined = not set → treat as 'no' (missing), UNLESS data-driven fallback says done
    return boolFallback ? 'si' : 'no';
  };
  // For pure "opinion" fields (no data to infer from), null always means 'no'
  const getQvOpinion = (qvField: QuickValue | null | undefined): QuickValue => {
    if (qvField !== null && qvField !== undefined) return qvField;
    return 'no';
  };

  const isDone = (qv: QuickValue) => qv === 'si' || qv === 'na';
  const isBlocking = (qv: QuickValue) => qv === 'no' || qv === 'obs';

  // isBlockingOpinion: blocks only when NOT explicitly answered (null/undefined) or when 'obs'.
  // Explicitly set to 'no' means "acknowledged / operation continues" → NOT blocking.
  const isBlockingOpinion = (rawField: QuickValue | null | undefined): boolean => {
    if (rawField === null || rawField === undefined) return true;  // not answered yet
    if (rawField === 'obs') return true;                           // observation/issue
    return false;                                                  // 'si', 'na', or 'no' → pass
  };

  // Opinion fields: null = 'no' (must be explicitly set to SI/NA to pass)
  const qvMandado = getQvOpinion(op.qvMandadoCotizar);
  const qvCotEnv = getQvOpinion(op.qvCotizacionEnviada);
  const qvCotAcep = getQvOpinion(op.qvCotizacionAceptada);
  const qvEnRecol = getQvOpinion(op.qvEnRecoleccion);
  const qvDraft = getQvOpinion(op.qvDraft);
  const qvAnmat = getQvOpinion(op.qvAnmat);
  const qvProforma = getQvOpinion(op.qvProforma);
  const qvArribo = getQvOpinion(op.qvArribo);
  const qvEntregado = getQvOpinion(op.qvEntregadoCliente);
  // Data-driven fields: if actual data exists, treat as done even if qv not explicitly set
  const qvCarpTrans = getQv(op.qvCarpetaTransporte, !!op.nCarpetaTransporte);
  const qvOficializacion = getQv(op.qvOficializacion, !!op.fechaOficializacion);
  const qvRemito = getQv(op.qvRemito, op.remito || !!op.nRemito);

  // Step 1: Mandado a cotizar
  if (isBlockingOpinion(op.qvMandadoCotizar)) {
    return { label: 'Falta cotizar', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }
  // Step 2: Cotización enviada
  if (isBlockingOpinion(op.qvCotizacionEnviada)) {
    return { label: 'Falta enviar cotización', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }
  // Step 3: Cotización aceptada — 'no' = acknowledged, operation continues; only null or 'obs' blocks
  if (isBlockingOpinion(op.qvCotizacionAceptada)) {
    return { label: 'Falta aceptar cotización', color: 'yellow', operationStatus: 'apertura-incompleta' };
  }
  // Step 4: Carpeta transporte — requiere respuesta explícita (SI o NO) para todas las operaciones
  // Si es null/undefined = no respondido → bloquea. Si es 'no' = sin transporte → pasa (no bloquea).
  if (op.qvCarpetaTransporte === null || op.qvCarpetaTransporte === undefined) {
    return { label: 'Falta definir carpeta transporte', color: 'yellow', operationStatus: 'esperando-documentacion' };
  }
  // Step 5: En recolección — SOLO si hay carpeta de transporte (qvCarpetaTransporte === 'si')
  if (op.qvCarpetaTransporte === 'si' && isBlockingOpinion(op.qvEnRecoleccion)) {
    return { label: 'Falta confirmar recolección', color: 'yellow', operationStatus: 'esperando-documentacion' };
  }
  // Step 6: Draft
  if (isBlockingOpinion(op.qvDraft)) {
    return { label: 'Falta Draft', color: 'red', operationStatus: 'esperando-documentacion' };
  }
  // Step 7: ANMAT — SOLO si el campo está explícitamente en 'obs' (no bloquear si es null/no/na)
  if (op.qvAnmat === 'obs') {
    return { label: 'Falta ANMAT', color: 'red', operationStatus: 'intervencion-pendiente' };
  }
  // Step 8: Proforma
  if (isBlockingOpinion(op.qvProforma)) {
    return { label: 'Falta Proforma', color: 'red', operationStatus: 'esperando-proforma' };
  }
  // Step 9: Arribo
  if (isBlockingOpinion(op.qvArribo)) {
    return { label: 'Falta Arribo', color: 'yellow', operationStatus: 'oficializado' };
  }
  // Step 10: Oficialización
  if (isBlocking(qvOficializacion)) {
    return { label: 'Falta Oficialización', color: 'red', operationStatus: 'listo-para-oficializar' };
  }
  // Step 11: Remito
  if (isBlocking(qvRemito)) {
    return { label: 'Falta Remito', color: 'yellow', operationStatus: 'pendiente-de-entrega' };
  }
  // Step 12: Entregado al cliente — REMOVED (no longer blocks progress)

  // Steps 13+: auto-detect from invoices
  const invList = invoices ?? [];

  // Find despacho invoice: prefer explicit 'Despacho' tipoOperativa,
  // but fall back to any active invoice that is NOT a transport type.
  // This handles cases where tipoOperativa is empty or not set.
  const TRANSPORT_TIPOS = ['Transporte', 'Flete', 'Transporte / Flete'];
  const facturaDespacho = invList.find(inv =>
    !inv.deletedAt && inv.tipoOperativa === 'Despacho'
  ) ?? invList.find(inv =>
    !inv.deletedAt && !TRANSPORT_TIPOS.includes(inv.tipoOperativa ?? '')
  );

  // Find transport invoice: prefer explicit transport tipoOperativa,
  // but fall back to any active invoice if the operation has a transport carpeta.
  const facturaTransporte = invList.find(inv =>
    !inv.deletedAt && TRANSPORT_TIPOS.includes(inv.tipoOperativa ?? '')
  );

  // Step 13: Factura despacho — SOLO si incluye despacho
  if (includesDespacho && !facturaDespacho && !op.facturaDespacho) {
    return { label: 'Falta Facturar', color: 'red', operationStatus: 'entregado-falta-facturar' };
  }
  // Step 14: Recibo/cobro despacho — SOLO si incluye despacho
  if (includesDespacho && (facturaDespacho || op.facturaDespacho)) {
    const cobrada = facturaDespacho
      ? (facturaDespacho.saldo === 0 && (facturaDespacho.recibido > 0 || (facturaDespacho.retencionGanancias + facturaDespacho.retencionIIBB + facturaDespacho.otrasRetenciones) > 0))
      : !!op.reciboDespacho;
    if (!cobrada) {
      return { label: 'Falta Cobrar', color: 'red', operationStatus: 'facturado-falta-cobrar' };
    }
  }
  // Step 15: Factura transporte — SOLO si hay carpeta de transporte (qvCarpetaTransporte === 'si')
  const hasCarpetaTransporte = op.qvCarpetaTransporte === 'si';
  if (hasCarpetaTransporte && !facturaTransporte && !op.facturaTransporte) {
    return { label: 'Falta Facturar', color: 'red', operationStatus: 'entregado-falta-facturar' };
  }
  // Step 16: Recibo/cobro transporte — SOLO si hay carpeta de transporte
  if (hasCarpetaTransporte && (facturaTransporte || op.facturaTransporte)) {
    const cobrada = facturaTransporte
      ? (facturaTransporte.saldo === 0 && (facturaTransporte.recibido > 0 || (facturaTransporte.retencionGanancias + facturaTransporte.retencionIIBB + facturaTransporte.otrasRetenciones) > 0))
      : !!op.reciboTransporte;
    if (!cobrada) {
      return { label: 'Falta Cobrar', color: 'red', operationStatus: 'facturado-falta-cobrar' };
    }
  }

  // All steps done
  return { label: 'Lista para cerrar', color: 'green', operationStatus: 'cobrado-listo-para-cerrar' };
}

// ============================================================
// CARPETA NORMALIZATION UTILITY
// ============================================================

/**
 * Normalizes a carpeta number for comparison:
 * - Trims whitespace
 * - Converts to string
 * - Removes leading zeros for purely numeric values
 * This ensures "02864", "2864", 2864 all match each other.
 */
export function normalizeCarpeta(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  if (!str) return '';
  // If purely numeric, remove leading zeros
  if (/^\d+$/.test(str)) return String(parseInt(str, 10));
  return str;
}

// ============================================================
// CLIENT NAME NORMALIZATION UTILITY
// ============================================================

/**
 * Normalizes a client name for grouping/comparison purposes.
 * - Trims whitespace
 * - Converts to uppercase
 * - Removes common Argentine legal entity suffixes (SA, SRL, S.A., S.R.L., etc.)
 * - Collapses multiple spaces
 * This ensures "MECANIZADOS GABRIEL SA", "MECANIZADOS GABRIEL S.A." and *"MECANIZADOS GABRIEL" all map to the same canonical key "MECANIZADOS GABRIEL".
 */
export function normalizeClienteName(name: string | null | undefined): string {
  if (!name) return '';
  let s = name.trim().toUpperCase();
  // Remove common legal suffixes at the end (with optional leading space/dot/comma)
  const suffixes = [
    'S\\.A\\.C\\.I\\.F\\.I\\.A\\.', 'S\\.A\\.C\\.I\\.F\\.I\\.A',
    'S\\.A\\.C\\.I\\.F\\.A\\.', 'S\\.A\\.C\\.I\\.F\\.A',
    'S\\.A\\.C\\.I\\.', 'S\\.A\\.C\\.I',
    'S\\.R\\.L\\.', 'S\\.R\\.L',
    'S\\.A\\.', 'S\\.A',
    'S\\.C\\.A\\.', 'S\\.C\\.A',
    'S\\.C\\.S\\.', 'S\\.C\\.S',
    'S\\.A\\.S\\.', 'S\\.A\\.S',
    'SOCIEDAD ANONIMA',
    'SOCIEDAD DE RESPONSABILIDAD LIMITADA',
    'SOCIEDAD EN COMANDITA',
    '\\bSRL\\b', '\\bSA\\b', '\\bSAS\\b', '\\bSCA\\b', '\\bSCS\\b',
  ];
  for (const suffix of suffixes) {
    s = s.replace(new RegExp('[,\\s.]*' + suffix + '[,\\s.]*$', 'i'), '').trim();
  }
  // Collapse multiple spaces
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

/**
 * Groups an array of items by normalized client name.
 * Returns a Map where:
 *   key   = normalized name (for comparison)
 *   value = { displayName: string (the most complete/longest original name), items: T[] }
 *
 * Usage: groupByNormalizedCliente(rows, r => r.cliente)
 */
export function groupByNormalizedCliente<T>(
  items: T[],
  getName: (item: T) => string,
): Map<string, { displayName: string; items: T[] }> {
  const map = new Map<string, { displayName: string; items: T[] }>();
  for (const item of items) {
    const raw = getName(item) || '(Sin cliente)';
    const key = normalizeClienteName(raw) || raw;
    if (!map.has(key)) {
      map.set(key, { displayName: raw, items: [] });
    }
    const entry = map.get(key)!;
    // Prefer the longest name as display name (e.g. "MECANIZADOS GABRIEL SA" over "MECANIZADOS GABRIEL")
    if (raw.length > entry.displayName.length) {
      entry.displayName = raw;
    }
    entry.items.push(item);
  }
  return map;
}

// ============================================================
// HELPERS
// ============================================================

/**
 * Normalize an operation status string from the DB to a valid OperationStatus.
 * Handles case variations: "Cerrado", "CERRADO", "cerrado" → 'cerrado'
 * Also handles accent variants: "Abierta", "abierta", etc.
 */
function normalizeEstado(raw: unknown): OperationStatus {
  if (!raw) return 'apertura-incompleta';
  // Normalize: trim, lowercase, remove accents
  let s = String(raw).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (s === 'cerrado' || s === 'cerrada' || s === 'closed') return 'cerrado';
  if (s === 'cancelado' || s === 'cancelada') return 'cancelado';
  if (s === 'apertura-incompleta' || s === 'apertura incompleta') return 'apertura-incompleta';
  if (s === 'esperando-documentacion' || s === 'esperando documentacion') return 'esperando-documentacion';
  if (s === 'documentacion-observada' || s === 'documentacion observada') return 'documentacion-observada';
  if (s === 'esperando-draft' || s === 'esperando draft') return 'esperando-draft';
  if (s === 'intervencion-pendiente' || s === 'intervencion pendiente') return 'intervencion-pendiente';
  if (s === 'esperando-proforma' || s === 'esperando proforma') return 'esperando-proforma';
  if (s === 'esperando-fondos' || s === 'esperando fondos') return 'esperando-fondos';
  if (s === 'listo-para-oficializar' || s === 'listo para oficializar') return 'listo-para-oficializar';
  if (s === 'oficializado' || s === 'oficializada') return 'oficializado';
  if (s === 'pendiente-de-entrega' || s === 'pendiente de entrega') return 'pendiente-de-entrega';
  if (s === 'entregado-falta-facturar' || s === 'entregado falta facturar') return 'entregado-falta-facturar';
  if (s === 'facturado-falta-cobrar' || s === 'facturado falta cobrar') return 'facturado-falta-cobrar';
  if (s === 'cobrado-listo-para-cerrar' || s === 'cobrado listo para cerrar') return 'cobrado-listo-para-cerrar';
  if (s === 'demorado' || s === 'demorada') return 'demorado';
  if (s === 'observado' || s === 'observada') return 'observado';
  // Return as-is if it already matches a valid status (TypeScript cast)
  return (raw as OperationStatus) ?? 'apertura-incompleta';
}

/**
 * Returns true if the operation estado means "closed" — case-insensitive. * Handles"cerrado", "Cerrado", "CERRADO", "cerrada", "closed".
 */
export function isEstadoCerrado(estado: string | null | undefined): boolean {
  if (!estado) return false;
  let s = estado.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return s === 'cerrado' || s === 'cerrada' || s === 'closed';
}

/**
 * Returns true if the operation estado means "cancelled" — case-insensitive.
 */
export function isEstadoCancelado(estado: string | null | undefined): boolean {
  if (!estado) return false;
  let s = estado.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return s === 'cancelado' || s === 'cancelada';
}

// ============================================================
// DUPLICATE VALIDATION SERVICE
// ============================================================

/**
 * Normalizes a key value for duplicate comparison.
 * Strips spaces, dots, dashes, leading zeros, and lowercases.
 * "002744", "2744", " 2744 ", "2.744" → "2744"
 */
export function normalizeKeyValue(val: string | null | undefined): string {
  if (!val) return '';
  // Remove spaces, dots, dashes
  const stripped = val.trim().replace(/[\s.\-]/g, '');
  // Remove leading zeros for numeric-like values
  const withoutLeadingZeros = stripped.replace(/^0+(\d)/, '$1');
  return withoutLeadingZeros.toLowerCase();
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  conflictId?: string;
  conflictValue?: string;
  message: string;
}

export const duplicateValidationService = {
  /**
   * Check if n_operacion already exists in another operation.
   * @param value - the value to check
   * @param excludeId - the current operation id (for edit mode — exclude self)
   */
  async checkNOperacion(value: string, excludeId?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('operaciones')
        .select('id, n_operacion')
        .eq('n_operacion', value.trim())
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data } = await query.maybeSingle();
      if (data) {
        return {
          isDuplicate: true,
          conflictId: data.id,
          conflictValue: data.n_operacion,
          message: 'Ya existe una operación con este número. Revisá el histórico antes de guardar.',
        };
      }
      // Also check normalized value (strip leading zeros/spaces)
      const normalized = normalizeKeyValue(value);
      if (normalized !== value.trim()) {
        let q2 = supabase
          .from('operaciones')
          .select('id, n_operacion')
          .is('deleted_at', null);
        if (excludeId) q2 = q2.neq('id', excludeId);
        const { data: allOps } = await q2;
        if (allOps) {
          const conflict = allOps.find(op => normalizeKeyValue(op.n_operacion) === normalized);
          if (conflict) {
            return {
              isDuplicate: true,
              conflictId: conflict.id,
              conflictValue: conflict.n_operacion,
              message: `Ya existe una operación con este número (${conflict.n_operacion}). Revisá el histórico antes de guardar.`,
            };
          }
        }
      }
      return { isDuplicate: false, message: '' };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },

  /**
   * Check if n_carpeta_despacho already exists in another operation.
   */
  async checkCarpetaDespacho(value: string, excludeId?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('operaciones')
        .select('id, n_operacion, n_carpeta_despacho')
        .eq('n_carpeta_despacho', value.trim())
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data } = await query.maybeSingle();
      if (data) {
        return {
          isDuplicate: true,
          conflictId: data.id,
          conflictValue: data.n_carpeta_despacho,
          message: 'Ya existe una operación con esta carpeta. No se puede duplicar.',
        };
      }
      return { isDuplicate: false, message: '' };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },

  /**
   * Check if n_carpeta_transporte already exists in another operation.
   */
  async checkCarpetaTransporte(value: string, excludeId?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('operaciones')
        .select('id, n_operacion, n_carpeta_transporte')
        .eq('n_carpeta_transporte', value.trim())
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data } = await query.maybeSingle();
      if (data) {
        return {
          isDuplicate: true,
          conflictId: data.id,
          conflictValue: data.n_carpeta_transporte,
          message: 'Ya existe una operación con esta carpeta de transporte.',
        };
      }
      return { isDuplicate: false, message: '' };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },

  /**
   * Check if n_factura already exists in another invoice.
   * Anuladas also block the number (per business rule).
   */
  async checkNFactura(value: string, excludeId?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('facturas')
        .select('id, n_factura, estado')
        .eq('n_factura', value.trim())
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data } = await query.maybeSingle();
      if (data) {
        const isAnulada = data.estado === 'anulada';
        return {
          isDuplicate: true,
          conflictId: data.id,
          conflictValue: data.n_factura,
          message: isAnulada
            ? `Ya existe una factura con este número (actualmente anulada). La facturación debe ser correlativa y no puede duplicarse.`
            : 'Ya existe una factura con este número. La facturación debe ser correlativa y no puede duplicarse.',
        };
      }
      return { isDuplicate: false, message: '' };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },

  /**
   * Check if n_carpeta already exists in another active (non-anulada) invoice.
   * Allows same carpeta for different tipo_operativa (e.g. Despacho + Transporte).
   * Only blocks if same carpeta AND same tipo_operativa already exists.
   */
  async checkFacturaCarpeta(value: string, excludeId?: string, tipoOperativa?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('facturas')
        .select('id, n_factura, n_carpeta, estado, tipo_operativa')
        .eq('n_carpeta', value.trim())
        .neq('estado', 'anulada')
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data: rows } = await query;
      if (!rows || rows.length === 0) return { isDuplicate: false, message: '' };

      // If tipoOperativa is provided, only block if same tipo_operativa already exists
      if (tipoOperativa) {
        const TRANSPORTE_TYPES = ['Transporte', 'Flete', 'Transporte / Flete'];
        const isNewTransporte = TRANSPORTE_TYPES.includes(tipoOperativa);
        const conflict = rows.find(r => {
          const existingIsTransporte = TRANSPORTE_TYPES.includes(r.tipo_operativa ?? '');
          // Block only if both are transporte OR both are non-transporte (despacho/otros)
          return isNewTransporte === existingIsTransporte;
        });
        if (conflict) {
          return {
            isDuplicate: true,
            conflictId: conflict.id,
            conflictValue: conflict.n_carpeta,
            message: `Ya existe una factura de ${tipoOperativa} para esta carpeta (Factura N° ${conflict.n_factura}).`,
          };
        }
        return { isDuplicate: false, message: '' };
      }

      // Legacy fallback (no tipoOperativa provided): warn but don't block if types differ
      const first = rows[0];
      return {
        isDuplicate: true,
        conflictId: first.id,
        conflictValue: first.n_carpeta,
        message: `Ya existe una factura cargada para esta carpeta (Factura N° ${first.n_factura}).`,
      };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },

  /**
   * Check if n_remito already exists in another operation.
   */
  async checkNRemito(value: string, excludeId?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('operaciones')
        .select('id, n_operacion, n_remito')
        .eq('n_remito', value.trim())
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data } = await query.maybeSingle();
      if (data) {
        return {
          isDuplicate: true,
          conflictId: data.id,
          conflictValue: data.n_remito,
          message: 'Ya existe un remito con este número.',
        };
      }
      return { isDuplicate: false, message: '' };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },

  /**
   * Check if n_carpeta already has a remito in another operation.
   */
  async checkRemitoCarpeta(value: string, excludeId?: string): Promise<DuplicateCheckResult> {
    if (!value.trim()) return { isDuplicate: false, message: '' };
    const supabase = createClient();
    try {
      let query = supabase
        .from('operaciones')
        .select('id, n_operacion, n_remito, n_carpeta_despacho')
        .eq('n_carpeta_despacho', value.trim())
        .not('n_remito', 'is', null)
        .is('deleted_at', null);
      if (excludeId) query = query.neq('id', excludeId);
      const { data } = await query.maybeSingle();
      if (data) {
        return {
          isDuplicate: true,
          conflictId: data.id,
          conflictValue: data.n_carpeta_despacho,
          message: `Ya existe un remito asociado a esta carpeta (Remito N° ${data.n_remito ?? '—'}). Revisá antes de continuar.`,
        };
      }
      return { isDuplicate: false, message: '' };
    } catch {
      return { isDuplicate: false, message: '' };
    }
  },
};

function isSchemaError(error: { code?: string; message?: string }): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error/i.test(error.message);
  }
  return false;
}

// snake_case DB row → camelCase Operation
function rowToOperation(row: Record<string, unknown>): Operation {
  // Normalize a raw DB value to a valid QuickValue or null
  const normalizeQv = (val: unknown): QuickValue | null => {
    if (val === null || val === undefined) return null;
    let s = String(val).toLowerCase().trim();
    if (s === 'si' || s === 'sí') return 'si';
    if (s === 'no') return 'no';
    if (s === 'na' || s === 'n/a') return 'na';
    if (s === 'obs') return 'obs';
    return null;
  };

  return {
    id: row.id as string,
    nOperacion: (row.n_operacion as string) ?? '',
    nCarpetaDespacho: (row.n_carpeta_despacho as string) ?? '',
    nCarpetaTransporte: row.n_carpeta_transporte as string | undefined,
    clienteId: row.cliente_id as string | undefined,
    clienteNombre: (row.cliente_nombre as string) ?? '',
    referencia: (row.referencia as string) ?? '',
    proveedor: (row.proveedor as string) ?? '',
    tipoOperacion: (row.tipo_operacion as string) ?? 'Importación',
    servicio: (row.servicio as ServiceType) ?? 'Solo despacho',
    responsable: (row.responsable as string) ?? '',
    fechaApertura: (row.fecha_apertura as string) ?? '',
    estado: normalizeEstado(row.estado as unknown),
    proximoPaso: (row.proximo_paso as string) ?? '',
    linkDrive: row.link_drive as string | undefined,
    fechaArriboEstimado: row.fecha_arribo_estimado as string | undefined,
    fechaOficializacion: row.fecha_oficializacion as string | undefined,
    nDespacho: row.n_despacho as string | undefined,
    nRemito: row.n_remito as string | undefined,
    fechaEntrega: row.fecha_entrega as string | undefined,
    facturaDespacho: row.factura_despacho as string | undefined,
    facturaTransporte: row.factura_transporte as string | undefined,
    reciboDespacho: row.recibo_despacho as string | undefined,
    reciboTransporte: row.recibo_transporte as string | undefined,
    fechaCierre: row.fecha_cierre as string | undefined,
    observaciones: row.observaciones as string | undefined,
    checklistProgress: (row.checklist_progress as number) ?? 0,
    ultimaActualizacion: (row.ultima_actualizacion as string) ?? (row.updated_at as string) ?? '',
    mandadoCotizar: Boolean(row.mandado_cotizar),
    cotizacionEnviada: Boolean(row.cotizacion_enviada),
    cotizacionAceptada: Boolean(row.cotizacion_aceptada),
    enRecoleccion: Boolean(row.en_recoleccion),
    draft: Boolean(row.draft),
    anmat: Boolean(row.anmat),
    proforma: Boolean(row.proforma),
    arribo: Boolean(row.arribo),
    remito: Boolean(row.remito),
    entregadoCliente: Boolean(row.entregado_cliente),
    // Quick view fields
    qvMandadoCotizar: normalizeQv(row.qv_mandado_cotizar),
    qvCotizacionEnviada: normalizeQv(row.qv_cotizacion_enviada),
    qvCotizacionAceptada: normalizeQv(row.qv_cotizacion_aceptada),
    qvCarpetaTransporte: normalizeQv(row.qv_carpeta_transporte),
    qvEnRecoleccion: normalizeQv(row.qv_en_recoleccion),
    qvDraft: normalizeQv(row.qv_draft),
    qvAnmat: normalizeQv(row.qv_anmat),
    qvProforma: normalizeQv(row.qv_proforma),
    qvArribo: normalizeQv(row.qv_arribo),
    qvOficializacion: normalizeQv(row.qv_oficializacion),
    qvRemito: normalizeQv(row.qv_remito),
    qvEntregadoCliente: normalizeQv(row.qv_entregado_cliente),
    qvObsDraft: row.qv_obs_draft as string | null,
    qvObsAnmat: row.qv_obs_anmat as string | null,
    qvObsCarpetaTransporte: row.qv_obs_carpeta_transporte as string | null,
    qvNCarpetaTransporteNro: row.qv_n_carpeta_transporte_nro as string | null,
    qvFechaArribo: row.qv_fecha_arribo as string | null,
    qvCanalOficializacion: row.qv_canal_oficializacion as string | null,
    qvObsEntregado: row.qv_obs_entregado as string | null,
    estadoManual: ((row.estado_manual as string) ?? 'automatico') as EstadoManual,
    deletedAt: row.deleted_at as string | undefined,
  };
}

// snake_case DB row → camelCase Invoice
function rowToInvoice(row: Record<string, unknown>): Invoice {
  return {
    id: row.id as string,
    fecha: (row.fecha as string) ?? '',
    clienteId: row.cliente_id as string | undefined,
    clienteNombre: (row.cliente_nombre as string) ?? '',
    puntoVenta: (row.punto_venta as string) ?? '0001',
    tipoComprobante: (row.tipo_comprobante as string) ?? 'A',
    nFactura: (row.n_factura as string) ?? '',
    nCarpeta: (row.n_carpeta as string) ?? '',
    operacionId: row.operacion_id as string | undefined,
    concepto: (row.concepto as string) ?? '',
    importe: Number(row.importe ?? 0),
    base: Number(row.base ?? 0),
    alicuotaIva: Number(row.alicuota_iva ?? 21),
    moneda: (row.moneda as string) ?? 'ARS',
    tipoOperativa: (row.tipo_operativa as string) ?? 'Despacho',
    nRecibo: row.n_recibo as string | undefined,
    fechaCobro: row.fecha_cobro as string | undefined,
    recibido: Number(row.recibido ?? 0),
    retencionGanancias: Number(row.retencion_ganancias ?? 0),
    retencionIIBB: Number(row.retencion_iibb ?? 0),
    otrasRetenciones: Number(row.otras_retenciones ?? 0),
    retencionesTotales: Number(row.retenciones_totales ?? 0),
    baseExenta: Number(row.base_exenta ?? 0),
    iva21: Number(row.iva_21 ?? 0),
    mesAnio: row.mes_anio as string | undefined,
    neto: Number(row.neto ?? 0),
    saldo: Number(row.saldo ?? 0),
    estado: (row.estado as InvoiceStatus) ?? 'emitida',
    observaciones: row.observaciones as string | undefined,
    origenRegistro: (row.origen_registro as Invoice['origenRegistro']) ?? (row.origen === 'historico' ? 'historico_importado' : 'operativo_real'),
    origen: row.origen as string | undefined,
    incluirEnSeguimientoCobranza: row.incluir_en_seguimiento_cobranza !== false,
    impactaCaja: Boolean(row.impacta_caja ?? true),
    motivoAnulacion: row.motivo_anulacion as string | undefined,
    deletedAt: row.deleted_at as string | undefined,
  };
}

function rowToCliente(row: Record<string, unknown>): Cliente {
  return {
    id: row.id as string,
    razonSocial: (row.razon_social as string) ?? '',
    cuit: row.cuit as string | undefined,
    contacto: row.contacto as string | undefined,
    email: row.email as string | undefined,
    telefono: row.telefono as string | undefined,
    direccion: row.direccion as string | undefined,
    observaciones: row.observaciones as string | undefined,
  };
}

function rowToCobranza(row: Record<string, unknown>): Cobranza {
  return {
    id: row.id as string,
    clienteId: row.cliente_id as string | undefined,
    clienteNombre: (row.cliente_nombre as string) ?? '',
    facturaId: row.factura_id as string | undefined,
    nFactura: row.n_factura as string | undefined,
    nCarpeta: (row.n_carpeta as string) ?? '',
    operacionId: row.operacion_id as string | undefined,
    fechaCobro: (row.fecha_cobro as string) ?? '',
    monto: Number(row.monto ?? 0),
    moneda: (row.moneda as string) ?? 'ARS',
    medioCobro: (row.medio_cobro as string) ?? '',
    cuentaIngreso: (row.cuenta_ingreso as string) ?? '',
    nRecibo: (row.n_recibo as string) ?? '',
    retencionGanancias: Number(row.retencion_ganancias ?? 0),
    retencionIIBB: Number(row.retencion_iibb ?? 0),
    otrasRetenciones: Number(row.otras_retenciones ?? 0),
    comprobante: row.comprobante as string | undefined,
    observaciones: row.observaciones as string | undefined,
    deletedAt: row.deleted_at as string | undefined,
  };
}

function rowToCuentaCaja(row: Record<string, unknown>): CuentaCaja {
  return {
    id: row.id as string,
    nombre: (row.nombre as string) ?? '',
    tipo: row.tipo as string | undefined,
    moneda: (row.moneda as string) ?? 'ARS',
    saldoInicial: Number(row.saldo_inicial ?? 0),
    saldoActual: Number(row.saldo_actual ?? 0),
    activa: Boolean(row.activa),
  };
}

function rowToChecklistItem(row: Record<string, unknown>): ChecklistItemDB {
  return {
    id: row.id as string,
    operacionId: row.operacion_id as string,
    bloque: (row.bloque as string) ?? '',
    itemKey: (row.item_key as string) ?? '',
    label: (row.label as string) ?? '',
    estado: (row.estado as ChecklistState) ?? 'pendiente',
    observacion: row.observacion as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

// ============================================================
// AUDIT
// ============================================================

export async function logAudit(entry: AuditEntry): Promise<void> {
  try {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('auditoria').insert({
      usuario_id: user?.id ?? null,
      usuario_nombre: entry.usuarioNombre ?? user?.email ?? 'Sistema',
      modulo: entry.modulo,
      accion: entry.accion,
      registro_id: entry.registroId ?? null,
      registro_tipo: entry.registroTipo ?? null,
      campo_modificado: entry.campoModificado ?? null,
      valor_anterior: entry.valorAnterior ?? null,
      valor_nuevo: entry.valorNuevo ?? null,
      motivo: entry.motivo ?? null,
    });
  } catch {
    // Audit failures should not block main operations
  }
}

// ============================================================
// OPERACIONES
// ============================================================

export const operacionesService = {
  async getAll(): Promise<Operation[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('operaciones')
        .select('*')
        .is('deleted_at', null)
        .order('n_operacion', { ascending: true, nullsFirst: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(rowToOperation);
    } catch (e: unknown) {
      if (isSchemaError(e as { code?: string; message?: string })) throw e;
      return [];
    }
  },

  async getById(id: string): Promise<Operation | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('operaciones')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle();
    if (error || !data) return null;
    return rowToOperation(data as Record<string, unknown>);
  },

  async create(op: Omit<Operation, 'id' | 'ultimaActualizacion' | 'deletedAt'>): Promise<Operation | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('operaciones')
      .insert({
        n_operacion: op.nOperacion,
        n_carpeta_despacho: op.nCarpetaDespacho,
        n_carpeta_transporte: op.nCarpetaTransporte ?? null,
        cliente_id: op.clienteId ?? null,
        cliente_nombre: op.clienteNombre,
        referencia: op.referencia,
        proveedor: op.proveedor,
        tipo_operacion: op.tipoOperacion,
        servicio: op.servicio,
        responsable: op.responsable,
        fecha_apertura: op.fechaApertura || null,
        estado: op.estado,
        proximo_paso: op.proximoPaso,
        link_drive: op.linkDrive ?? null,
        observaciones: op.observaciones ?? null,
        checklist_progress: op.checklistProgress,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    await logAudit({
      modulo: 'operaciones',
      accion: 'crear',
      registroId: data.id,
      registroTipo: 'operacion',
      valorNuevo: op.nOperacion,
    });
    if (op.clienteId) {
      try {
        await fetch('/api/operaciones/sync-drive-folder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operacionId: data.id }),
        });
      } catch (e) {
        console.error('sync-drive-folder (create) error:', e);
      }
    }
    return rowToOperation(data as Record<string, unknown>);
  },

  async update(id: string, updates: Partial<Operation>, motivo?: string): Promise<Operation | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const prev = await this.getById(id);

    const dbUpdates: Record<string, unknown> = { updated_by: user?.id ?? null, ultima_actualizacion: new Date().toISOString() };
    if (updates.nOperacion !== undefined) dbUpdates.n_operacion = updates.nOperacion;
    if (updates.nCarpetaDespacho !== undefined) dbUpdates.n_carpeta_despacho = updates.nCarpetaDespacho;
    if (updates.nCarpetaTransporte !== undefined) dbUpdates.n_carpeta_transporte = updates.nCarpetaTransporte;
    if (updates.clienteId !== undefined) dbUpdates.cliente_id = updates.clienteId;
    if (updates.clienteNombre !== undefined) dbUpdates.cliente_nombre = updates.clienteNombre;
    if (updates.referencia !== undefined) dbUpdates.referencia = updates.referencia;
    if (updates.proveedor !== undefined) dbUpdates.proveedor = updates.proveedor;
    if (updates.servicio !== undefined) dbUpdates.servicio = updates.servicio;
    if (updates.responsable !== undefined) dbUpdates.responsable = updates.responsable;
    if (updates.fechaApertura !== undefined) dbUpdates.fecha_apertura = updates.fechaApertura || null;
    if (updates.estado !== undefined) dbUpdates.estado = updates.estado;
    if (updates.proximoPaso !== undefined) dbUpdates.proximo_paso = updates.proximoPaso;
    if (updates.linkDrive !== undefined) dbUpdates.link_drive = updates.linkDrive;
    if (updates.fechaArriboEstimado !== undefined) dbUpdates.fecha_arribo_estimado = updates.fechaArriboEstimado || null;
    if (updates.fechaOficializacion !== undefined) dbUpdates.fecha_oficializacion = updates.fechaOficializacion || null;
    if (updates.nDespacho !== undefined) dbUpdates.n_despacho = updates.nDespacho;
    if (updates.nRemito !== undefined) dbUpdates.n_remito = updates.nRemito;
    if (updates.fechaEntrega !== undefined) dbUpdates.fecha_entrega = updates.fechaEntrega || null;
    if (updates.facturaDespacho !== undefined) dbUpdates.factura_despacho = updates.facturaDespacho;
    if (updates.facturaTransporte !== undefined) dbUpdates.factura_transporte = updates.facturaTransporte;
    if (updates.reciboDespacho !== undefined) dbUpdates.recibo_despacho = updates.reciboDespacho;
    if (updates.reciboTransporte !== undefined) dbUpdates.recibo_transporte = updates.reciboTransporte;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones;
    if (updates.checklistProgress !== undefined) dbUpdates.checklist_progress = updates.checklistProgress;
    if (updates.mandadoCotizar !== undefined) dbUpdates.mandado_cotizar = updates.mandadoCotizar;
    if (updates.cotizacionEnviada !== undefined) dbUpdates.cotizacion_enviada = updates.cotizacionEnviada;
    if (updates.cotizacionAceptada !== undefined) dbUpdates.cotizacion_aceptada = updates.cotizacionAceptada;
    if (updates.enRecoleccion !== undefined) dbUpdates.en_recoleccion = updates.enRecoleccion;
    if (updates.draft !== undefined) dbUpdates.draft = updates.draft;
    if (updates.anmat !== undefined) dbUpdates.anmat = updates.anmat;
    if (updates.proforma !== undefined) dbUpdates.proforma = updates.proforma;
    if (updates.arribo !== undefined) dbUpdates.arribo = updates.arribo;
    if (updates.remito !== undefined) dbUpdates.remito = updates.remito;
    if (updates.entregadoCliente !== undefined) dbUpdates.entregado_cliente = updates.entregadoCliente;
    if (updates.estadoManual !== undefined) dbUpdates.estado_manual = updates.estadoManual;

    const { data, error } = await supabase
      .from('operaciones')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    await logAudit({
      modulo: 'operaciones',
      accion: 'editar',
      registroId: id,
      registroTipo: 'operacion',
      valorAnterior: prev?.estado,
      valorNuevo: updates.estado ?? prev?.estado,
      motivo,
    });
    const debeSincronizarDrive =
      updates.clienteId !== undefined ||
      updates.nCarpetaDespacho !== undefined ||
      updates.nCarpetaTransporte !== undefined ||
      updates.referencia !== undefined;
    if (debeSincronizarDrive) {
      try {
        await fetch('/api/operaciones/sync-drive-folder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operacionId: id }),
        });
      } catch (e) {
        console.error('sync-drive-folder (update) error:', e);
      }
    }
    return rowToOperation(data as Record<string, unknown>);
  },

  async softDelete(id: string, motivo: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('operaciones')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id ?? null,
        delete_reason: motivo,
        estado: 'cancelado',
        updated_by: user?.id ?? null,
      })
      .eq('id', id);
    if (error) return false;
    await logAudit({ modulo: 'operaciones', accion: 'anular', registroId: id, registroTipo: 'operacion', motivo });
    try {
      await fetch('/api/operaciones/trash-drive-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operacionId: id }),
      });
    } catch (e) {
      console.error('trash-drive-folder error:', e);
    }
    return true;
  },

  async getKPIs(): Promise<{
    abiertas: number;
    pendientesDoc: number;
    proximasArribar: number;
    entregadasSinFacturar: number;
    facturadaSinCobrar: number;
    sinActualizar72h: number;
  }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('operaciones')
        .select('estado, estado_manual, ultima_actualizacion, fecha_arribo_estimado')
        .is('deleted_at', null);
      if (error || !data) return { abiertas: 0, pendientesDoc: 0, proximasArribar: 0, entregadasSinFacturar: 0, facturadaSinCobrar: 0, sinActualizar72h: 0 };

      const now = new Date();
      const in7days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const h72ago = new Date(now.getTime() - 72 * 60 * 60 * 1000);

      // Helper: get effective closed/cancelled state respecting estadoManual
      const isEfCerrada = (r: { estado: string; estado_manual?: string }) => {
        if (r.estado_manual && r.estado_manual !== 'automatico') return r.estado_manual === 'cerrada';
        return isEstadoCerrado(r.estado);
      };
      const isEfCancelada = (r: { estado: string; estado_manual?: string }) => {
        if (r.estado_manual && r.estado_manual !== 'automatico') return r.estado_manual === 'cancelada';
        return isEstadoCancelado(r.estado);
      };
      const isEfOpen = (r: { estado: string; estado_manual?: string }) => !isEfCerrada(r) && !isEfCancelada(r);

      return {
        abiertas: data.filter(r => isEfOpen(r)).length,
        pendientesDoc: data.filter(r => isEfOpen(r) && (r.estado === 'esperando-documentacion' || r.estado === 'documentacion-observada')).length,
        proximasArribar: data.filter(r => { if (!r.fecha_arribo_estimado) return false; const d = new Date(r.fecha_arribo_estimado); return d >= now && d <= in7days; }).length,
        entregadasSinFacturar: data.filter(r => isEfOpen(r) && r.estado === 'entregado-falta-facturar').length,
        facturadaSinCobrar: data.filter(r => isEfOpen(r) && r.estado === 'facturado-falta-cobrar').length,
        sinActualizar72h: data.filter(r => { if (!r.ultima_actualizacion) return false; return new Date(r.ultima_actualizacion) < h72ago && isEfOpen(r); }).length,
      };
    } catch {
      return { abiertas: 0, pendientesDoc: 0, proximasArribar: 0, entregadasSinFacturar: 0, facturadaSinCobrar: 0, sinActualizar72h: 0 };
    }
  },
};

// ============================================================
// CHECKLIST
// ============================================================

export const checklistService = {
  async getByOperacion(operacionId: string): Promise<ChecklistItemDB[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('checklist_items')
        .select('*')
        .eq('operacion_id', operacionId)
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data ?? []).map(rowToChecklistItem);
    } catch {
      return [];
    }
  },

  async initForOperacion(operacionId: string): Promise<ChecklistItemDB[]> {
    const supabase = createClient();
    const defaults = buildDefaultChecklist(operacionId);
    let rows = defaults.map(d => ({
      operacion_id: d.operacionId,
      bloque: d.bloque,
      item_key: d.itemKey,
      label: d.label,
      estado: d.estado,
    }));
    // Upsert — won't duplicate due to unique index
    const { data, error } = await supabase
      .from('checklist_items')
      .upsert(rows, { onConflict: 'operacion_id,item_key', ignoreDuplicates: true })
      .select();
    if (error) return [];
    // Re-fetch to get all items
    return this.getByOperacion(operacionId);
  },

  async updateItem(id: string, estado: ChecklistState, observacion?: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('checklist_items')
      .update({
        estado,
        observacion: observacion ?? null,
        updated_at: new Date().toISOString(),
        updated_by: user?.id ?? null,
      })
      .eq('id', id);
    return !error;
  },

  async updateProgress(operacionId: string, items: ChecklistItemDB[]): Promise<void> {
    const applicable = items.filter(i => i.estado !== 'no-aplica');
    const done = applicable.filter(i => i.estado === 'hecho');
    const progress = applicable.length > 0 ? Math.round((done.length / applicable.length) * 100) : 0;
    const suggestedStatus = suggestStatusFromChecklist(items);

    const supabase = createClient();
    await supabase
      .from('operaciones')
      .update({
        checklist_progress: progress,
        estado: suggestedStatus,
        ultima_actualizacion: new Date().toISOString(),
      })
      .eq('id', operacionId);
  },
};

// ============================================================
// QUICK UPDATE SERVICE — for Excel-style inline editing
// ============================================================

const QV_FIELD_MAP: Record<string, string> = {
  qvMandadoCotizar: 'qv_mandado_cotizar',
  qvCotizacionEnviada: 'qv_cotizacion_enviada',
  qvCotizacionAceptada: 'qv_cotizacion_aceptada',
  qvCarpetaTransporte: 'qv_carpeta_transporte',
  qvEnRecoleccion: 'qv_en_recoleccion',
  qvDraft: 'qv_draft',
  qvAnmat: 'qv_anmat',
  qvProforma: 'qv_proforma',
  qvArribo: 'qv_arribo',
  qvOficializacion: 'qv_oficializacion',
  qvRemito: 'qv_remito',
  qvEntregadoCliente: 'qv_entregado_cliente',
  // Extra data fields
  qvObsDraft: 'qv_obs_draft',
  qvObsAnmat: 'qv_obs_anmat',
  qvObsCarpetaTransporte: 'qv_obs_carpeta_transporte',
  qvNCarpetaTransporteNro: 'qv_n_carpeta_transporte_nro',
  qvFechaArribo: 'qv_fecha_arribo',
  qvCanalOficializacion: 'qv_canal_oficializacion',
  qvObsEntregado: 'qv_obs_entregado',
  // Also sync to main fields when relevant
  nDespacho: 'n_despacho',
  nRemito: 'n_remito',
  fechaEntrega: 'fecha_entrega',
  fechaOficializacion: 'fecha_oficializacion',
  fechaArriboEstimado: 'fecha_arribo_estimado',
};

export const quickUpdateService = {
  /**
   * Update a single quick-view field on an operation.
   * Automatically syncs related fields (e.g. qvRemito=si → sets remito=true).
   * Recalculates and saves the auto status.
   * Logs to historial.
   */
  async updateField(
    operacionId: string,
    field: string,
    value: QuickValue | string | null,
    extraData?: QuickViewExtraData
  ): Promise<Operation | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    const dbUpdates: Record<string, unknown> = {
      updated_by: user?.id ?? null,
      ultima_actualizacion: new Date().toISOString(),
    };

    // Map camelCase field to snake_case DB column
    const dbField = QV_FIELD_MAP[field];
    if (dbField) {
      dbUpdates[dbField] = value;
    }

    // Sync boolean legacy fields
    if (field === 'qvMandadoCotizar') dbUpdates.mandado_cotizar = value === 'si';
    if (field === 'qvCotizacionEnviada') dbUpdates.cotizacion_enviada = value === 'si';
    if (field === 'qvCotizacionAceptada') dbUpdates.cotizacion_aceptada = value === 'si';
    if (field === 'qvEnRecoleccion') dbUpdates.en_recoleccion = value === 'si';
    if (field === 'qvDraft') dbUpdates.draft = value === 'si';
    if (field === 'qvAnmat') dbUpdates.anmat = value === 'si';
    if (field === 'qvProforma') dbUpdates.proforma = value === 'si';
    if (field === 'qvArribo') dbUpdates.arribo = value === 'si';
    if (field === 'qvRemito') dbUpdates.remito = value === 'si';
    if (field === 'qvEntregadoCliente') dbUpdates.entregado_cliente = value === 'si';

    // Apply extra data fields
    if (extraData) {
      if (extraData.nDespacho !== undefined) {
        dbUpdates.n_despacho = extraData.nDespacho || null;
        // NOTE: qv_n_despacho does NOT exist in DB — only n_despacho
      }
      if (extraData.fechaOficializacion !== undefined) {
        dbUpdates.fecha_oficializacion = extraData.fechaOficializacion || null;
      }
      if (extraData.canalOficializacion !== undefined) {
        dbUpdates.qv_canal_oficializacion = extraData.canalOficializacion || null;
      }
      if (extraData.obsOficializacion !== undefined) {
        dbUpdates.observaciones = extraData.obsOficializacion || null;
      }
      if (extraData.nRemito !== undefined) {
        dbUpdates.n_remito = extraData.nRemito || null;
      }
      if (extraData.fechaEntrega !== undefined) {
        dbUpdates.fecha_entrega = extraData.fechaEntrega || null;
      }
      if (extraData.obsEntregado !== undefined) {
        dbUpdates.qv_obs_entregado = extraData.obsEntregado || null;
      }
      if (extraData.nCarpetaTransporteNro !== undefined) {
        dbUpdates.qv_n_carpeta_transporte_nro = extraData.nCarpetaTransporteNro || null;
        if (extraData.nCarpetaTransporteNro) {
          dbUpdates.n_carpeta_transporte = extraData.nCarpetaTransporteNro;
        }
      }
      if (extraData.fechaArribo !== undefined) {
        dbUpdates.qv_fecha_arribo = extraData.fechaArribo || null;
        dbUpdates.fecha_arribo_estimado = extraData.fechaArribo || null;
      }
      if (extraData.obsDraft !== undefined) {
        dbUpdates.qv_obs_draft = extraData.obsDraft || null;
      }
      if (extraData.obsAnmat !== undefined) {
        dbUpdates.qv_obs_anmat = extraData.obsAnmat || null;
      }
    }

    const { data, error } = await supabase
      .from('operaciones')
      .update(dbUpdates)
      .eq('id', operacionId)
      .select()
      .single();

    if (error) {
      // Provide specific error messages instead of generic ones
      let specificMessage = 'Error al guardar';
      const msg = error.message ?? '';
      const code = error.code ?? '';

      if (!operacionId) {
        specificMessage = 'No se encontró la operación';
      } else if (code === 'PGRST116' || msg.includes('no rows')) {
        specificMessage = 'No se encontró la operación';
      } else if (code === '42703' || msg.includes('column') && msg.includes('does not exist')) {
        const colMatch = msg.match(/column "([^"]+)"/);
        specificMessage = colMatch ? `No existe columna ${colMatch[1]}` : 'Error de esquema en base de datos';
      } else if (code === '42501' || msg.includes('permission') || msg.includes('policy')) {
        specificMessage = 'Error de permisos en Supabase';
      } else if (code === '23502' || msg.includes('null value') || msg.includes('not-null')) {
        specificMessage = 'Falta un campo obligatorio';
      } else if (code === '23503' || msg.includes('foreign key')) {
        specificMessage = 'Error de referencia: operación no encontrada';
      } else if (msg.includes('fecha_oficializacion') || msg.includes('fecha oficialización')) {
        specificMessage = 'Falta fecha de oficialización';
      } else if (msg.includes('n_despacho') || msg.includes('despacho')) {
        specificMessage = 'Falta N° de despacho';
      }

      const enhancedError = new Error(specificMessage) as Error & { originalError: typeof error; specificMessage: string };
      enhancedError.originalError = error;
      enhancedError.specificMessage = specificMessage;
      throw enhancedError;
    }

    const updated = rowToOperation(data as Record<string, unknown>);

    // Log to historial
    try {
      const labelMap: Record<string, string> = {
        qvMandadoCotizar: 'Mandado a cotizar',
        qvCotizacionEnviada: 'Cotización enviada',
        qvCotizacionAceptada: 'Cotización aceptada',
        qvCarpetaTransporte: 'Carpeta transporte',
        qvEnRecoleccion: 'En recolección',
        qvDraft: 'Draft',
        qvAnmat: 'ANMAT',
        qvProforma: 'Proforma',
        qvArribo: 'Arribo',
        qvOficializacion: 'Oficialización',
        qvRemito: 'Remito',
        qvEntregadoCliente: 'Entregado al cliente',
      };
      const fieldLabel = labelMap[field] ?? field;
      const valueLabel = value === 'si' ? 'SI' : value === 'no' ? 'NO' : value === 'na' ? 'N/A' : value === 'obs' ? 'OBS' : String(value ?? '');
      await supabase.from('operacion_historial').insert({
        operacion_id: operacionId,
        usuario_id: user?.id ?? null,
        usuario_nombre: user?.email ?? 'Sistema',
        accion: 'actualización rápida',
        campo_modificado: fieldLabel,
        valor_nuevo: valueLabel,
      });
    } catch {
      // historial failures don't block
    }

    return updated;
  },
};

// ============================================================
// FACTURAS
// ============================================================

// Helper: detect if a factura is anulada by its concepto text
export function isAnuladaByConcepto(concepto: string): boolean {
  const c = (concepto ?? '').toUpperCase().trim();
  return c.includes('ANULAD') || c.includes('ANULADO') || c.includes('NOTA DE CR') || c === 'NC' || c.startsWith('NC ') || c.endsWith(' NC');
}

// Helper: detect if a factura should be considered cobrada based on its fields
export function isCobradaByFields(inv: {
  nRecibo?: string | null;
  fechaCobro?: string | null;
  recibido?: number | null;
  importe?: number | null;
}): boolean {
  const hasRecibo = !!(inv.nRecibo && inv.nRecibo.trim());
  const hasFechaCobro = !!(inv.fechaCobro && inv.fechaCobro.trim());
  const hasRecibido = (inv.recibido ?? 0) > 0;
  return hasRecibo || hasFechaCobro || hasRecibido;
}

export const facturasService = {
  async getAll(): Promise<Invoice[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('facturas')
        .select('*')
        .is('deleted_at', null)
        .order('fecha', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(rowToInvoice);
    } catch (e: unknown) {
      if (isSchemaError(e as { code?: string; message?: string })) throw e;
      return [];
    }
  },

  async getById(id: string): Promise<Invoice | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('facturas')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle();
    if (error || !data) return null;
    return rowToInvoice(data as Record<string, unknown>);
  },

  async getByCarpeta(nCarpeta: string): Promise<Invoice[]> {
    if (!nCarpeta) return [];
    const supabase = createClient();
    const { data, error } = await supabase
      .from('facturas')
      .select('*')
      .eq('n_carpeta', nCarpeta)
      .is('deleted_at', null)
      .order('fecha', { ascending: false });
    if (error) return [];
    return (data ?? []).map(rowToInvoice);
  },

  async getByCliente(clienteNombre: string): Promise<Invoice[]> {
    const supabase = createClient();
    // First try exact match
    const { data: exactData, error: exactError } = await supabase
      .from('facturas')
      .select('*')
      .eq('cliente_nombre', clienteNombre)
      .is('deleted_at', null)
      .order('fecha', { ascending: false });
    if (!exactError && exactData && exactData.length > 0) {
      return exactData.map(rowToInvoice);
    }
    // Fallback: fetch all and filter by normalized name to handle "OUTLANDISH" vs "OUTLANDISH SRL"
    const normalizedSearch = normalizeClienteName(clienteNombre);
    const { data, error } = await supabase
      .from('facturas')
      .select('*')
      .is('deleted_at', null)
      .order('fecha', { ascending: false })
      .range(0, 4999);
    if (error) return [];
    return (data ?? [])
      .filter((row: any) => normalizeClienteName(row.cliente_nombre) === normalizedSearch)
      .map(rowToInvoice);
  },

  async create(inv: Omit<Invoice, 'id' | 'deletedAt'>): Promise<Invoice | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    // ── Auto-detect estado based on fields ────────────────────────────────────
    // If concepto contains ANULADA/NC → anulada
    // If has recibo OR fecha cobro OR recibido > 0 → cobrada-total or cobrada-parcial
    // Otherwise → use provided estado
    let estadoFinal = inv.estado;
    const isNC = isAnuladaByConcepto(inv.concepto ?? '');
    if (isNC) {
      estadoFinal = 'anulada';
    } else if (isCobradaByFields(inv)) {
      // Calculate saldo to determine if fully or partially cobrada
      const saldoCalc = inv.importe - (inv.recibido ?? 0) - (inv.retencionGanancias ?? 0) - (inv.retencionIIBB ?? 0) - (inv.otrasRetenciones ?? 0);
      estadoFinal = saldoCalc <= 0 ? 'cobrada-total' : 'cobrada-parcial';
    }

    const saldo = isNC ? 0 : Math.max(0, inv.importe - (inv.recibido ?? 0) - (inv.retencionGanancias ?? 0) - (inv.retencionIIBB ?? 0) - (inv.otrasRetenciones ?? 0));

    // Try to find linked operation
    let operacionId = inv.operacionId ?? null;
    if (!operacionId && inv.nCarpeta) {
      const { data: ops } = await supabase
        .from('operaciones')
        .select('id')
        .or(`n_carpeta_despacho.eq.${inv.nCarpeta},n_carpeta_transporte.eq.${inv.nCarpeta}`)
        .is('deleted_at', null)
        .limit(1);
      if (ops && ops.length > 0) operacionId = ops[0].id;
    }

    // Build insert payload — include optional columns with fallback
    const insertPayload: Record<string, unknown> = {
      fecha: inv.fecha,
      cliente_id: inv.clienteId ?? null,
      cliente_nombre: inv.clienteNombre,
      punto_venta: inv.puntoVenta,
      tipo_comprobante: inv.tipoComprobante,
      n_factura: inv.nFactura,
      n_carpeta: inv.nCarpeta ?? null,
      operacion_id: operacionId,
      concepto: inv.concepto,
      importe: inv.importe,
      base: inv.base,
      alicuota_iva: inv.alicuotaIva,
      moneda: inv.moneda,
      tipo_operativa: inv.tipoOperativa,
      n_recibo: inv.nRecibo ?? null,
      fecha_cobro: inv.fechaCobro ?? null,
      recibido: inv.recibido,
      retencion_ganancias: inv.retencionGanancias,
      retencion_iibb: inv.retencionIIBB,
      otras_retenciones: inv.otrasRetenciones,
      saldo,
      estado: estadoFinal,
      observaciones: inv.observaciones ?? null,
      origen_registro: inv.origenRegistro ?? 'operativo_real',
      incluir_en_seguimiento_cobranza: inv.incluirEnSeguimientoCobranza ?? true,
      created_by: user?.id ?? null,
      updated_by: user?.id ?? null,
    };

    // Add optional columns if they exist in schema (safe with try/catch on insert)
    if ((inv as any).baseExenta !== undefined) insertPayload.base_exenta = (inv as any).baseExenta;
    // Always compute and save iva_21 from base and alicuota_iva
    const computedIva21 = (inv as any).iva21 !== undefined && (inv as any).iva21 > 0
      ? (inv as any).iva21
      : (inv.base > 0 && inv.alicuotaIva >= 0
        ? Math.round(inv.base * (inv.alicuotaIva / 100) * 100) / 100
        : 0);
    insertPayload.iva_21 = computedIva21;
    if ((inv as any).mesAnio !== undefined) insertPayload.mes_anio = (inv as any).mesAnio;
    if ((inv as any).neto !== undefined) insertPayload.neto = (inv as any).neto;

    const { data, error } = await supabase
      .from('facturas')
      .insert(insertPayload)
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    await logAudit({
      modulo: 'facturas',
      accion: 'crear',
      registroId: data.id,
      registroTipo: 'factura',
      valorNuevo: `${inv.tipoComprobante} ${inv.puntoVenta}-${inv.nFactura} | Estado: ${estadoFinal}`,
    });

    // ── Auto-update operacion with N° factura (only the number, no financial data) ──
    // Operaciones only stores the invoice number reference, not amounts or cobro details
    if (operacionId) {
      try {
        const tipoOp = inv.tipoOperativa ?? '';
        const isTransporte = tipoOp === 'Transporte' || tipoOp === 'Flete' || tipoOp === 'Transporte / Flete';
        const nFacturaRef = `${inv.tipoComprobante} ${inv.puntoVenta}-${inv.nFactura}`;
        const opUpdatePayload: Record<string, unknown> = {
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        };
        if (isTransporte) {
          opUpdatePayload.factura_transporte = nFacturaRef;
        } else {
          opUpdatePayload.factura_despacho = nFacturaRef;
        }
        await supabase.from('operaciones').update(opUpdatePayload).eq('id', operacionId);
      } catch {
        // Non-blocking — factura was saved successfully
      }
    }

    // ── Auto-link costos_proveedor by carpeta — update n_factura_cliente for profit metrics ──
    // This ensures profit is calculated by fecha factura cliente, not by fecha pago proveedor
    if (inv.nCarpeta) {
      try {
        const nFacturaClienteRef = `${inv.tipoComprobante} ${inv.puntoVenta}-${inv.nFactura}`;
        await supabase
          .from('costos_proveedor')
          .update({
            n_factura_cliente: nFacturaClienteRef,
            fecha_factura_cliente: inv.fecha ?? null,
            monto_facturado_cliente: inv.importe ?? 0,
            updated_by: user?.id ?? null,
          })
          .eq('n_carpeta', inv.nCarpeta)
          .is('n_factura_cliente', null); // Only update if not already linked
      } catch {
        // Non-blocking
      }
    }

    return rowToInvoice(data as Record<string, unknown>);
  },

  async update(id: string, updates: Partial<Invoice>): Promise<Invoice | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const dbUpdates: Record<string, unknown> = { updated_by: user?.id ?? null };
    // Main invoice fields
    if (updates.fecha !== undefined) dbUpdates.fecha = updates.fecha;
    if (updates.clienteNombre !== undefined) dbUpdates.cliente_nombre = updates.clienteNombre;
    if (updates.nFactura !== undefined) dbUpdates.n_factura = updates.nFactura;
    if (updates.nCarpeta !== undefined) dbUpdates.n_carpeta = updates.nCarpeta || null;
    if (updates.concepto !== undefined) dbUpdates.concepto = updates.concepto;
    if (updates.tipoComprobante !== undefined) dbUpdates.tipo_comprobante = updates.tipoComprobante;
    if (updates.puntoVenta !== undefined) dbUpdates.punto_venta = updates.puntoVenta;
    if (updates.moneda !== undefined) dbUpdates.moneda = updates.moneda;
    if (updates.tipoOperativa !== undefined) dbUpdates.tipo_operativa = updates.tipoOperativa;
    // Cobro fields
    if (updates.nRecibo !== undefined) dbUpdates.n_recibo = updates.nRecibo;
    if (updates.fechaCobro !== undefined) dbUpdates.fecha_cobro = updates.fechaCobro || null;
    if (updates.recibido !== undefined) dbUpdates.recibido = updates.recibido;
    if (updates.retencionGanancias !== undefined) dbUpdates.retencion_ganancias = updates.retencionGanancias;
    if (updates.retencionIIBB !== undefined) dbUpdates.retencion_iibb = updates.retencionIIBB;
    if (updates.otrasRetenciones !== undefined) dbUpdates.otras_retenciones = updates.otrasRetenciones;
    if (updates.estado !== undefined) dbUpdates.estado = updates.estado;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones;
    if (updates.importe !== undefined) dbUpdates.importe = updates.importe;
    if (updates.base !== undefined) dbUpdates.base = updates.base;
    if (updates.alicuotaIva !== undefined) dbUpdates.alicuota_iva = updates.alicuotaIva;
    if (updates.motivoAnulacion !== undefined) dbUpdates.motivo_anulacion = updates.motivoAnulacion ?? null;

    // Save iva_21: use explicitly provided value if > 0, otherwise recalculate from base and alicuota
    if (updates.iva21 !== undefined && updates.iva21 > 0) {
      dbUpdates.iva_21 = updates.iva21;
    } else if (updates.base !== undefined || updates.alicuotaIva !== undefined) {
      const baseForIva = updates.base ?? 0;
      const alicuotaForIva = updates.alicuotaIva !== undefined ? updates.alicuotaIva : 21;
      if (baseForIva > 0 && alicuotaForIva >= 0) {
        dbUpdates.iva_21 = Math.round(baseForIva * (alicuotaForIva / 100) * 100) / 100;
      } else {
        dbUpdates.iva_21 = 0;
      }
    }

    // If anulada: force saldo=0, skip cobro auto-logic
    if (updates.estado === 'anulada') {
      dbUpdates.saldo = 0;
      dbUpdates.incluir_en_seguimiento_cobranza = false;
    } else {
      // Recalculate saldo
      const current = await this.getById(id);
      if (current) {
        const importe = updates.importe ?? current.importe;
        const recibido = updates.recibido ?? current.recibido;
        const retGan = updates.retencionGanancias ?? current.retencionGanancias;
        const retIIBB = updates.retencionIIBB ?? current.retencionIIBB;
        const retOtras = updates.otrasRetenciones ?? current.otrasRetenciones;
        const saldo = importe - recibido - retGan - retIIBB - retOtras;
        dbUpdates.saldo = saldo;
        // Auto-set estado based on cobro fields
        if (updates.recibido !== undefined || updates.retencionGanancias !== undefined || updates.retencionIIBB !== undefined || updates.otrasRetenciones !== undefined || updates.nRecibo !== undefined || updates.fechaCobro !== undefined) {
          const nRecibo = updates.nRecibo ?? current.nRecibo;
          const fechaCobro = updates.fechaCobro ?? current.fechaCobro;
          if (saldo <= 0) {
            dbUpdates.estado = 'cobrada-total';
          } else if (recibido > 0 || retGan > 0 || retIIBB > 0 || retOtras > 0 || nRecibo || fechaCobro) {
            dbUpdates.estado = 'cobrada-parcial';
          }
        }
      }
    }

    const { data, error } = await supabase
      .from('facturas')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    if (error) return null;
    return rowToInvoice(data as Record<string, unknown>);
  },

  async softDelete(id: string, motivo: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('facturas')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id ?? null,
        delete_reason: motivo,
        estado: 'anulada',
        updated_by: user?.id ?? null,
      })
      .eq('id', id);
    if (error) return false;

    await logAudit({ modulo: 'facturas', accion: 'anular', registroId: id, registroTipo: 'factura', motivo });
    return true;
  },

  async getKPIs(): Promise<{
    totalFacturado: number;
    totalPendiente: number;
    totalCobrado: number;
    vencidas: number;
    cantidadFacturas: number;
    cantidadPendientes: number;
    cantidadCobradas: number;
  }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('facturas')
        .select('importe, saldo, recibido, estado, fecha_cobro')
        .is('deleted_at', null)
        .neq('estado', 'anulada');
      if (error || !data) return { totalFacturado: 0, totalPendiente: 0, totalCobrado: 0, vencidas: 0, cantidadFacturas: 0, cantidadPendientes: 0, cantidadCobradas: 0 };

      const totalFacturado = data.reduce((s, r) => s + Number(r.importe ?? 0), 0);
      const totalPendiente = data.reduce((s, r) => s + Number(r.saldo ?? 0), 0);
      const totalCobrado = data.reduce((s, r) => s + Number(r.recibido ?? 0), 0);
      const cantidadFacturas = data.length;
      const cantidadPendientes = data.filter(r => r.estado === 'pendiente-cobro' || r.estado === 'pendiente' || r.estado === 'emitida' || r.estado === 'enviada').length;
      const cantidadCobradas = data.filter(r => r.estado === 'cobrada-total').length;
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const vencidas = data.filter(r => {
        if (r.estado === 'cobrada-total' || r.estado === 'anulada') return false;
        if (!r.fecha_cobro) return Number(r.saldo ?? 0) > 0;
        return new Date(r.fecha_cobro) < thirtyDaysAgo && Number(r.saldo ?? 0) > 0;
      }).length;

      return { totalFacturado, totalPendiente, totalCobrado, vencidas, cantidadFacturas, cantidadPendientes, cantidadCobradas };
    } catch {
      return { totalFacturado: 0, totalPendiente: 0, totalCobrado: 0, vencidas: 0, cantidadFacturas: 0, cantidadPendientes: 0, cantidadCobradas: 0 };
    }
  },
};

// ============================================================
// COBRANZAS
// ============================================================

export const cobranzasService = {
  async getAll(): Promise<Cobranza[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('cobranzas')
        .select('*')
        .is('deleted_at', null)
        .order('fecha_cobro', { ascending: false });
      if (error) return [];
      return (data ?? []).map(rowToCobranza);
    } catch {
      return [];
    }
  },

  async getByCliente(clienteNombre: string): Promise<Cobranza[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('cobranzas')
      .select('*')
      .eq('cliente_nombre', clienteNombre)
      .is('deleted_at', null)
      .order('fecha_cobro', { ascending: false });
    if (error) return [];
    return (data ?? []).map(rowToCobranza);
  },

  async getByCarpeta(nCarpeta: string): Promise<Cobranza[]> {
    if (!nCarpeta) return [];
    const supabase = createClient();
    const { data, error } = await supabase
      .from('cobranzas')
      .select('*')
      .eq('n_carpeta', nCarpeta)
      .is('deleted_at', null)
      .order('fecha_cobro', { ascending: false });
    if (error) return [];
    return (data ?? []).map(rowToCobranza);
  },

  // Main create: creates cobro, updates factura, creates movimiento_caja, saves medios_pago and echeqs
  // Returns { cobro, factura } on success, throws Error with specific message on failure
  async create(
    cobro: Omit<Cobranza, 'id' | 'deletedAt'>,
    mediosPago?: CobroMedioPago[]
  ): Promise<{ cobro: Cobranza; factura?: Invoice } | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    // ── Helper: normalize invoice number for fuzzy matching ──────────────────
    const normalizeFacturaNum = (s: string): string =>
      s.replace(/[-\s]/g, '').replace(/^0+/, '') || '0';

    // ── 1. Find factura ───────────────────────────────────────────────────────
    let facturaId = cobro.facturaId ?? null;
    let factura: Invoice | null = null;

    // CRITICAL FIX: If facturaId is already provided, fetch the factura object
    // Without this, factura stays null and the invoice update is skipped entirely
    if (facturaId) {
      const { data: fDirect } = await supabase
        .from('facturas')
        .select('*')
        .eq('id', facturaId)
        .is('deleted_at', null)
        .maybeSingle();
      if (fDirect) {
        factura = rowToInvoice(fDirect as Record<string, unknown>);
      }
    }

    if (!facturaId && cobro.nFactura) {
      const { data: fExact } = await supabase
        .from('facturas')
        .select('*')
        .eq('n_factura', cobro.nFactura.trim())
        .is('deleted_at', null)
        .limit(1);

      if (fExact && fExact.length > 0) {
        facturaId = fExact[0].id;
        factura = rowToInvoice(fExact[0] as Record<string, unknown>);
      } else {
        const { data: allFacturas } = await supabase
          .from('facturas')
          .select('*')
          .is('deleted_at', null);

        const inputNorm = normalizeFacturaNum(cobro.nFactura.trim());
        const match = (allFacturas ?? []).find(
          (f) => normalizeFacturaNum(String(f.n_factura ?? '')) === inputNorm
        );
        if (match) {
          facturaId = match.id;
          factura = rowToInvoice(match as Record<string, unknown>);
        }
      }
    }

    if (!facturaId && cobro.nCarpeta) {
      const { data: fRows } = await supabase
        .from('facturas')
        .select('*')
        .eq('n_carpeta', cobro.nCarpeta.trim())
        .is('deleted_at', null)
        .limit(1);
      if (fRows && fRows.length > 0) {
        facturaId = fRows[0].id;
        factura = rowToInvoice(fRows[0] as Record<string, unknown>);
      }
    }

    // Block save if no invoice found and not explicitly marked as unallocated
    if (!facturaId && !cobro.noImputado) {
      let errMsg = cobro.nFactura
        ? `No se encontró una factura pendiente con ese punto de venta, número, cliente y carpeta. Verificá que la factura esté cargada en Facturación.`
        : `Debés seleccionar una factura pendiente para registrar el cobro imputado, o marcar "Cobro no imputado".`;
      console.error('[cobranzasService.create]', errMsg);
      throw new Error(errMsg);
    }

    // ── 2. Validate amount vs invoice ─────────────────────────────────────────
    // NOTE: Overpayment is ALLOWED when using E-cheq — the excess stays in the e-cheq
    // as available balance (monto_usado tracks only what was applied to the invoice).
    // For non-e-cheq payments, overpayment is still blocked.
    if (factura) {
      const totalMedios = mediosPago && mediosPago.length > 0
        ? mediosPago.reduce((s, m) => s + m.monto, 0)
        : cobro.monto;
      const totalCancelado = totalMedios + cobro.retencionGanancias + cobro.retencionIIBB + cobro.otrasRetenciones;
      const efectiveSaldo = (factura.saldo <= 0 && factura.estado === 'emitida') || (factura.saldo <= 0 && factura.estado === 'enviada') || (factura.saldo <= 0 && factura.estado === 'pendiente-cobro') || (factura.saldo <= 0 && factura.estado === 'pendiente')
        ? factura.importe
        : factura.saldo;
      // Check if any medio is E-cheq (overpayment allowed for e-cheqs)
      const hasEcheq = mediosPago && mediosPago.some(m => m.medio === 'E-cheq');
      if (!hasEcheq && totalCancelado > efectiveSaldo + 0.01) {
        let errMsg = `El cobro supera el saldo de la factura. Saldo actual: $${efectiveSaldo.toLocaleString('es-AR')}. Total cancelado: $${totalCancelado.toLocaleString('es-AR')}. Revisá montos o retenciones.`;
        console.error('[cobranzasService.create]', errMsg);
        throw new Error(errMsg);
      }
      // E-cheqs: no saldo validation — montos no tienen que coincidir exactamente
    }

    // ── 3. Normalize date ─────────────────────────────────────────────────────
    const normalizeDate = (d: string): string => {
      if (!d) throw new Error('La fecha de cobro es obligatoria.');
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      const parts = d.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        if (day && month && year && year.length === 4) {
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      throw new Error(`La fecha de cobro tiene formato inválido: "${d}". Usá el formato DD/MM/YYYY o YYYY-MM-DD.`);
    };

    let fechaCobroISO: string;
    try {
      fechaCobroISO = normalizeDate(cobro.fechaCobro);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Fecha de cobro inválida.';
      console.error('[cobranzasService.create] Fecha inválida:', msg);
      throw new Error(msg);
    }

    // ── 4. Find operacion ─────────────────────────────────────────────────────
    let operacionId = cobro.operacionId ?? null;
    if (!operacionId && cobro.nCarpeta) {
      const { data: ops } = await supabase
        .from('operaciones')
        .select('id')
        .or(`n_carpeta_despacho.eq.${cobro.nCarpeta},n_carpeta_transporte.eq.${cobro.nCarpeta}`)
        .is('deleted_at', null)
        .limit(1);
      if (ops && ops.length > 0) operacionId = ops[0].id;
    }

    // ── 5. Determine monto total from medios_pago or cobro.monto ─────────────
    const montoTotal = mediosPago && mediosPago.length > 0
      ? mediosPago.reduce((s, m) => s + m.monto, 0)
      : cobro.monto;

    // Determine primary medio for legacy field
    const primaryMedio = mediosPago && mediosPago.length > 0
      ? mediosPago[0].medio
      : cobro.medioCobro;
    const primaryCuenta = mediosPago && mediosPago.length > 0
      ? (mediosPago[0].cuentaDestino ?? cobro.cuentaIngreso)
      : cobro.cuentaIngreso;

    // ── 6. Insert cobro ───────────────────────────────────────────────────────
    const { data: cobroData, error: cobroError } = await supabase
      .from('cobranzas')
      .insert({
        cliente_id: cobro.clienteId ?? null,
        cliente_nombre: cobro.clienteNombre,
        factura_id: facturaId,
        n_factura: cobro.nFactura ?? null,
        n_carpeta: cobro.nCarpeta ?? null,
        operacion_id: operacionId,
        fecha_cobro: fechaCobroISO,
        monto: montoTotal,
        moneda: cobro.moneda,
        medio_cobro: primaryMedio,
        cuenta_ingreso: primaryCuenta,
        n_recibo: cobro.nRecibo,
        retencion_ganancias: cobro.retencionGanancias,
        retencion_iibb: cobro.retencionIIBB,
        otras_retenciones: cobro.otrasRetenciones,
        comprobante: cobro.comprobante ?? null,
        observaciones: cobro.observaciones ?? null,
        no_imputado: cobro.noImputado ?? false,
        origen_registro: 'operativo_real',
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      })
      .select()
      .single();

    if (cobroError) {
      console.error('[cobranzasService.create] Error insertando cobro:', cobroError);
      let errMsg = 'No se pudo registrar el cobro.';
      if (cobroError.code === '42501') errMsg = 'Error de permisos en Supabase. Contactá al administrador.';
      else if (cobroError.code === '42703') errMsg = `La tabla cobranzas no tiene la columna requerida: ${cobroError.message}`;
      else if (cobroError.code === '23502') errMsg = `Falta un campo obligatorio en el cobro: ${cobroError.message}`;
      else if (cobroError.code === '23503') errMsg = `Referencia inválida (factura o cliente no encontrado): ${cobroError.message}`;
      else if (cobroError.message) errMsg = cobroError.message;
      throw new Error(errMsg);
    }
    const newCobro = rowToCobranza(cobroData as Record<string, unknown>);

    // ── 7. Save medios_pago lines ─────────────────────────────────────────────
    if (mediosPago && mediosPago.length > 0) {
      const totalRetencionesCobro = cobro.retencionGanancias + cobro.retencionIIBB + cobro.otrasRetenciones;

      // Track how much of the invoice saldo has been consumed by ALL previous medios
      // (both e-cheq and non-e-cheq), so each new medio only applies against the real remaining balance.
      const efectiveSaldoBase = factura
        ? ((factura.saldo <= 0 && factura.estado !== 'cobrada-total' && factura.estado !== 'cobrada-parcial')
          ? factura.importe
          : factura.saldo)
        : null;

      // Start with saldo minus retenciones; each medio will reduce this
      let globalRemainingForMedios = efectiveSaldoBase !== null
        ? Math.max(0, efectiveSaldoBase - totalRetencionesCobro)
        : null;

      for (const medio of mediosPago) {
        // The amount this medio actually applies to the invoice
        // (already capped by the form, but we enforce it server-side too)
        const montoMedioAplicado = globalRemainingForMedios !== null
          ? Math.min(medio.monto, Math.max(0, globalRemainingForMedios))
          : medio.monto;

        const { data: medioData, error: medioError } = await supabase
          .from('cobro_medios_pago')
          .insert({
            cobranza_id: newCobro.id,
            medio: medio.medio,
            cuenta_destino: medio.cuentaDestino ?? null,
            monto: medio.monto,
            moneda: medio.moneda,
            tipo_cambio: medio.tipoCambio ?? null,
            observaciones: medio.observaciones ?? null,
            created_by: user?.id ?? null,
          })
          .select()
          .single();

        if (medioError) {
          console.warn('[cobranzasService.create] Error guardando medio de pago:', medioError.message);
          // Still reduce globalRemaining even if medio insert failed, to keep accounting consistent
          if (globalRemainingForMedios !== null) {
            globalRemainingForMedios = Math.max(0, globalRemainingForMedios - montoMedioAplicado);
          }
          continue;
        }

        // ── 8. Save echeqs if medio is E-cheq ───────────────────────────────
        if (medio.medio === 'E-cheq' && medio.echeqs && medio.echeqs.length > 0) {
          // remainingInvoiceSaldo for THIS medio starts at globalRemainingForMedios
          // (already accounts for all previous medios applied before this one)
          let remainingInvoiceSaldo = globalRemainingForMedios;

          for (const echeq of medio.echeqs) {
            // For existing echeqs (echeqCarteraId set), monto is already the exact amount to apply.
            // For new echeqs: apply MIN(full e-cheq amount, remaining invoice saldo).
            // This ensures the e-cheq is NOT fully consumed when it exceeds the invoice amount.
            const montoUsado = echeq.echeqCarteraId
              ? (remainingInvoiceSaldo !== null
                  ? Math.min(echeq.monto, Math.max(0, remainingInvoiceSaldo))
                  : echeq.monto)
              : (remainingInvoiceSaldo !== null
                  ? Math.min(echeq.monto, Math.max(0, remainingInvoiceSaldo))
                  : echeq.monto);

            // Reduce remaining invoice saldo for the next e-cheq in this medio
            if (remainingInvoiceSaldo !== null) {
              remainingInvoiceSaldo = Math.max(0, remainingInvoiceSaldo - montoUsado);
            }

            if (echeq.echeqCarteraId) {
              // ── UPDATE existing echeq in cartera ──────────────────────────
              // CONCEPTUAL FIX: Applying an e-cheq to a factura (cobranza) does NOT
              // consume its financial availability. monto_usado is only updated by
              // financial movements: depósito, cesión a proveedor, anulación.
              // Here we only update factura_id for trazabilidad, NOT monto_usado.
              const { data: existingEcheq } = await supabase
                .from('echeqs')
                .select('monto_usado, importe, monto, estado')
                .eq('id', echeq.echeqCarteraId)
                .single();

              if (existingEcheq) {
                const montoTotal = Number(existingEcheq.monto ?? existingEcheq.importe ?? 0);
                const montoUsadoActual = Number(existingEcheq.monto_usado ?? 0);
                const saldoActual = Math.max(0, montoTotal - montoUsadoActual);
                const estadoActual = existingEcheq.estado ?? 'en-cartera';
                // Keep estado as-is (en-cartera / recibido) — NOT 'cobrado' just because it was applied to a factura
                // Only update factura_id for trazabilidad
                await supabase
                  .from('echeqs')
                  .update({
                    factura_id: facturaId ?? null,
                    updated_by: user?.id ?? null,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('id', echeq.echeqCarteraId);

                // ── INSERT audit record in echeq_aplicaciones (trazabilidad only) ──
                await supabase.from('echeq_aplicaciones').insert({
                  echeq_id: echeq.echeqCarteraId,
                  cobranza_id: newCobro.id,
                  factura_id: facturaId ?? null,
                  monto_aplicado: montoUsado,
                  saldo_anterior: saldoActual,
                  saldo_nuevo: saldoActual, // financial saldo unchanged — only trazabilidad
                  estado_anterior: estadoActual,
                  estado_nuevo: estadoActual, // estado unchanged
                  tipo_aplicacion: 'cobranza',
                  observaciones: `Trazabilidad: e-cheq aplicado a factura${cobro.nFactura ? ` N°${cobro.nFactura}` : ''} — ${cobro.clienteNombre}. Disponibilidad financiera NO modificada.`,
                  usuario: user?.email ?? 'Sistema',
                  created_by: user?.id ?? null,
                });
              }
            } else {
              // ── INSERT new echeq record into cartera ──────────────────────
              // ── ANTI-DUPLICATE GUARD: check if this e-cheq already exists ─
              // Match by BOTH n_echeq AND banco to avoid false positives with different banks.
              const bancoNormGuard = (echeq.bancoEmisor ?? '').trim().toLowerCase();
              const { data: allWithSameNEcheq } = await supabase
                .from('echeqs')
                .select('id, banco, banco_emisor, estado, cobranza_id, monto_usado, monto, importe')
                .eq('n_echeq', echeq.nEcheq ?? '')
                .not('estado', 'in', '("anulado","rechazado")')
                .is('deleted_at', null);

              // Find a match where banco also matches (case-insensitive)
              const existingDuplicateRow = (allWithSameNEcheq ?? []).find(row => {
                const rowBanco = ((row.banco ?? '') as string).trim().toLowerCase();
                const rowBancoEmisor = ((row.banco_emisor ?? '') as string).trim().toLowerCase();
                return rowBanco === bancoNormGuard || rowBancoEmisor === bancoNormGuard ||
                  rowBanco.includes(bancoNormGuard) || bancoNormGuard.includes(rowBanco) ||
                  rowBancoEmisor.includes(bancoNormGuard) || bancoNormGuard.includes(rowBancoEmisor);
              });
              const existingDuplicate = existingDuplicateRow ? [existingDuplicateRow] : [];

              if (existingDuplicate && existingDuplicate.length > 0) {
                // E-cheq already registered — record trazabilidad only, do NOT update monto_usado
                const dupEcheq = existingDuplicate[0];
                const montoTotal = Number(dupEcheq.monto ?? dupEcheq.importe ?? 0);
                const montoUsadoActual = Number(dupEcheq.monto_usado ?? 0);
                const saldoActual = Math.max(0, montoTotal - montoUsadoActual);
                const estadoActual = dupEcheq.estado ?? 'en-cartera';

                console.warn(
                  `[cobranzasService.create] E-cheq N°${echeq.nEcheq} ya existe (id: ${dupEcheq.id}). Registrando trazabilidad sin modificar disponibilidad financiera. Disponible: $${saldoActual}.`
                );

                // Only update factura_id for trazabilidad — do NOT touch monto_usado
                await supabase
                  .from('echeqs')
                  .update({
                    factura_id: facturaId ?? null,
                    updated_by: user?.id ?? null,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('id', dupEcheq.id);

                // Insert audit record (trazabilidad only)
                await supabase.from('echeq_aplicaciones').insert({
                  echeq_id: dupEcheq.id,
                  cobranza_id: newCobro.id,
                  factura_id: facturaId ?? null,
                  monto_aplicado: montoUsado,
                  saldo_anterior: saldoActual,
                  saldo_nuevo: saldoActual, // financial saldo unchanged
                  estado_anterior: estadoActual,
                  estado_nuevo: estadoActual, // estado unchanged
                  tipo_aplicacion: 'cobranza',
                  observaciones: `Trazabilidad: e-cheq N°${echeq.nEcheq} aplicado a factura${cobro.nFactura ? ` N°${cobro.nFactura}` : ''}. Disponibilidad financiera NO modificada.`,
                  usuario: user?.email ?? 'Sistema',
                  created_by: user?.id ?? null,
                });

                // ── INSERT historial_cheques for duplicate e-cheq (new cobro row) ──
                // Same e-cheq number used for a different invoice — always insert a new row.
                if (echeq.nEcheq?.trim()) {
                  const { data: existingPagoDup } = await supabase
                    .from('historial_cheques')
                    .select('proveedor_nombre, n_factura_proveedor, proveedor_factura_id')
                    .eq('n_echeq', echeq.nEcheq.trim())
                    .not('proveedor_nombre', 'is', null)
                    .limit(1)
                    .maybeSingle();

                  try {
                    await supabase.from('historial_cheques').insert({
                      n_echeq: echeq.nEcheq.trim(),
                      banco_emisor: echeq.bancoEmisor ?? null,
                      monto: echeq.monto,
                      fecha: fechaCobroISO,
                      cliente_nombre: cobro.clienteNombre ?? null,
                      n_factura_cliente: cobro.nFactura ?? null,
                      cobranza_id: newCobro.id,
                      echeq_cartera_id: dupEcheq.id,
                      proveedor_nombre: existingPagoDup?.proveedor_nombre ?? null,
                      n_factura_proveedor: existingPagoDup?.n_factura_proveedor ?? null,
                      proveedor_factura_id: existingPagoDup?.proveedor_factura_id ?? null,
                      estado: existingPagoDup ? 'aplicado-a-proveedor' : 'cobrado-sin-aplicar',
                      updated_at: new Date().toISOString(),
                      created_by: user?.id ?? null,
                    });
                  } catch (e) {
                    console.warn('[cobranzasService] historial_cheques insert (dup branch) failed (non-blocking):', e);
                  }
                }
                // Continue — do NOT throw, do NOT insert duplicate
              } else {
                // E-cheq not found — insert new record
                // CONCEPTUAL FIX: monto_usado starts at 0 — the e-cheq enters cartera fully available.
                // Applying to a factura does NOT consume financial availability.
                const { data: newEcheqData, error: echeqError } = await supabase
                  .from('echeqs')
                  .insert({
                    cobranza_id: newCobro.id,
                    cobro_medio_id: medioData?.id ?? null,
                    banco: echeq.bancoEmisor ?? null,
                    banco_emisor: echeq.bancoEmisor ?? null,
                    n_echeq: echeq.nEcheq ?? null,
                    cuit_librador: echeq.cuitLibrador ?? null,
                    fecha_recepcion: fechaCobroISO,
                    fecha_emision: echeq.fechaEmision ?? null,
                    fecha_vencimiento: echeq.fechaVencimiento ?? null,
                    importe: echeq.monto,
                    monto: echeq.monto,
                    monto_usado: 0, // FIXED: always 0 when entering cartera via cobranza
                    estado: 'en-cartera', // FIXED: always 'en-cartera' — NOT 'cobrado'
                    cliente_nombre: cobro.clienteNombre,
                    n_carpeta: cobro.nCarpeta ?? null,
                    factura_id: facturaId,
                    observaciones: echeq.observaciones ?? null,
                    created_by: user?.id ?? null,
                  })
                  .select('id')
                  .single();
                if (echeqError) {
                  console.error('[cobranzasService.create] FATAL: Error guardando e-cheq en cartera:', echeqError.message, echeqError.code, echeqError.details);
                  // CRITICAL: Revert the cobro — the echeq record MUST exist for the cartera to be consistent
                  await supabase.from('cobranzas').delete().eq('id', newCobro.id);
                  let errMsg = `No se pudo registrar el e-cheq N°${echeq.nEcheq ?? '?'} en cartera. El cobro fue revertido para mantener consistencia.`;
                  if (echeqError.code === '42501') errMsg = `Sin permisos para registrar el e-cheq en cartera. Contactá al administrador.`;
                  else if (echeqError.code === '23502') errMsg = `Falta un campo obligatorio en el e-cheq: ${echeqError.message}`;
                  else if (echeqError.message) errMsg = `Error al registrar e-cheq en cartera: ${echeqError.message}. El cobro fue revertido.`;
                  throw new Error(errMsg);
                }
                // ── INSERT audit record for new echeq (trazabilidad) ────────
                if (newEcheqData?.id) {
                  await supabase.from('echeq_aplicaciones').insert({
                    echeq_id: newEcheqData.id,
                    cobranza_id: newCobro.id,
                    factura_id: facturaId ?? null,
                    monto_aplicado: montoUsado,
                    saldo_anterior: echeq.monto,
                    saldo_nuevo: echeq.monto, // financial saldo = full amount (unchanged)
                    estado_anterior: 'en-cartera',
                    estado_nuevo: 'en-cartera', // FIXED: always en-cartera
                    tipo_aplicacion: 'cobranza',
                    observaciones: `Nuevo e-cheq N°${echeq.nEcheq ?? '?'} recibido. Monto total: $${echeq.monto.toLocaleString('es-AR')}. Trazabilidad: aplicado a factura${cobro.nFactura ? ` N°${cobro.nFactura}` : ''}: $${montoUsado.toLocaleString('es-AR')}. Disponible financiero en cartera: $${echeq.monto.toLocaleString('es-AR')}.`,
                    usuario: user?.email ?? 'Sistema',
                    created_by: user?.id ?? null,
                  });

                  // ── INSERT historial_cheques (lado cobro) — always a new row ────────────────
                  // A same e-cheq number can appear in multiple cobros (multiple client invoices).
                  // Each cobro gets its own row in historial_cheques.
                  if (echeq.nEcheq?.trim()) {
                    // Check if proveedor side already exists for this n_echeq
                    const { data: existingPago } = await supabase
                      .from('historial_cheques')
                      .select('proveedor_nombre, n_factura_proveedor, proveedor_factura_id')
                      .eq('n_echeq', echeq.nEcheq.trim())
                      .not('proveedor_nombre', 'is', null)
                      .limit(1)
                      .maybeSingle();

                    await supabase.from('historial_cheques').insert({
                      n_echeq: echeq.nEcheq.trim(),
                      banco_emisor: echeq.bancoEmisor ?? null,
                      monto: echeq.monto,
                      fecha: fechaCobroISO,
                      cliente_nombre: cobro.clienteNombre ?? null,
                      n_factura_cliente: cobro.nFactura ?? null,
                      cobranza_id: newCobro.id,
                      echeq_cartera_id: newEcheqData.id,
                      // If proveedor side already exists, link it immediately
                      proveedor_nombre: existingPago?.proveedor_nombre ?? null,
                      n_factura_proveedor: existingPago?.n_factura_proveedor ?? null,
                      proveedor_factura_id: existingPago?.proveedor_factura_id ?? null,
                      estado: existingPago ? 'aplicado-a-proveedor' : 'cobrado-sin-aplicar',
                      updated_at: new Date().toISOString(),
                      created_by: user?.id ?? null,
                    });
                  }
                }
              }
            }
          }
        }

        // ── Reduce globalRemainingForMedios after this medio is processed ────
        // This ensures the next medio in the loop sees the correct remaining balance.
        if (globalRemainingForMedios !== null) {
          globalRemainingForMedios = Math.max(0, globalRemainingForMedios - montoMedioAplicado);
        }
      }
    }

    // ── 9. Update factura ─────────────────────────────────────────────────────
    let updatedFactura: Invoice | undefined;
    if (facturaId && factura) {
      // Use importe as base if saldo is 0 (invoice not yet partially paid)
      const baseSaldo = (factura.saldo <= 0 && factura.estado !== 'cobrada-total' && factura.estado !== 'cobrada-parcial')
        ? factura.importe
        : factura.saldo;
      // Cap the applied amount to the invoice saldo (e-cheq overpayment stays in e-cheq)
      const totalRetenciones = cobro.retencionGanancias + cobro.retencionIIBB + cobro.otrasRetenciones;
      const montoAplicado = Math.min(montoTotal, Math.max(0, baseSaldo - totalRetenciones));
      const newRecibido = (factura.recibido ?? 0) + montoAplicado;
      const newRetGan = (factura.retencionGanancias ?? 0) + cobro.retencionGanancias;
      const newRetIIBB = (factura.retencionIIBB ?? 0) + cobro.retencionIIBB;
      const newRetOtras = (factura.otrasRetenciones ?? 0) + cobro.otrasRetenciones;
      const newSaldoRaw = factura.importe - newRecibido - newRetGan - newRetIIBB - newRetOtras;
      // Apply rounding tolerance: if saldo is within 1 cent of zero, treat as fully paid
      const ROUNDING_TOLERANCE = 0.01;
      const newSaldo = newSaldoRaw <= ROUNDING_TOLERANCE ? 0 : Math.max(0, newSaldoRaw);
      // Estado: cobrada-total when saldo=0, cobrada-parcial only when there is real remaining balance
      const newEstado: InvoiceStatus = newSaldo <= 0 ? 'cobrada-total' : 'cobrada-parcial';

      const { data: fUpdated, error: fError } = await supabase
        .from('facturas')
        .update({
          n_recibo: cobro.nRecibo || factura.nRecibo,
          fecha_cobro: fechaCobroISO,
          recibido: newRecibido,
          retencion_ganancias: newRetGan,
          retencion_iibb: newRetIIBB,
          otras_retenciones: newRetOtras,
          saldo: newSaldo,
          estado: newEstado,
          updated_by: user?.id ?? null,
        })
        .eq('id', facturaId)
        .select()
        .single();

      if (fError) {
        console.error('[cobranzasService.create] Error actualizando factura:', fError);
        // CRITICAL: Revert the cobro if invoice update fails
        await supabase.from('cobranzas').delete().eq('id', newCobro.id);
        let errMsg = `El cobro fue creado pero falló la actualización de la factura (ID: ${facturaId}). El cobro fue revertido.`;
        if (fError.code === '42501') errMsg = `RLS bloqueó la actualización de la factura (ID: ${facturaId}). Contactá al administrador.`;
        else if (fError.code === '23503') errMsg = `Error de referencia al actualizar la factura: ${fError.message}`;
        else if (fError.message) errMsg = `No se pudo actualizar la factura: ${fError.message}`;
        throw new Error(errMsg);
      } else if (fUpdated) {
        updatedFactura = rowToInvoice(fUpdated as Record<string, unknown>);
      }

      // ── 9b. Update operacion with ONLY n_recibo (no financial data in Operaciones) ────────────────────
      if (operacionId && cobro.nRecibo) {
        // Determine which recibo field to update based on invoice type
        // Operaciones only stores the recibo number — no amounts, no cobro details
        const facturaLinked = updatedFactura ?? factura;
        const tipoOperativa = facturaLinked?.tipoOperativa ?? '';
        const isTransporte = tipoOperativa === 'Transporte' || tipoOperativa === 'Flete' || tipoOperativa === 'Transporte / Flete';

        const updatePayload: Record<string, unknown> = {
          updated_by: user?.id ?? null,
        };
        if (isTransporte) {
          updatePayload.recibo_transporte = cobro.nRecibo;
        } else {
          // Default: despacho or any other type
          updatePayload.recibo_despacho = cobro.nRecibo;
        }

        const { error: opError } = await supabase
          .from('operaciones')
          .update(updatePayload)
          .eq('id', operacionId);
        if (opError) {
          console.warn('[cobranzasService.create] No se pudo actualizar recibo en operación:', opError.message);
        }
      }
    } else if (facturaId && !factura) {
      // facturaId was provided but factura could not be fetched — revert cobro
      await supabase.from('cobranzas').delete().eq('id', newCobro.id);
      throw new Error(`No se pudo cargar la factura con ID ${facturaId} para actualizarla. El cobro fue revertido.`);
    }

    // ── 10. Create movimiento_caja for each medio that generates real cash ────
    // E-cheq: registered as "E-cheq en cartera" ingreso — ALWAYS, regardless of cajaImpactEnabled
    // Retenciones: cancel saldo but do NOT enter caja
    // Transferencia, Efectivo ARS/USD, Mercado Pago, Otro: enter caja when cajaImpactEnabled
    try {
      // ── E-CHEQ: always create movimiento_caja as "ingreso en cartera" ────────
      // IMPORTANT: Skip movimiento_caja for existing echeqs (echeqCarteraId set) —
      // they already have a movement from when they were first received.
      // Only create a new movement for NEW echeqs being received for the first time.
      const echeqMedios = mediosPago
        ? mediosPago.filter(m => m.medio === 'E-cheq' && m.monto > 0 && !m.echeqs?.[0]?.echeqCarteraId)
        : cobro.medioCobro === 'E-cheq' ? [{ medio: 'E-cheq', monto: cobro.monto, moneda: cobro.moneda }] : [];

      for (const echeqMedio of echeqMedios) {
        const echeqDetalle = echeqMedio.echeqs?.[0];
        const nEcheqStr = echeqDetalle?.nEcheq ? ` N°${echeqDetalle.nEcheq}` : '';
        const bancoStr = echeqDetalle?.bancoEmisor ? ` (${echeqDetalle.bancoEmisor})` : '';
        // Use the full e-cheq amount (not the applied amount to the invoice)
        // The full e-cheq enters the cartera regardless of how much was applied to the invoice
        const montoEcheqCompleto = echeqDetalle?.monto ?? echeqMedio.monto;
        const nFacturaStr = cobro.nFactura ? ` — Factura ${cobro.nFactura}` : (cobro.nCarpeta ? ` — Carpeta ${cobro.nCarpeta}` : '');
        const { error: echeqMovError } = await supabase.from('movimientos_caja').insert({
          fecha: fechaCobroISO,
          tipo: 'ingreso',
          tipo_movimiento: 'ingreso',
          categoria: 'E-cheq en cartera',
          concepto: `E-cheq recibido${nEcheqStr}${bancoStr}${nFacturaStr} — ${cobro.clienteNombre}`,
          monto: montoEcheqCompleto,
          moneda: echeqMedio.moneda ?? cobro.moneda ?? 'ARS',
          medio_pago: 'E-cheq',
          cuenta_nombre: 'E-cheqs en cartera',
          cliente_nombre: cobro.clienteNombre,
          n_carpeta: cobro.nCarpeta ?? null,
          factura_id: facturaId ?? null,
          n_recibo: cobro.nRecibo,
          observaciones: `Cobro${nFacturaStr} — E-cheq${nEcheqStr}${bancoStr}`,
          usuario: user?.email ?? 'Sistema',
          created_by: user?.id ?? null,
          origen_registro: 'operativo_real',
        });
        if (echeqMovError) {
          console.warn('[cobranzasService.create] Error creando movimiento caja para e-cheq:', echeqMovError.message);
        }
      }

      // ── CAJA IMPACT for non-echeq medios ────────────────────────────────────
      // ALWAYS impact caja for Transferencia, Efectivo, Mercado Pago, Otro
      // E-cheq: already handled above as "E-cheq en cartera" — NOT as available cash
      // Retenciones: cancel saldo but do NOT enter caja
      const mediosParaCaja = mediosPago && mediosPago.length > 0
        ? mediosPago.filter(m => m.medio !== 'E-cheq' && m.medio !== 'Cheque físico')
        : (cobro.medioCobro !== 'E-cheq' && cobro.medioCobro !== 'Cheque físico')
          ? [{ medio: cobro.medioCobro, cuentaDestino: cobro.cuentaIngreso, monto: cobro.monto, moneda: cobro.moneda }]
          : [];

      for (const medioItem of mediosParaCaja) {
        // ── Auto-route account by medio if not explicitly set ─────────────────
        let cuentaNombre = medioItem.cuentaDestino ?? cobro.cuentaIngreso;
        if (!cuentaNombre) {
          const medioLower = (medioItem.medio ?? '').toLowerCase();
          if (medioLower.includes('transferencia')) {
            cuentaNombre = 'Banco ARS';
          } else if (medioLower.includes('efectivo ars') || medioLower === 'efectivo') {
            cuentaNombre = 'Caja Efectivo ARS';
          } else if (medioLower.includes('efectivo usd')) {
            cuentaNombre = 'Caja Efectivo USD';
          } else if (medioLower.includes('mercado pago')) {
            cuentaNombre = 'Banco ARS';
          } else {
            cuentaNombre = 'Otros medios';
          }
        }
        const { data: cuentas } = await supabase
          .from('cuentas_caja')
          .select('id, saldo_actual')
          .ilike('nombre', `%${cuentaNombre}%`)
          .limit(1);

        if (!cuentas || cuentas.length === 0) {
          console.warn(`[cobranzasService.create] No se encontró la cuenta "${cuentaNombre}" en Caja/Bancos.`);
          continue;
        }

        const cuenta = cuentas[0];
        const { error: movError } = await supabase.from('movimientos_caja').insert({
          fecha: fechaCobroISO,
          tipo: 'ingreso',
          categoria: 'Cobro cliente',
          concepto: `Cobro recibo ${cobro.nRecibo} — Factura ${cobro.nFactura ?? cobro.nCarpeta} — ${medioItem.medio}`,
          cuenta_nombre: cuentaNombre,
          monto: medioItem.monto,
          moneda: medioItem.moneda ?? cobro.moneda,
          medio_pago: medioItem.medio,
          cliente_nombre: cobro.clienteNombre,
          n_carpeta: cobro.nCarpeta ?? null,
          operacion_id: operacionId,
          factura_id: facturaId,
          n_recibo: cobro.nRecibo,
          observaciones: `Cobro recibo ${cobro.nRecibo} - Factura ${cobro.nFactura ?? cobro.nCarpeta} - ${medioItem.medio}`,
          usuario: user?.email ?? 'Sistema',
          created_by: user?.id ?? null,
          origen_registro: 'operativo_real',
        });
        if (movError) {
          console.warn('[cobranzasService.create] Error creando movimiento caja:', movError.message);
        } else {
          await supabase
            .from('cuentas_caja')
            .update({ saldo_actual: Number(cuenta.saldo_actual ?? 0) + medioItem.monto })
            .eq('id', cuenta.id);

          // ── AUTO 0.6% FEE for Transferencia ────────────────────────────────
          // Applies to BOTH ingreso and egreso for transfers
          const isTransferencia = (medioItem.medio ?? '').toLowerCase().includes('transferencia');
          if (isTransferencia && medioItem.monto > 0) {
            const comisionMonto = Math.round(medioItem.monto * 0.006);
            if (comisionMonto > 0) {
              await supabase.from('movimientos_caja').insert({
                fecha: fechaCobroISO,
                tipo: 'egreso',
                categoria: 'Comisión transferencia',
                concepto: `Impuesto bancario 0,6% — Cobro recibo ${cobro.nRecibo} — ${cobro.clienteNombre}`,
                cuenta_nombre: cuentaNombre,
                monto: comisionMonto,
                moneda: medioItem.moneda ?? cobro.moneda,
                medio_pago: 'Transferencia',
                cliente_nombre: cobro.clienteNombre,
                n_carpeta: cobro.nCarpeta ?? null,
                factura_id: facturaId,
                n_recibo: cobro.nRecibo,
                observaciones: `Impuesto bancario 0,6% sobre transferencia de ${medioItem.monto.toLocaleString('es-AR')}. Neto acreditado: ${(medioItem.monto - comisionMonto).toLocaleString('es-AR')}`,
                es_impuesto_bancario: true,
                impuesto_bancario_tipo: 'credito',
                impuesto_bancario_alicuota: 0.006,
                usuario: user?.email ?? 'Sistema',
                created_by: user?.id ?? null,
                origen_registro: 'operativo_real',
              });
              // Update cuenta saldo to reflect the fee deduction
              const { data: cuentaActual } = await supabase
                .from('cuentas_caja')
                .select('saldo_actual')
                .eq('id', cuenta.id)
                .single();
              if (cuentaActual) {
                await supabase
                  .from('cuentas_caja')
                  .update({ saldo_actual: Number(cuentaActual.saldo_actual ?? 0) - comisionMonto })
                  .eq('id', cuenta.id);
              }
            }
          }
        }
      }
    } catch (cajErr) {
      console.warn('[cobranzasService.create] Movimiento caja falló (no bloquea el cobro):', cajErr);
    }

    await logAudit({
      modulo: 'cobranzas', accion: 'crear', registroId: newCobro.id, registroTipo: 'cobranza',
      valorNuevo: `Recibo ${cobro.nRecibo} - $${montoTotal} - Factura ${cobro.nFactura ?? cobro.nCarpeta}`,
    });

    return { cobro: newCobro, factura: updatedFactura };
  },

  async softDelete(id: string, motivo: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    // ── 1. Load the cobro to know which factura to restore ───────────────────
    const { data: cobroRow } = await supabase
      .from('cobranzas')
      .select('*')
      .eq('id', id)
      .single();

    // ── 2. Soft-delete the cobro ─────────────────────────────────────────────
    const { error } = await supabase
      .from('cobranzas')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id ?? null,
        delete_reason: motivo,
        updated_by: user?.id ?? null,
      })
      .eq('id', id);
    if (error) return false;

    // ── 3. Restore the factura to its previous state ─────────────────────────
    if (cobroRow && cobroRow.factura_id) {
      let facturaId = cobroRow.factura_id;
      const { data: facturaRow } = await supabase
        .from('facturas')
        .select('*')
        .eq('id', facturaId)
        .single();

      if (facturaRow) {
        const montoCobro = Number(cobroRow.monto ?? 0);
        const retGan = Number(cobroRow.retencion_ganancias ?? 0);
        const retIIBB = Number(cobroRow.retencion_iibb ?? 0);
        const retOtras = Number(cobroRow.otras_retenciones ?? 0);

        const prevRecibido = Math.max(0, Number(facturaRow.recibido ?? 0) - montoCobro);
        const prevRetGan = Math.max(0, Number(facturaRow.retencion_ganancias ?? 0) - retGan);
        const prevRetIIBB = Math.max(0, Number(facturaRow.retencion_iibb ?? 0) - retIIBB);
        const prevRetOtras = Math.max(0, Number(facturaRow.otras_retenciones ?? 0) - retOtras);
        const importe = Number(facturaRow.importe ?? 0);
        const prevSaldo = Math.max(0, importe - prevRecibido - prevRetGan - prevRetIIBB - prevRetOtras);

        // Determine restored estado
        let restoredEstado: InvoiceStatus;
        if (prevRecibido <= 0 && prevRetGan <= 0 && prevRetIIBB <= 0 && prevRetOtras <= 0) {
          // No payments remain — restore to pending
          restoredEstado = 'pendiente';
        } else if (prevSaldo <= 0) {
          restoredEstado = 'cobrada-total';
        } else {
          restoredEstado = 'cobrada-parcial';
        }

        await supabase
          .from('facturas')
          .update({
            recibido: prevRecibido,
            retencion_ganancias: prevRetGan,
            retencion_iibb: prevRetIIBB,
            otras_retenciones: prevRetOtras,
            saldo: prevSaldo,
            estado: restoredEstado,
            updated_by: user?.id ?? null,
          })
          .eq('id', facturaId);
      }
    }

    // ── 4. Restore echeq monto_usado if any echeqs were linked ────────────
    if (cobroRow) {
      const { data: echeqRows } = await supabase
        .from('echeqs')
        .select('id, monto, monto_usado')
        .eq('cobranza_id', id)
        .is('deleted_at', null);

      if (echeqRows && echeqRows.length > 0) {
        for (const echeq of echeqRows) {
          // Restore monto_usado to 0 (the e-cheq is fully available again)
          await supabase
            .from('echeqs')
            .update({ monto_usado: 0, estado: 'en-cartera', updated_by: user?.id ?? null })
            .eq('id', echeq.id);
        }
      }
    }

    await logAudit({ modulo: 'cobranzas', accion: 'anular', registroId: id, registroTipo: 'cobranza', motivo });
    return true;
  },

  async getKPIs(): Promise<{ totalCobrado: number; cantidadCobros: number; totalRetenciones: number }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('cobranzas')
        .select('monto, retencion_ganancias, retencion_iibb, otras_retenciones')
        .is('deleted_at', null);
      if (error || !data) return { totalCobrado: 0, cantidadCobros: 0, totalRetenciones: 0 };
      return {
        totalCobrado: data.reduce((s, r) => s + Number(r.monto ?? 0), 0),
        cantidadCobros: data.length,
        totalRetenciones: data.reduce((s, r) => s + Number(r.retencion_ganancias ?? 0) + Number(r.retencion_iibb ?? 0) + Number(r.otras_retenciones ?? 0), 0),
      };
    } catch {
      return { totalCobrado: 0, cantidadCobros: 0, totalRetenciones: 0 };
    }
  },

  // ── RESUMEN PRE-REVERSIÓN: muestra qué va a revertir antes de ejecutar ──
  async getResumenCobranzasEcheq(nFacturas: string[]): Promise<{
    cobranzas: Array<{
      cobranzaId: string;
      clienteNombre: string;
      nRecibo: string | null;
      facturaId: string | null;
      nFactura: string | null;
      montoCobranza: number;
      echeqs: Array<{
        echeqId: string;
        nEcheq: string | null;
        banco: string | null;
        montoTotal: number;
        montoUsado: number;
        montoDisponible: number;
        estado: string;
      }>;
      movimientos: Array<{
        movId: string;
        concepto: string | null;
        monto: number;
        categoria: string | null;
      }>;
    }>;
  }> {
    const supabase = createClient();
    try {
      // Find facturas by n_factura
      const { data: facturasRows } = await supabase
        .from('facturas')
        .select('id, n_factura, importe, saldo, estado, cliente_id')
        .in('n_factura', nFacturas);

      let result: Array<{
        cobranzaId: string;
        clienteNombre: string;
        nRecibo: string | null;
        facturaId: string | null;
        nFactura: string | null;
        montoCobranza: number;
        echeqs: Array<{ echeqId: string; nEcheq: string | null; banco: string | null; montoTotal: number; montoUsado: number; montoDisponible: number; estado: string }>;
        movimientos: Array<{ movId: string; concepto: string | null; monto: number; categoria: string | null }>;
      }> = [];

      for (let factura of facturasRows ?? []) {
        // Find cobranzas for this factura
        const { data: cobranzasRows } = await supabase
          .from('cobranzas')
          .select('*')
          .eq('factura_id', factura.id)
          .is('deleted_at', null);

        for (const cobro of cobranzasRows ?? []) {
          // Find echeqs linked to this cobranza
          const { data: echeqRows } = await supabase
            .from('echeqs')
            .select('*')
            .eq('cobranza_id', cobro.id)
            .is('deleted_at', null);

          // Find movimientos for this cobranza/factura
          const { data: movRows } = await supabase
            .from('movimientos_caja')
            .select('id, concepto, monto, categoria')
            .is('deleted_at', null)
            .or(`factura_id.eq.${factura.id},n_recibo.eq.${cobro.n_recibo ?? ''}`);

          result.push({
            cobranzaId: cobro.id,
            clienteNombre: cobro.cliente_nombre ?? '',
            nRecibo: cobro.n_recibo ?? null,
            facturaId: factura.id,
            nFactura: factura.n_factura ?? null,
            montoCobranza: Number(cobro.monto ?? 0),
            echeqs: (echeqRows ?? []).map((e: Record<string, unknown>) => {
              const monto = Number(e.monto ?? e.importe ?? 0);
              const montoUsado = Number(e.monto_usado ?? 0);
              return {
                echeqId: e.id as string,
                nEcheq: e.n_echeq as string | null,
                banco: (e.banco_emisor ?? e.banco) as string | null,
                montoTotal: monto,
                montoUsado,
                montoDisponible: Math.max(0, monto - montoUsado),
                estado: e.estado as string,
              };
            }),
            movimientos: (movRows ?? []).map((m: Record<string, unknown>) => ({
              movId: m.id as string,
              concepto: m.concepto as string | null,
              monto: Number(m.monto ?? 0),
              categoria: m.categoria as string | null,
            })),
          });
        }

        // If no cobranzas found, still show the factura as a row
        if (!cobranzasRows || cobranzasRows.length === 0) {
          result.push({
            cobranzaId: '__sin_cobranza__',
            clienteNombre: '',
            nRecibo: null,
            facturaId: factura.id,
            nFactura: factura.n_factura ?? null,
            montoCobranza: 0,
            echeqs: [],
            movimientos: [],
          });
        }
      }

      return { cobranzas: result };
    } catch (e) {
      console.error('[cobranzasService.getResumenCobranzasEcheq]', e);
      return { cobranzas: [] };
    }
  },

  // ── REVERSIÓN DEFINITIVA: revierte facturas por n_factura (fuente primaria) ──
  // No depende de que el e-cheq exista en cartera. Busca por factura → cobranza → movimientos → echeqs.
  // Soporta búsqueda por sufijo: "2523" encuentra "00001-00002523"
  async revertirFacturasPorNumero(
    nFacturas: string[],
    motivo: string = 'Reversión definitiva por error de conciliación de e-cheqs y saldos parciales'
  ): Promise<{
    success: boolean;
    revertidas: number;
    errores: string[];
    resumen: string;
  }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const errores: string[] = [];
    let revertidas = 0;
    const resumenParts: string[] = [];

    for (const nFactura of nFacturas) {
      try {
        // ── 1. Find the factura — try exact match first, then suffix match ──
        let facturaRows: any[] | null = null;

        // Try exact match
        const { data: exactRows } = await supabase
          .from('facturas')
          .select('*')
          .eq('n_factura', nFactura)
          .order('created_at', { ascending: false })
          .limit(1);

        if (exactRows && exactRows.length > 0) {
          facturaRows = exactRows;
        } else {
          // Try suffix match (e.g. "2523" matches "00002523")
          const { data: suffixRows } = await supabase
            .from('facturas')
            .select('*')
            .like('n_factura', `%${nFactura}`)
            .order('created_at', { ascending: false })
            .limit(1);
          facturaRows = suffixRows;
        }

        let factura = facturaRows?.[0];
        if (!factura) {
          errores.push(`Factura N°${nFactura}: no encontrada`);
          continue;
        }

        // ── 2. Restore factura to pendiente-cobro ──────────────────────────
        await supabase
          .from('facturas')
          .update({
            recibido: 0,
            retencion_ganancias: 0,
            retencion_iibb: 0,
            otras_retenciones: 0,
            saldo: Number(factura.importe ?? 0),
            estado: 'pendiente-cobro',
            n_recibo: null,
            fecha_cobro: null,
            updated_by: user?.id ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', factura.id);

        resumenParts.push(`Factura N°${factura.n_factura} restaurada a pendiente-cobro (importe=${factura.importe})`);

        // ── 3. Find all cobranzas for this factura ─────────────────────────
        const { data: cobranzasRows } = await supabase
          .from('cobranzas')
          .select('*')
          .eq('factura_id', factura.id)
          .is('deleted_at', null);

        for (const cobro of cobranzasRows ?? []) {
          // ── 3a. Delete echeq_aplicaciones for this cobranza ───────────────
          await supabase
            .from('echeq_aplicaciones')
            .delete()
            .eq('cobranza_id', cobro.id);

          // ── 3b. Find and delete echeqs linked to this cobranza ────────────
          const { data: echeqRows } = await supabase
            .from('echeqs')
            .select('*')
            .eq('cobranza_id', cobro.id)
            .is('deleted_at', null);

          for (const echeq of echeqRows ?? []) {
            await supabase.from('echeq_aplicaciones').delete().eq('echeq_id', echeq.id);
            await supabase.from('echeqs').delete().eq('id', echeq.id);
            resumenParts.push(`E-cheq N°${echeq.n_echeq ?? echeq.id} eliminado (monto=${echeq.monto ?? echeq.importe})`);
          }

          // ── 3c. Delete cobro_medios_pago ──────────────────────────────────
          await supabase.from('cobro_medios_pago').delete().eq('cobranza_id', cobro.id);

          // ── 3d. Delete movimientos_caja for this cobranza/factura ─────────
          const { data: movRows } = await supabase
            .from('movimientos_caja')
            .select('id, concepto, monto')
            .or(`factura_id.eq.${factura.id}${cobro.n_recibo ? `,n_recibo.eq.${cobro.n_recibo}` : ''}`);

          for (const mov of movRows ?? []) {
            await supabase.from('movimientos_caja').delete().eq('id', mov.id);
          }

          if ((movRows ?? []).length > 0) {
            resumenParts.push(`${movRows!.length} movimiento(s) eliminados para cobranza id=${cobro.id}`);
          }

          // ── 3e. Delete the cobranza ────────────────────────────────────────
          await supabase.from('cobranzas').delete().eq('id', cobro.id);
          resumenParts.push(`Cobranza id=${cobro.id} (nRecibo=${cobro.n_recibo ?? '-'}) eliminada`);
        }

        // ── 4. Delete orphan echeqs linked to factura (no cobranza) ────────
        const { data: orphanEcheqs } = await supabase
          .from('echeqs')
          .select('*')
          .eq('factura_id', factura.id)
          .is('deleted_at', null);

        for (const echeq of orphanEcheqs ?? []) {
          await supabase.from('echeq_aplicaciones').delete().eq('echeq_id', echeq.id);
          await supabase.from('echeqs').delete().eq('id', echeq.id);
          resumenParts.push(`E-cheq huérfano N°${echeq.n_echeq ?? echeq.id} eliminado`);
        }

        // ── 5. Delete orphan movimientos_caja by factura_id ─────────────────
        const { data: orphanMovs } = await supabase
          .from('movimientos_caja')
          .select('id, concepto, monto')
          .eq('factura_id', factura.id);

        for (const mov of orphanMovs ?? []) {
          await supabase.from('movimientos_caja').delete().eq('id', mov.id);
        }

        if ((orphanMovs ?? []).length > 0) {
          resumenParts.push(`${orphanMovs!.length} movimiento(s) huérfano(s) eliminados para Factura N°${factura.n_factura}`);
        }

        // ── 6. Audit log per factura ───────────────────────────────────────
        await supabase.from('auditoria').insert({
          usuario_id: user?.id ?? null,
          usuario_nombre: user?.email ?? 'Sistema',
          fecha_hora: new Date().toISOString(),
          modulo: 'cobranzas',
          accion: 'reversion_definitiva_factura',
          registro_id: factura.id,
          registro_tipo: 'factura',
          campo_modificado: 'estado_factura_cobranza',
          valor_anterior: `Factura N°${factura.n_factura} | estado=${factura.estado} | saldo=${factura.saldo}`,
          valor_nuevo: `pendiente-cobro | saldo=${factura.importe} | cobranzas/echeqs/movimientos eliminados`,
          motivo,
        });

        revertidas++;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errores.push(`Factura N°${nFactura}: ${msg}`);
        console.error(`[cobranzasService.revertirFacturasPorNumero] Factura N°${nFactura}:`, e);
      }
    }

    // Final audit summary
    if (revertidas > 0) {
      await supabase.from('auditoria').insert({
        usuario_id: user?.id ?? null,
        usuario_nombre: user?.email ?? 'Sistema',
        fecha_hora: new Date().toISOString(),
        modulo: 'cobranzas',
        accion: 'reversion_definitiva_masiva',
        registro_tipo: 'facturas_echeq',
        campo_modificado: 'reversion_definitiva',
        valor_anterior: `Facturas activas: ${nFacturas.join(', ')}`,
        valor_nuevo: `${revertidas} factura(s) revertidas. ${errores.length} errores.`,
        motivo,
      });
    }

    return {
      success: errores.length === 0,
      revertidas,
      errores,
      resumen: resumenParts.join(' | '),
    };
  },

  // ── REVERSIÓN COMPLETA: revierte cobranzas E-cheq por números de e-cheq ──
  // NOTA: Este método ahora delega a revertirFacturasPorNumero cuando el e-cheq no está en cartera.
  async revertirCobranzasEcheq(
    nEcheqs: string[],
    motivo: string = 'Reversión manual por error de conciliación de e-cheqs y saldos parciales'
  ): Promise<{
    success: boolean;
    revertidas: number;
    errores: string[];
    resumen: string;
  }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const errores: string[] = [];
    let revertidas = 0;
    const resumenParts: string[] = [];

    for (const nEcheq of nEcheqs) {
      try {
        // ── 1. Find the echeq ──────────────────────────────────────────────
        const { data: echeqRows } = await supabase
          .from('echeqs')
          .select('*')
          .eq('n_echeq', nEcheq.trim())
          .is('deleted_at', null)
          .limit(1);

        const echeq = echeqRows?.[0];
        if (!echeq) {
          errores.push(`E-cheq N°${nEcheq}: no encontrado en cartera`);
          continue;
        }

        // ── 2. Find the cobranza ───────────────────────────────────────────
        let cobranza: Record<string, unknown> | null = null;
        if (echeq.cobranza_id) {
          const { data: cRow } = await supabase
            .from('cobranzas')
            .select('*')
            .eq('id', echeq.cobranza_id)
            .is('deleted_at', null)
            .single();
          cobranza = cRow ?? null;
        }

        // ── 3. Restore factura to pending ──────────────────────────────────
        let facturaId = cobranza?.factura_id ?? echeq.factura_id;
        if (facturaId) {
          const { data: facturaRow } = await supabase
            .from('facturas')
            .select('*')
            .eq('id', facturaId)
            .single();

          if (facturaRow) {
            await supabase
              .from('facturas')
              .update({
                recibido: 0,
                retencion_ganancias: 0,
                retencion_iibb: 0,
                otras_retenciones: 0,
                saldo: Number(facturaRow.importe ?? 0),
                estado: 'pendiente',
                updated_by: user?.id ?? null,
                updated_at: new Date().toISOString(),
              })
              .eq('id', facturaId);

            resumenParts.push(
              `Factura id=${facturaId} restaurada a pendiente (importe=${facturaRow.importe})`
            );
          }
        }

        // ── 4. Soft-delete the cobranza ────────────────────────────────────
        if (cobranza) {
          await supabase
            .from('cobranzas')
            .update({
              deleted_at: new Date().toISOString(),
              deleted_by: user?.id ?? null,
              delete_reason: motivo,
              updated_by: user?.id ?? null,
            })
            .eq('id', cobranza.id as string)
            .is('deleted_at', null);

          resumenParts.push(`Cobranza id=${cobranza.id} anulada`);
        }

        // ── 5. Annul movimientos_caja for this echeq ───────────────────────
        const { data: movRows } = await supabase
          .from('movimientos_caja')
          .select('id, concepto, monto')
          .is('deleted_at', null)
          .eq('anulado', false)
          .or(`medio_pago.ilike.%E-cheq%,categoria.ilike.%E-cheq%`)
          .or(`concepto.ilike.%${nEcheq}%`);

        for (const mov of movRows ?? []) {
          await supabase
            .from('movimientos_caja')
            .update({
              anulado: true,
              anulado_at: new Date().toISOString(),
              anulado_motivo: `${motivo} — E-cheq N°${nEcheq}`,
            })
            .eq('id', mov.id);
        }

        if ((movRows ?? []).length > 0) {
          resumenParts.push(`${movRows!.length} movimiento(s) anulados para E-cheq N°${nEcheq}`);
        }

        // ── 6. Delete echeq_aplicaciones ───────────────────────────────────
        await supabase
          .from('echeq_aplicaciones')
          .delete()
          .eq('echeq_id', echeq.id);

        // ── 7. Soft-delete the echeq from cartera ──────────────────────────
        await supabase
          .from('echeqs')
          .update({
            deleted_at: new Date().toISOString(),
            deleted_by: user?.id ?? null,
            delete_reason: motivo,
            monto_usado: 0,
            estado: 'en-cartera',
            updated_by: user?.id ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', echeq.id);

        resumenParts.push(
          `E-cheq N°${nEcheq} (${echeq.banco_emisor ?? echeq.banco ?? 'Galicia'}) $${echeq.monto ?? echeq.importe} anulado y eliminado de cartera`
        );

        // ── 8. Audit log ───────────────────────────────────────────────────
        await supabase.from('auditoria').insert({
          usuario_id: user?.id ?? null,
          usuario_nombre: user?.email ?? 'Sistema',
          fecha_hora: new Date().toISOString(),
          modulo: 'cobranzas',
          accion: 'reversion_echeq',
          registro_id: echeq.id,
          registro_tipo: 'echeq',
          campo_modificado: 'estado_echeq_cobranza',
          valor_anterior: `E-cheq N°${nEcheq} | monto_usado=${echeq.monto_usado} | estado=${echeq.estado}`,
          valor_nuevo: 'anulado | monto_usado=0 | factura=pendiente',
          motivo,
        });

        revertidas++;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errores.push(`E-cheq N°${nEcheq}: ${msg}`);
        console.error(`[cobranzasService.revertirCobranzasEcheq] E-cheq N°${nEcheq}:`, e);
      }
    }

    // Final audit summary
    if (revertidas > 0) {
      await supabase.from('auditoria').insert({
        usuario_id: user?.id ?? null,
        usuario_nombre: user?.email ?? 'Sistema',
        fecha_hora: new Date().toISOString(),
        modulo: 'cobranzas',
        accion: 'reversion_masiva_echeqs',
        registro_tipo: 'cobranza_echeq',
        campo_modificado: 'reversion_04062026',
        valor_anterior: `E-cheqs activos: ${nEcheqs.join(', ')}`,
        valor_nuevo: `${revertidas} e-cheqs revertidos. ${errores.length} errores.`,
        motivo,
      });
    }

    return {
      success: errores.length === 0,
      revertidas,
      errores,
      resumen: resumenParts.join(' | '),
    };
  },
};

// ============================================================
// CLIENTES
// ============================================================

export const clientesService = {
  async getAll(): Promise<Cliente[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('*')
        .is('deleted_at', null)
        .order('razon_social', { ascending: true });
      if (error) return [];
      return (data ?? []).map(rowToCliente);
    } catch {
      return [];
    }
  },

  async getById(id: string): Promise<Cliente | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle();
    if (error || !data) return null;
    return rowToCliente(data as Record<string, unknown>);
  },

  async create(c: Omit<Cliente, 'id'>): Promise<Cliente | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('clientes')
      .insert({
        razon_social: c.razonSocial,
        cuit: c.cuit ?? null,
        contacto: c.contacto ?? null,
        email: c.email ?? null,
        telefono: c.telefono ?? null,
        direccion: c.direccion ?? null,
        observaciones: c.observaciones ?? null,
        created_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) return null;
    await logAudit({ modulo: 'clientes', accion: 'crear', registroId: data.id, registroTipo: 'cliente', valorNuevo: c.razonSocial });
    return rowToCliente(data as Record<string, unknown>);
  },

  async update(id: string, updates: Partial<Cliente>): Promise<Cliente | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const dbUpdates: Record<string, unknown> = { updated_by: user?.id ?? null };
    if (updates.razonSocial !== undefined) dbUpdates.razon_social = updates.razonSocial;
    if (updates.cuit !== undefined) dbUpdates.cuit = updates.cuit;
    if (updates.contacto !== undefined) dbUpdates.contacto = updates.contacto;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.telefono !== undefined) dbUpdates.telefono = updates.telefono;
    if (updates.direccion !== undefined) dbUpdates.direccion = updates.direccion;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones;
    const { data, error } = await supabase.from('clientes').update(dbUpdates).eq('id', id).select().single();
    if (error) return null;
    return rowToCliente(data as Record<string, unknown>);
  },

  async softDelete(id: string, motivo: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('clientes')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user?.id ?? null, delete_reason: motivo })
      .eq('id', id);
    return !error;
  },

  async getCount(): Promise<number> {
    const supabase = createClient();
    try {
      const { count } = await supabase.from('clientes').select('*', { count: 'exact', head: true }).is('deleted_at', null);
      return count ?? 0;
    } catch { return 0; }
  },
};

// ============================================================
// FUZZY CLIENT NAME MATCHING
// ============================================================

/**
 * Normalizes a client name for fuzzy comparison:
 * - Lowercases
 * - Removes accents/diacritics
 * - Trims and collapses whitespace
 * - Removes common legal suffixes (S.A., S.R.L., S.A.S., etc.)
 */
export function normalizeClientName(name: string): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .replace(/\b(s\.?a\.?s?\.?|s\.?r\.?l\.?|s\.?c\.?|s\.?h\.?|s\.?a\.?c\.?i\.?|ltda?\.?|inc\.?|corp\.?|cia\.?|co\.?)\b/g, '')
    .replace(/[.,\-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Returns true if two client names are considered the same entity.
 * Handles: different casing, accents, abbreviations, extra spaces.
 */
export function fuzzyMatchClientName(a: string, b: string): boolean {
  if (!a || !b) return false;
  const na = normalizeClientName(a);
  const nb = normalizeClientName(b);
  if (na === nb) return true;
  // One contains the other (handles abbreviations)
  if (na.length >= 4 && nb.includes(na)) return true;
  if (nb.length >= 4 && na.includes(nb)) return true;
  // Word-level overlap: if ≥70% of words from the shorter name appear in the longer
  const wordsA = na.split(' ').filter(w => w.length > 2);
  const wordsB = nb.split(' ').filter(w => w.length > 2);
  if (wordsA.length === 0 || wordsB.length === 0) return false;
  const shorter = wordsA.length <= wordsB.length ? wordsA : wordsB;
  const longer = wordsA.length <= wordsB.length ? wordsB : wordsA;
  const matches = shorter.filter(w => longer.some(lw => lw.startsWith(w) || w.startsWith(lw)));
  return matches.length / shorter.length >= 0.7;
}

/**
 * From a list of raw DB client name strings, returns all that fuzzy-match the given name.
 */
export function findMatchingClientNames(targetName: string, allNames: string[]): string[] {
  return allNames.filter(n => fuzzyMatchClientName(targetName, n));
}

// ============================================================
// EXTENDED SERVICES WITH FUZZY MATCHING
// ============================================================

export const clienteDataService = {
  /**
   * Fetches all facturas for a client using fuzzy name matching.
   * Loads all facturas and filters client-side to catch name variations.
   */
  async getFacturasFuzzy(clienteNombre: string, clienteId?: string): Promise<Invoice[]> {
    const supabase = createClient();
    // First try by clienteId if available
    let rows: Record<string, unknown>[] = [];
    if (clienteId) {
      const { data } = await supabase
        .from('facturas')
        .select('*')
        .eq('cliente_id', clienteId)
        .is('deleted_at', null)
        .order('fecha', { ascending: false });
      rows = (data ?? []) as Record<string, unknown>[];
    }
    // Also fetch by name (exact + fuzzy)
    const { data: allData } = await supabase
      .from('facturas')
      .select('*')
      .is('deleted_at', null)
      .order('fecha', { ascending: false });
    const all = (allData ?? []) as Record<string, unknown>[];
    const byName = all.filter(r => fuzzyMatchClientName(clienteNombre, (r.cliente_nombre as string) ?? ''));
    // Merge, deduplicate by id
    const merged = [...rows, ...byName];
    const seen = new Set<string>();
    const unique = merged.filter(r => {
      const id = r.id as string;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return unique.map(r => rowToInvoice(r));
  },

  /**
   * Fetches all cobranzas for a client using fuzzy name matching.
   */
  async getCobranzasFuzzy(clienteNombre: string, clienteId?: string): Promise<Cobranza[]> {
    const supabase = createClient();
    let rows: Record<string, unknown>[] = [];
    if (clienteId) {
      const { data } = await supabase
        .from('cobranzas')
        .select('*')
        .eq('cliente_id', clienteId)
        .is('deleted_at', null)
        .order('fecha_cobro', { ascending: false });
      rows = (data ?? []) as Record<string, unknown>[];
    }
    const { data: allData } = await supabase
      .from('cobranzas')
      .select('*')
      .is('deleted_at', null)
      .order('fecha_cobro', { ascending: false });
    const all = (allData ?? []) as Record<string, unknown>[];
    const byName = all.filter(r => fuzzyMatchClientName(clienteNombre, (r.cliente_nombre as string) ?? ''));
    const merged = [...rows, ...byName];
    const seen = new Set<string>();
    const unique = merged.filter(r => {
      const id = r.id as string;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return unique.map(r => rowToCobranza(r));
  },

  /**
   * Fetches all operaciones for a client using fuzzy name matching.
   */
  async getOperacionesFuzzy(clienteNombre: string, clienteId?: string): Promise<Operation[]> {
    const supabase = createClient();
    let rows: Record<string, unknown>[] = [];
    if (clienteId) {
      const { data } = await supabase
        .from('operaciones')
        .select('*')
        .eq('cliente_id', clienteId)
        .is('deleted_at', null)
        .order('fecha_apertura', { ascending: false });
      rows = (data ?? []) as Record<string, unknown>[];
    }
    const { data: allData } = await supabase
      .from('operaciones')
      .select('*')
      .is('deleted_at', null)
      .order('fecha_apertura', { ascending: false });
    const all = (allData ?? []) as Record<string, unknown>[];
    const byName = all.filter(r => fuzzyMatchClientName(clienteNombre, (r.cliente_nombre as string) ?? ''));
    const merged = [...rows, ...byName];
    const seen = new Set<string>();
    const unique = merged.filter(r => {
      const id = r.id as string;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return unique.map(r => rowToOperation(r));
  },
};

// ============================================================
// CUENTAS CAJA
// ============================================================

export const cuentasCajaService = {
  async getAll(): Promise<CuentaCaja[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('cuentas_caja')
        .select('*')
        .eq('activa', true)
        .order('nombre', { ascending: true });
      if (error) return [];
      return (data ?? []).map(rowToCuentaCaja);
    } catch {
      return [];
    }
  },
};

// ============================================================
// OPERACION HISTORIAL
// ============================================================

function rowToHistorial(row: Record<string, unknown>): OperacionHistorial {
  return {
    id: row.id as string,
    operacionId: row.operacion_id as string,
    usuarioId: row.usuario_id as string | undefined,
    usuarioNombre: row.usuario_nombre as string | undefined,
    accion: (row.accion as string) ?? '',
    campoModificado: row.campo_modificado as string | undefined,
    valorAnterior: row.valor_anterior as string | undefined,
    valorNuevo: row.valor_nuevo as string | undefined,
    observacion: row.observacion as string | undefined,
    createdAt: (row.created_at as string) ?? '',
  };
}

export const historialService = {
  async getByOperacion(operacionId: string): Promise<OperacionHistorial[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('operacion_historial')
        .select('*')
        .eq('operacion_id', operacionId)
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data ?? []).map(rowToHistorial);
    } catch {
      return [];
    }
  },

  async log(operacionId: string, accion: string, campo?: string, anterior?: string, nuevo?: string, observacion?: string): Promise<void> {
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('operacion_historial').insert({
        operacion_id: operacionId,
        usuario_id: user?.id ?? null,
        usuario_nombre: user?.email ?? 'Sistema',
        accion,
        campo_modificado: campo ?? null,
        valor_anterior: anterior ?? null,
        valor_nuevo: nuevo ?? null,
        observacion: observacion ?? null,
      });
    } catch {
      // historial failures don't block main operations
    }
  },
};

// ============================================================
// PAGOS MENSUALES
// ============================================================

export interface PagoMensual {
  id: string;
  anio: number;
  mes: number;
  concepto: string;
  categoria: string;
  importe: number | null;
  importeEspecial: string | null; // 'a-favor' | 'no-se-paga' | null
  vencimiento: string | null;
  formaPago: string | null;
  fechaPago: string | null;
  cuentaPago: string | null;
  estado: 'pendiente' | 'pagado' | 'vencido' | 'no-se-paga' | 'a-favor' | 'anulado';
  observaciones: string | null;
  movimientoCajaId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

function rowToPagoMensual(row: Record<string, unknown>): PagoMensual {
  return {
    id: row.id as string,
    anio: Number(row.anio),
    mes: Number(row.mes),
    concepto: (row.concepto as string) ?? '',
    categoria: (row.categoria as string) ?? 'Otros',
    importe: row.importe !== null ? Number(row.importe) : null,
    importeEspecial: row.importe_especial as string | null,
    vencimiento: row.vencimiento as string | null,
    formaPago: row.forma_pago as string | null,
    fechaPago: row.fecha_pago as string | null,
    cuentaPago: row.cuenta_pago as string | null,
    estado: (row.estado as PagoMensual['estado']) ?? 'pendiente',
    observaciones: row.observaciones as string | null,
    movimientoCajaId: row.movimiento_caja_id as string | null,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

export const pagosMensualesService = {
  async getByMes(anio: number, mes: number): Promise<PagoMensual[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('pagos_mensuales')
        .select('*')
        .eq('anio', anio)
        .eq('mes', mes)
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data ?? []).map(rowToPagoMensual);
    } catch {
      return [];
    }
  },

  async create(pago: Omit<PagoMensual, 'id' | 'createdAt' | 'updatedAt'>): Promise<PagoMensual> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('pagos_mensuales')
      .insert({
        anio: pago.anio,
        mes: pago.mes,
        concepto: pago.concepto,
        categoria: pago.categoria,
        importe: pago.importe ?? 0,
        importe_especial: pago.importeEspecial ?? null,
        vencimiento: pago.vencimiento ?? null,
        forma_pago: pago.formaPago ?? null,
        fecha_pago: pago.fechaPago ?? null,
        cuenta_pago: pago.cuentaPago ?? null,
        estado: pago.estado,
        observaciones: pago.observaciones ?? null,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return rowToPagoMensual(data as Record<string, unknown>);
  },

  async update(id: string, updates: Partial<PagoMensual>): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const dbUpdates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
      updated_by: user?.id ?? null,
    };
    if (updates.concepto !== undefined) dbUpdates.concepto = updates.concepto;
    if (updates.categoria !== undefined) dbUpdates.categoria = updates.categoria;
    if (updates.importe !== undefined) dbUpdates.importe = updates.importe;
    if (updates.importeEspecial !== undefined) dbUpdates.importe_especial = updates.importeEspecial;
    if (updates.vencimiento !== undefined) dbUpdates.vencimiento = updates.vencimiento;
    if (updates.formaPago !== undefined) dbUpdates.forma_pago = updates.formaPago;
    if (updates.fechaPago !== undefined) dbUpdates.fecha_pago = updates.fechaPago;
    if (updates.cuentaPago !== undefined) dbUpdates.cuenta_pago = updates.cuentaPago;
    if (updates.estado !== undefined) dbUpdates.estado = updates.estado;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones;
    if (updates.movimientoCajaId !== undefined) dbUpdates.movimiento_caja_id = updates.movimientoCajaId;
    const { error } = await supabase.from('pagos_mensuales').update(dbUpdates).eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('pagos_mensuales').delete().eq('id', id);
    if (error) throw error;
  },
};

// ============================================================
// ÓRDENES DE PAGO V2
// ============================================================

export interface OrdenPagoV2Item {
  id?: string;
  ordenId?: string;
  costoPrProveedorId?: string | null;
  guiaBl?: string | null;
  clienteNombre?: string | null;
  nCarpeta?: string | null;
  nFacturaProveedor?: string | null;
  fechaFacturaProveedor?: string | null;
  moneda?: string;
  montoMoneda?: number;
  tcEmision?: number;
  costoEstimadoPesos?: number;
  fechaFacturaCliente?: string | null;
  nFacturaCliente?: string | null;
  montoFacturadoCliente?: number;
  tcPago?: number;
  costoRealPesos?: number;
  nReciboProveedor?: string | null;
  fechaReciboProveedor?: string | null;
  profit?: number;
}

export interface OrdenPagoV2 {
  id: string;
  nOrden: string;
  tipo: string;
  estado: 'borrador' | 'pendiente-aprobacion' | 'aprobada' | 'ejecutada' | 'anulada';
  fechaCreacion: string;
  fechaPagoEstimada?: string | null;
  fechaPagoReal?: string | null;
  monto: number;
  moneda?: string;
  cuentaSalida?: string | null;
  medioPago?: string | null;
  observaciones?: string | null;
  proveedorId?: string | null;
  proveedorNombre?: string | null;
  persona?: string | null;
  motivo?: string | null;
  categoria?: string | null;
  beneficiario?: string | null;
  subcategoria?: string | null;
  concepto?: string | null;
  movimientoCajaId?: string | null;
  items?: OrdenPagoV2Item[];
  createdAt?: string;
  updatedAt?: string;
}

function rowToOrdenPagoV2(row: Record<string, unknown>, items?: OrdenPagoV2Item[]): OrdenPagoV2 {
  return {
    id: row.id as string,
    nOrden: (row.n_orden as string) ?? '',
    tipo: (row.tipo as string) ?? 'otros-gastos',
    estado: (row.estado as OrdenPagoV2['estado']) ?? 'borrador',
    fechaCreacion: (row.fecha_creacion as string) ?? '',
    fechaPagoEstimada: row.fecha_pago_estimada as string | null,
    fechaPagoReal: row.fecha_pago_real as string | null,
    monto: Number(row.monto ?? 0),
    moneda: (row.moneda as string) ?? 'ARS',
    cuentaSalida: row.cuenta_salida as string | null,
    medioPago: row.medio_pago as string | null,
    observaciones: row.observaciones as string | null,
    proveedorId: row.proveedor_id as string | null,
    proveedorNombre: row.proveedor_nombre as string | null,
    persona: row.persona as string | null,
    motivo: row.motivo as string | null,
    categoria: row.categoria as string | null,
    beneficiario: row.beneficiario as string | null,
    subcategoria: row.subcategoria as string | null,
    concepto: row.concepto as string | null,
    movimientoCajaId: row.movimiento_caja_id as string | null,
    items: items ?? [],
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

function rowToOrdenItem(row: Record<string, unknown>): OrdenPagoV2Item {
  return {
    id: row.id as string,
    ordenId: row.orden_id as string,
    guiaBl: row.guia_bl as string | null,
    clienteNombre: row.cliente_nombre as string | null,
    nCarpeta: row.n_carpeta as string | null,
    nFacturaProveedor: row.n_factura_proveedor as string | null,
    fechaFacturaProveedor: row.fecha_factura_proveedor as string | null,
    moneda: (row.moneda as string) ?? 'USD',
    montoMoneda: Number(row.monto_moneda ?? 0),
    tcEmision: Number(row.tc_emision ?? 1),
    costoEstimadoPesos: Number(row.costo_estimado_pesos ?? 0),
    fechaFacturaCliente: row.fecha_factura_cliente as string | null,
    nFacturaCliente: row.n_factura_cliente as string | null,
    montoFacturadoCliente: Number(row.monto_facturado_cliente ?? 0),
    tcPago: Number(row.tc_pago ?? 1),
    costoRealPesos: Number(row.costo_real_pesos ?? 0),
    nReciboProveedor: row.n_recibo_proveedor as string | null,
    fechaReciboProveedor: row.fecha_recibo_proveedor as string | null,
    profit: Number(row.profit ?? 0),
  };
}

let _ordenCounter = 1;

export const ordenesPagoV2Service = {
  async getAll(): Promise<OrdenPagoV2[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('ordenes_pago_v2')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) return [];
      const ordenes = (data ?? []).map(r => rowToOrdenPagoV2(r as Record<string, unknown>));
      // Load items for proveedor-operativo
      const provOrdenes = ordenes.filter(o => o.tipo === 'proveedor-operativo');
      if (provOrdenes.length > 0) {
        const ids = provOrdenes.map(o => o.id);
        const { data: itemsData } = await supabase
          .from('ordenes_pago_v2_items')
          .select('*')
          .in('orden_id', ids);
        const itemsByOrden: Record<string, OrdenPagoV2Item[]> = {};
        for (const it of (itemsData ?? [])) {
          const item = rowToOrdenItem(it as Record<string, unknown>);
          if (!itemsByOrden[item.ordenId!]) itemsByOrden[item.ordenId!] = [];
          itemsByOrden[item.ordenId!].push(item);
        }
        return ordenes.map(o => ({ ...o, items: itemsByOrden[o.id] ?? [] }));
      }
      return ordenes;
    } catch {
      return [];
    }
  },

  async create(
    orden: Omit<OrdenPagoV2, 'id' | 'nOrden' | 'createdAt' | 'updatedAt'>,
    items: Partial<OrdenPagoV2Item>[] = []
  ): Promise<OrdenPagoV2> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const nOrden = `OP-${new Date().getFullYear()}-${String(_ordenCounter++).padStart(4, '0')}`;
    const { data, error } = await supabase
      .from('ordenes_pago_v2')
      .insert({
        n_orden: nOrden,
        tipo: orden.tipo,
        estado: orden.estado ?? 'borrador',
        fecha_creacion: orden.fechaCreacion,
        fecha_pago_estimada: orden.fechaPagoEstimada ?? null,
        monto: orden.monto ?? 0,
        moneda: orden.moneda ?? 'ARS',
        cuenta_salida: orden.cuentaSalida ?? null,
        medio_pago: orden.medioPago ?? null,
        observaciones: orden.observaciones ?? null,
        proveedor_id: orden.proveedorId ?? null,
        proveedor_nombre: orden.proveedorNombre ?? null,
        persona: orden.persona ?? null,
        motivo: orden.motivo ?? null,
        categoria: orden.categoria ?? null,
        beneficiario: orden.beneficiario ?? null,
        subcategoria: orden.subcategoria ?? null,
        concepto: orden.concepto ?? null,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    const created = rowToOrdenPagoV2(data as Record<string, unknown>);
    if (items.length > 0) {
      await supabase.from('ordenes_pago_v2_items').insert(
        items.map(it => ({
          orden_id: created.id,
          guia_bl: it.guiaBl ?? null,
          cliente_nombre: it.clienteNombre ?? null,
          n_carpeta: it.nCarpeta ?? null,
          n_factura_proveedor: it.nFacturaProveedor ?? null,
          fecha_factura_proveedor: it.fechaFacturaProveedor ?? null,
          moneda: it.moneda ?? 'USD',
          monto_moneda: it.montoMoneda ?? 0,
          tc_emision: it.tcEmision ?? 1,
          costo_estimado_pesos: it.costoEstimadoPesos ?? 0,
          fecha_factura_cliente: it.fechaFacturaCliente ?? null,
          n_factura_cliente: it.nFacturaCliente ?? null,
          monto_facturado_cliente: it.montoFacturadoCliente ?? 0,
          tc_pago: it.tcPago ?? 1,
          costo_real_pesos: it.costoRealPesos ?? 0,
          n_recibo_proveedor: it.nReciboProveedor ?? null,
          fecha_recibo_proveedor: it.fechaReciboProveedor ?? null,
        }))
      );
    }
    return created;
  },

  async update(
    id: string,
    updates: Partial<OrdenPagoV2>,
    items?: Partial<OrdenPagoV2Item>[]
  ): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const dbUpdates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
      updated_by: user?.id ?? null,
    };
    if (updates.tipo !== undefined) dbUpdates.tipo = updates.tipo;
    if (updates.estado !== undefined) dbUpdates.estado = updates.estado;
    if (updates.fechaCreacion !== undefined) dbUpdates.fecha_creacion = updates.fechaCreacion;
    if (updates.fechaPagoEstimada !== undefined) dbUpdates.fecha_pago_estimada = updates.fechaPagoEstimada;
    if (updates.monto !== undefined) dbUpdates.monto = updates.monto;
    if (updates.moneda !== undefined) dbUpdates.moneda = updates.moneda;
    if (updates.cuentaSalida !== undefined) dbUpdates.cuenta_salida = updates.cuentaSalida;
    if (updates.medioPago !== undefined) dbUpdates.medio_pago = updates.medioPago;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones;
    if (updates.proveedorId !== undefined) dbUpdates.proveedor_id = updates.proveedorId;
    if (updates.proveedorNombre !== undefined) dbUpdates.proveedor_nombre = updates.proveedorNombre;
    if (updates.persona !== undefined) dbUpdates.persona = updates.persona;
    if (updates.motivo !== undefined) dbUpdates.motivo = updates.motivo;
    if (updates.categoria !== undefined) dbUpdates.categoria = updates.categoria;
    if (updates.beneficiario !== undefined) dbUpdates.beneficiario = updates.beneficiario;
    if (updates.subcategoria !== undefined) dbUpdates.subcategoria = updates.subcategoria;
    if (updates.concepto !== undefined) dbUpdates.concepto = updates.concepto;
    const { error } = await supabase.from('ordenes_pago_v2').update(dbUpdates).eq('id', id);
    if (error) throw error;
    if (items !== undefined) {
      await supabase.from('ordenes_pago_v2_items').delete().eq('orden_id', id);
      if (items.length > 0) {
        await supabase.from('ordenes_pago_v2_items').insert(
          items.map(it => ({
            orden_id: id,
            guia_bl: it.guiaBl ?? null,
            cliente_nombre: it.clienteNombre ?? null,
            n_carpeta: it.nCarpeta ?? null,
            n_factura_proveedor: it.nFacturaProveedor ?? null,
            fecha_factura_proveedor: it.fechaFacturaProveedor ?? null,
            moneda: it.moneda ?? 'USD',
            monto_moneda: it.montoMoneda ?? 0,
            tc_emision: it.tcEmision ?? 1,
            costo_estimado_pesos: it.costoEstimadoPesos ?? 0,
            fecha_factura_cliente: it.fechaFacturaCliente ?? null,
            n_factura_cliente: it.nFacturaCliente ?? null,
            monto_facturado_cliente: it.montoFacturadoCliente ?? 0,
            tc_pago: it.tcPago ?? 1,
            costo_real_pesos: it.costoRealPesos ?? 0,
            n_recibo_proveedor: it.nReciboProveedor ?? null,
            fecha_recibo_proveedor: it.fechaReciboProveedor ?? null,
          }))
        );
      }
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('ordenes_pago_v2_items').delete().eq('orden_id', id);
    const { error } = await supabase.from('ordenes_pago_v2').delete().eq('id', id);
    if (error) throw error;
  },
};

// ============================================================
// MOVIMIENTOS CAJA
// ============================================================

export interface MovimientoCaja {
  id: string;
  cuentaId?: string | null;
  cuentaNombre?: string | null;
  tipo: 'ingreso' | 'egreso';
  categoria?: string | null;
  concepto: string;
  monto: number;
  moneda?: string;
  fecha: string;
  medioPago?: string | null;
  referenciaTipo?: string | null;
  referenciaId?: string | null;
  esImpuestoBancario?: boolean;
  impuestoBancarioTipo?: string | null;
  impuestoBancarioAlicuota?: number;
  observaciones?: string | null;
  anulado?: boolean;
  anuladoAt?: string | null;
  anuladoMotivo?: string | null;
  createdAt?: string;
}

export interface MovimientoCajaInput {
  tipo: 'ingreso' | 'egreso';
  categoria?: string;
  concepto: string;
  monto: number;
  fecha: string;
  medioPago?: string;
  cuentaNombre?: string;
  referenciaTipo?: string;
  referenciaId?: string;
  observaciones?: string;
}

export const movimientosCajaService = {
  async create(input: MovimientoCajaInput): Promise<MovimientoCaja> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    // Check if bank tax should be applied
    let taxMonto = 0;
    let taxTipo: string | null = null;
    try {
      const { data: configs } = await supabase
        .from('configuracion_sistema')
        .select('clave, valor')
        .in('clave', [
          'impuesto_bancario_activo',
          'impuesto_bancario_alicuota_credito',
          'impuesto_bancario_alicuota_debito',
          'impuesto_bancario_medios',
        ]);
      const cfgMap: Record<string, string> = {};
      for (const c of (configs ?? [])) cfgMap[c.clave] = c.valor;

      const activo = cfgMap['impuesto_bancario_activo'] === 'true';
      const mediosAlcanzados = (cfgMap['impuesto_bancario_medios'] ?? '').split(',').map(m => m.trim().toLowerCase());
      const medioActual = (input.medioPago ?? '').toLowerCase();
      const medioAlcanzado = mediosAlcanzados.some(m => medioActual.includes(m) || m.includes(medioActual));

      if (activo && medioAlcanzado && input.monto > 0) {
        if (input.tipo === 'ingreso') {
          const alicuota = parseFloat(cfgMap['impuesto_bancario_alicuota_credito'] ?? '0.006');
          taxMonto = Math.round(input.monto * alicuota);
          taxTipo = 'credito';
        } else {
          // For egreso by transfer: tax is added ON TOP of the payment amount
          const alicuota = parseFloat(cfgMap['impuesto_bancario_alicuota_debito'] ?? '0.006');
          taxMonto = Math.round(input.monto * alicuota);
          taxTipo = 'debito';
        }
      }
    } catch {
      // Tax calculation failure should not block main movement
    }

    const { data, error } = await supabase
      .from('movimientos_caja')
      .insert({
        cuenta_nombre: input.cuentaNombre ?? null,
        tipo: input.tipo,
        categoria: input.categoria ?? null,
        concepto: input.concepto,
        monto: input.monto,
        moneda: 'ARS',
        fecha: input.fecha,
        medio_pago: input.medioPago ?? null,
        referencia_tipo: input.referenciaTipo ?? null,
        referencia_id: input.referenciaId ?? null,
        observaciones: input.observaciones ?? null,
        created_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) throw error;

    // Create bank tax movement if applicable
    if (taxMonto > 0 && taxTipo) {
      await supabase.from('movimientos_caja').insert({
        cuenta_nombre: input.cuentaNombre ?? null,
        tipo: 'egreso',
        categoria: 'Impuesto bancario',
        concepto: `Imp. ${taxTipo === 'credito' ? 'crédito' : 'débito'} bancario 0,6% — ${input.concepto}`,
        monto: taxMonto,
        moneda: 'ARS',
        fecha: input.fecha,
        medio_pago: input.medioPago ?? null,
        referencia_tipo: input.referenciaTipo ?? null,
        referencia_id: input.referenciaId ?? null,
        es_impuesto_bancario: true,
        impuesto_bancario_tipo: taxTipo,
        impuesto_bancario_alicuota: taxTipo === 'credito' ? 0.006 : 0.006,
        created_by: user?.id ?? null,
      });
    }

    return {
      id: (data as Record<string, unknown>).id as string,
      tipo: input.tipo,
      concepto: input.concepto,
      monto: input.monto,
      fecha: input.fecha,
    };
  },

  async getAll(): Promise<MovimientoCaja[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('movimientos_caja')
        .select('*')
        .eq('anulado', false)
        .order('fecha', { ascending: false });
      if (error) return [];
      return (data ?? []).map(r => ({
        id: (r as Record<string, unknown>).id as string,
        cuentaId: (r as Record<string, unknown>).cuenta_id as string | null,
        cuentaNombre: (r as Record<string, unknown>).cuenta_nombre as string | null,
        tipo: (r as Record<string, unknown>).tipo as 'ingreso' | 'egreso',
        categoria: (r as Record<string, unknown>).categoria as string | null,
        concepto: (r as Record<string, unknown>).concepto as string,
        monto: Number((r as Record<string, unknown>).monto ?? 0),
        moneda: (r as Record<string, unknown>).moneda as string,
        fecha: (r as Record<string, unknown>).fecha as string,
        medioPago: (r as Record<string, unknown>).medio_pago as string | null,
        referenciaTipo: (r as Record<string, unknown>).referencia_tipo as string | null,
        referenciaId: (r as Record<string, unknown>).referencia_id as string | null,
        esImpuestoBancario: Boolean((r as Record<string, unknown>).es_impuesto_bancario),
        impuestoBancarioTipo: (r as Record<string, unknown>).impuesto_bancario_tipo as string | null,
        observaciones: (r as Record<string, unknown>).observaciones as string | null,
        anulado: Boolean((r as Record<string, unknown>).anulado),
        createdAt: (r as Record<string, unknown>).created_at as string,
      }));
    } catch {
      return [];
    }
  },

  async anularPorReferencia(referenciaTipo: string, referenciaId: string, motivo: string): Promise<void> {
    const supabase = createClient();
    await supabase
      .from('movimientos_caja')
      .update({
        anulado: true,
        anulado_at: new Date().toISOString(),
        anulado_motivo: motivo,
      })
      .eq('referencia_tipo', referenciaTipo)
      .eq('referencia_id', referenciaId);
  },
};

// ============================================================
// CONFIGURACIÓN SISTEMA
// ============================================================

export interface ConfiguracionItem {
  clave: string;
  valor: string;
  descripcion: string;
}

// ============================================================
// PERMISOS DE USUARIO
// ============================================================

export interface UserPermisos {
  id?: string;
  userId: string;
  userEmail?: string;
  // Operaciones
  verOperaciones: boolean;
  crearOperaciones: boolean;
  editarOperaciones: boolean;
  // Clientes
  verClientes: boolean;
  crearClientes: boolean;
  editarClientes: boolean;
  // Facturación
  verFacturacion: boolean;
  crearFacturas: boolean;
  editarFacturas: boolean;
  // Cobranzas
  verCobranzas: boolean;
  registrarCobros: boolean;
  // Proveedores
  verProveedores: boolean;
  cargarFacturasProveedor: boolean;
  editarProveedores: boolean;
  // Órdenes de pago
  verOrdenesPago: boolean;
  crearOrdenesPago: boolean;
  aprobarOrdenesPago: boolean;
  ejecutarOrdenesPago: boolean;
  // Caja/Bancos
  verCaja: boolean;
  // E-cheqs
  verEcheqs: boolean;
  // Pagos mensuales
  verPagosMensuales: boolean;
  editarPagosMensuales: boolean;
  // Métricas
  verMetricas: boolean;
  // Auditoría
  verAuditoria: boolean;
  // Admin
  administrarUsuarios: boolean;
}

export const SUPER_ADMIN_EMAIL = 'franco';

export function isSuperAdmin(email?: string | null): boolean {
  if (!email) return false;
  return email.toLowerCase().includes('franco');
}

function rowToPermisos(row: Record<string, unknown>): UserPermisos {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    userEmail: row.user_email as string | undefined,
    verOperaciones: Boolean(row.ver_operaciones ?? true),
    crearOperaciones: Boolean(row.crear_operaciones ?? true),
    editarOperaciones: Boolean(row.editar_operaciones ?? true),
    verClientes: Boolean(row.ver_clientes ?? true),
    crearClientes: Boolean(row.crear_clientes ?? true),
    editarClientes: Boolean(row.editar_clientes ?? true),
    verFacturacion: Boolean(row.ver_facturacion ?? true),
    crearFacturas: Boolean(row.crear_facturas ?? true),
    editarFacturas: Boolean(row.editar_facturas ?? true),
    verCobranzas: Boolean(row.ver_cobranzas ?? true),
    registrarCobros: Boolean(row.registrar_cobros ?? true),
    verProveedores: Boolean(row.ver_proveedores ?? true),
    cargarFacturasProveedor: Boolean(row.cargar_facturas_proveedor ?? true),
    editarProveedores: Boolean(row.editar_proveedores ?? true),
    verOrdenesPago: Boolean(row.ver_ordenes_pago ?? true),
    crearOrdenesPago: Boolean(row.crear_ordenes_pago ?? true),
    aprobarOrdenesPago: Boolean(row.aprobar_ordenes_pago ?? false),
    ejecutarOrdenesPago: Boolean(row.ejecutar_ordenes_pago ?? false),
    verCaja: Boolean(row.ver_caja ?? true),
    verEcheqs: Boolean(row.ver_echeqs ?? true),
    verPagosMensuales: Boolean(row.ver_pagos_mensuales ?? true),
    editarPagosMensuales: Boolean(row.editar_pagos_mensuales ?? true),
    verMetricas: Boolean(row.ver_metricas ?? true),
    verAuditoria: Boolean(row.ver_auditoria ?? false),
    administrarUsuarios: Boolean(row.administrar_usuarios ?? false),
  };
}

export const permisosService = {
  async getByUserId(userId: string): Promise<UserPermisos | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_permisos')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (error || !data) return null;
      return rowToPermisos(data as Record<string, unknown>);
    } catch {
      return null;
    }
  },

  async getAllUsers(): Promise<Array<{ id: string; email: string; permisos: UserPermisos | null }>> {
    const supabase = createClient();
    try {
      const { data: permsData } = await supabase
        .from('user_permisos')
        .select('*')
        .order('user_email');
      return (permsData ?? []).map((r: Record<string, unknown>) => ({
        id: r.user_id as string,
        email: (r.user_email as string) ?? '',
        permisos: rowToPermisos(r),
      }));
    } catch {
      return [];
    }
  },

  async upsert(permisos: UserPermisos): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_permisos')
        .upsert({
          user_id: permisos.userId,
          user_email: permisos.userEmail ?? null,
          ver_operaciones: permisos.verOperaciones,
          crear_operaciones: permisos.crearOperaciones,
          editar_operaciones: permisos.editarOperaciones,
          ver_clientes: permisos.verClientes,
          crear_clientes: permisos.crearClientes,
          editar_clientes: permisos.editarClientes,
          ver_facturacion: permisos.verFacturacion,
          crear_facturas: permisos.crearFacturas,
          editar_facturas: permisos.editarFacturas,
          ver_cobranzas: permisos.verCobranzas,
          registrar_cobros: permisos.registrarCobros,
          ver_proveedores: permisos.verProveedores,
          cargar_facturas_proveedor: permisos.cargarFacturasProveedor,
          editar_proveedores: permisos.editarProveedores,
          ver_ordenes_pago: permisos.verOrdenesPago,
          crear_ordenes_pago: permisos.crearOrdenesPago,
          aprobar_ordenes_pago: permisos.aprobarOrdenesPago,
          ejecutar_ordenes_pago: permisos.ejecutarOrdenesPago,
          ver_caja: permisos.verCaja,
          ver_echeqs: permisos.verEcheqs,
          ver_pagos_mensuales: permisos.verPagosMensuales,
          editar_pagos_mensuales: permisos.editarPagosMensuales,
          ver_metricas: permisos.verMetricas,
          ver_auditoria: permisos.verAuditoria,
          administrar_usuarios: permisos.administrarUsuarios,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      return !error;
    } catch {
      return false;
    }
  },
};

// ============================================================
// PROVEEDOR LINEAS (Cuenta Corriente Proveedor)
// ============================================================

export interface ProveedorLinea {
  id: string;
  proveedorId: string;
  proveedorNombre?: string;
  guiaBl?: string;
  carpeta?: string;
  clienteNombre?: string;
  clienteId?: string;
  nFacturaProveedor?: string;
  fechaFacturaProveedor?: string;
  moneda: string;
  montoMoneda: number;
  tcEmision: number;
  costoEstimadoPesos: number;
  facturaClienteId?: string;
  fechaFacturaCliente?: string;
  puntoVentaCliente?: string;
  tipoFacturaCliente?: string;
  nFacturaCliente?: string;
  montoFacturadoCliente: number;
  fechaPagoProveedor?: string;
  tcPago: number;
  costoRealPesos: number;
  nReciboProveedor?: string;
  fechaReciboProveedor?: string;
  profitEstimado: number;
  profitReal: number;
  estado: string;
  observaciones?: string;
  anuladaMotivo?: string;
  createdAt?: string;
  updatedAt?: string;
}

function rowToProveedorLinea(row: Record<string, unknown>): ProveedorLinea {
  return {
    id: row.id as string,
    proveedorId: row.proveedor_id as string,
    proveedorNombre: row.proveedor_nombre as string | undefined,
    guiaBl: row.guia_bl as string | undefined,
    carpeta: row.carpeta as string | undefined,
    clienteNombre: row.cliente_nombre as string | undefined,
    clienteId: row.cliente_id as string | undefined,
    nFacturaProveedor: row.n_factura_proveedor as string | undefined,
    fechaFacturaProveedor: row.fecha_factura_proveedor as string | undefined,
    moneda: (row.moneda as string) ?? 'ARS',
    montoMoneda: Number(row.monto_moneda ?? 0),
    tcEmision: Number(row.tc_emision ?? 1),
    costoEstimadoPesos: Number(row.costo_estimado_pesos ?? 0),
    facturaClienteId: row.factura_cliente_id as string | undefined,
    fechaFacturaCliente: row.fecha_factura_cliente as string | undefined,
    puntoVentaCliente: row.punto_venta_cliente as string | undefined,
    tipoFacturaCliente: row.tipo_factura_cliente as string | undefined,
    nFacturaCliente: row.n_factura_cliente as string | undefined,
    montoFacturadoCliente: Number(row.monto_facturado_cliente ?? 0),
    fechaPagoProveedor: row.fecha_pago_proveedor as string | undefined,
    tcPago: Number(row.tc_pago ?? 1),
    costoRealPesos: Number(row.costo_real_pesos ?? 0),
    nReciboProveedor: row.n_recibo_proveedor as string | undefined,
    fechaReciboProveedor: row.fecha_recibo_proveedor as string | undefined,
    profitEstimado: Number(row.profit_estimado ?? 0),
    profitReal: Number(row.profit_real ?? 0),
    estado: (row.estado as string) ?? 'pendiente-factura-cliente',
    observaciones: row.observaciones as string | undefined,
    anuladaMotivo: row.anulada_motivo as string | undefined,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

export function calcularEstadoLinea(linea: Partial<ProveedorLinea>): string {
  if (linea.estado === 'anulada') return 'anulada';
  if (!linea.nFacturaCliente && !linea.facturaClienteId) return 'pendiente-factura-cliente';
  if (!linea.costoRealPesos || linea.costoRealPesos === 0) return 'pendiente-costo-real';
  if (!linea.fechaPagoProveedor) return 'pendiente-pago-proveedor';
  if (!linea.nReciboProveedor) return 'pendiente-recibo-proveedor';
  if (linea.observaciones && linea.estado === 'observada') return 'observada';
  return 'completa';
}

export const proveedorLineasService = {
  async getByProveedor(proveedorId: string): Promise<ProveedorLinea[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('proveedor_lineas')
        .select('*')
        .eq('proveedor_id', proveedorId)
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data ?? []).map(rowToProveedorLinea);
    } catch {
      return [];
    }
  },

  async getAll(): Promise<ProveedorLinea[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('proveedor_lineas')
        .select('*, proveedores(nombre)')
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data ?? []).map((r: Record<string, unknown>) => {
        const linea = rowToProveedorLinea(r);
        const prov = r.proveedores as Record<string, unknown> | null;
        if (prov) linea.proveedorNombre = prov.nombre as string;
        return linea;
      });
    } catch {
      return [];
    }
  },

  async getByCarpeta(carpeta: string): Promise<ProveedorLinea[]> {
    if (!carpeta) return [];
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('proveedor_lineas')
        .select('*')
        .eq('carpeta', carpeta)
        .neq('estado', 'anulada');
      if (error) return [];
      return (data ?? []).map(rowToProveedorLinea);
    } catch {
      return [];
    }
  },

  async create(linea: Omit<ProveedorLinea, 'id' | 'profitEstimado' | 'profitReal' | 'createdAt' | 'updatedAt'>): Promise<ProveedorLinea | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const estado = calcularEstadoLinea(linea);
    const { data, error } = await supabase
      .from('proveedor_lineas')
      .insert({
        proveedor_id: linea.proveedorId,
        guia_bl: linea.guiaBl ?? null,
        carpeta: linea.carpeta ?? null,
        cliente_nombre: linea.clienteNombre ?? null,
        cliente_id: linea.clienteId ?? null,
        n_factura_proveedor: linea.nFacturaProveedor ?? null,
        fecha_factura_proveedor: linea.fechaFacturaProveedor ?? null,
        moneda: linea.moneda ?? 'ARS',
        monto_moneda: linea.montoMoneda ?? 0,
        tc_emision: linea.tcEmision ?? 1,
        costo_estimado_pesos: linea.costoEstimadoPesos ?? 0,
        factura_cliente_id: linea.facturaClienteId ?? null,
        fecha_factura_cliente: linea.fechaFacturaCliente ?? null,
        punto_venta_cliente: linea.puntoVentaCliente ?? null,
        tipo_factura_cliente: linea.tipoFacturaCliente ?? null,
        n_factura_cliente: linea.nFacturaCliente ?? null,
        monto_facturado_cliente: linea.montoFacturadoCliente ?? 0,
        fecha_pago_proveedor: linea.fechaPagoProveedor ?? null,
        tc_pago: linea.tcPago ?? 1,
        costo_real_pesos: linea.costoRealPesos ?? 0,
        n_recibo_proveedor: linea.nReciboProveedor ?? null,
        fecha_recibo_proveedor: linea.fechaReciboProveedor ?? null,
        estado,
        observaciones: linea.observaciones ?? null,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) {
      console.error('proveedorLineasService.create error:', error);
      return null;
    }
    await logAudit({
      modulo: 'proveedores',
      accion: 'crear-linea',
      registroId: (data as Record<string, unknown>).id as string,
      registroTipo: 'proveedor_linea',
      valorNuevo: `Carpeta: ${linea.carpeta ?? '—'} | Proveedor factura: ${linea.nFacturaProveedor ?? '—'}`,
    });
    return rowToProveedorLinea(data as Record<string, unknown>);
  },

  async update(id: string, updates: Partial<ProveedorLinea>): Promise<ProveedorLinea | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const dbUpdates: Record<string, unknown> = { updated_by: user?.id ?? null, updated_at: new Date().toISOString() };
    if (updates.guiaBl !== undefined) dbUpdates.guia_bl = updates.guiaBl ?? null;
    if (updates.carpeta !== undefined) dbUpdates.carpeta = updates.carpeta ?? null;
    if (updates.clienteNombre !== undefined) dbUpdates.cliente_nombre = updates.clienteNombre ?? null;
    if (updates.clienteId !== undefined) dbUpdates.cliente_id = updates.clienteId ?? null;
    if (updates.nFacturaProveedor !== undefined) dbUpdates.n_factura_proveedor = updates.nFacturaProveedor ?? null;
    if (updates.fechaFacturaProveedor !== undefined) dbUpdates.fecha_factura_proveedor = updates.fechaFacturaProveedor ?? null;
    if (updates.moneda !== undefined) dbUpdates.moneda = updates.moneda;
    if (updates.montoMoneda !== undefined) dbUpdates.monto_moneda = updates.montoMoneda;
    if (updates.tcEmision !== undefined) dbUpdates.tc_emision = updates.tcEmision;
    if (updates.costoEstimadoPesos !== undefined) dbUpdates.costo_estimado_pesos = updates.costoEstimadoPesos;
    if (updates.facturaClienteId !== undefined) dbUpdates.factura_cliente_id = updates.facturaClienteId ?? null;
    if (updates.fechaFacturaCliente !== undefined) dbUpdates.fecha_factura_cliente = updates.fechaFacturaCliente ?? null;
    if (updates.puntoVentaCliente !== undefined) dbUpdates.punto_venta_cliente = updates.puntoVentaCliente ?? null;
    if (updates.tipoFacturaCliente !== undefined) dbUpdates.tipo_factura_cliente = updates.tipoFacturaCliente ?? null;
    if (updates.nFacturaCliente !== undefined) dbUpdates.n_factura_cliente = updates.nFacturaCliente ?? null;
    if (updates.montoFacturadoCliente !== undefined) dbUpdates.monto_facturado_cliente = updates.montoFacturadoCliente;
    if (updates.fechaPagoProveedor !== undefined) dbUpdates.fecha_pago_proveedor = updates.fechaPagoProveedor ?? null;
    if (updates.tcPago !== undefined) dbUpdates.tc_pago = updates.tcPago;
    if (updates.costoRealPesos !== undefined) dbUpdates.costo_real_pesos = updates.costoRealPesos;
    if (updates.nReciboProveedor !== undefined) dbUpdates.n_recibo_proveedor = updates.nReciboProveedor ?? null;
    if (updates.fechaReciboProveedor !== undefined) dbUpdates.fecha_recibo_proveedor = updates.fechaReciboProveedor ?? null;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones ?? null;
    if (updates.estado !== undefined) dbUpdates.estado = updates.estado;
    else {
      // Auto-recalculate estado
      const current = await this.getById(id);
      if (current) {
        const merged = { ...current, ...updates };
        dbUpdates.estado = calcularEstadoLinea(merged);
      }
    }
    const { data, error } = await supabase
      .from('proveedor_lineas')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    if (error) return null;
    return rowToProveedorLinea(data as Record<string, unknown>);
  },

  async getById(id: string): Promise<ProveedorLinea | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('proveedor_lineas')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    return rowToProveedorLinea(data as Record<string, unknown>);
  },

  // Called when a factura cliente is created/updated — auto-link by carpeta
  async autoLinkByCarpeta(factura: Invoice): Promise<{ linked: number; multiple: boolean; lineaIds: string[] }> {
    if (!factura.nCarpeta) return { linked: 0, multiple: false, lineaIds: [] };
    const supabase = createClient();
    try {
      const lineas = await this.getByCarpeta(factura.nCarpeta);
      if (lineas.length === 0) return { linked: 0, multiple: false, lineaIds: [] };
      const { data: { user } } = await supabase.auth.getUser();
      const updates: Record<string, unknown> = {
        factura_cliente_id: factura.id,
        fecha_factura_cliente: factura.fecha ?? null,
        punto_venta_cliente: factura.puntoVenta ?? null,
        tipo_factura_cliente: factura.tipoComprobante ?? null,
        n_factura_cliente: `${factura.puntoVenta}-${factura.nFactura}`,
        monto_facturado_cliente: factura.importe ?? 0,
        cliente_nombre: factura.clienteNombre ?? null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const ids = lineas.map(l => l.id);
      for (const linea of lineas) {
        const merged = { ...linea, ...{
          facturaClienteId: factura.id,
          fechaFacturaCliente: factura.fecha,
          nFacturaCliente: `${factura.puntoVenta}-${factura.nFactura}`,
          montoFacturadoCliente: factura.importe,
        }};
        updates.estado = calcularEstadoLinea(merged);
        await supabase.from('proveedor_lineas').update(updates).eq('id', linea.id);
      }
      // Audit
      await logAudit({
        modulo: 'proveedores',
        accion: 'vinculacion-automatica',
        registroId: factura.id,
        registroTipo: 'factura',
        valorNuevo: `Factura ${factura.tipoComprobante} ${factura.puntoVenta}-${factura.nFactura} vinculada a ${lineas.length} línea(s) proveedor por carpeta ${factura.nCarpeta}`,
      });
      return { linked: lineas.length, multiple: lineas.length > 1, lineaIds: ids };
    } catch (e) {
      console.error('autoLinkByCarpeta error:', e);
      return { linked: 0, multiple: false, lineaIds: [] };
    }
  },
};

export const configuracionService = {
  async getAll(): Promise<ConfiguracionItem[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('configuracion_sistema')
        .select('clave, valor, descripcion')
        .order('clave');
      if (error) return [];
      return (data ?? []).map(r => ({
        clave: (r as Record<string, unknown>).clave as string,
        valor: (r as Record<string, unknown>).valor as string ?? '',
        descripcion: (r as Record<string, unknown>).descripcion as string ?? '',
      }));
    } catch {
      return [];
    }
  },

  async update(clave: string, valor: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('configuracion_sistema')
      .update({ valor, updated_at: new Date().toISOString() })
      .eq('clave', clave);
    if (error) throw error;
  },
};
export const proveedoresService = {
  async getAll(): Promise<Proveedor[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('proveedores')
        .select('*')
        .is('deleted_at', null)
        .order('nombre', { ascending: true });
      if (error) {
        console.error('proveedoresService.getAll error:', error);
        return [];
      }
      return (data ?? []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        nombre: (r.nombre as string) ?? '',
        cuit: r.cuit as string | undefined,
        tipo: r.tipo as string | undefined,
        contacto: r.contacto as string | undefined,
        email: r.email as string | undefined,
        telefono: r.telefono as string | undefined,
        condicionPago: r.condicion_pago as string | undefined,
        cuentaBancaria: r.cuenta_bancaria as string | undefined,
        observaciones: r.observaciones as string | undefined,
      }));
    } catch (e) {
      console.error('proveedoresService.getAll exception:', e);
      return [];
    }
  },

  async create(proveedor: Omit<Proveedor, 'id'>): Promise<Proveedor> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('proveedores')
      .insert({
        nombre: proveedor.nombre,
        cuit: proveedor.cuit ?? null,
        tipo: proveedor.tipo ?? null,
        contacto: proveedor.contacto ?? null,
        email: proveedor.email ?? null,
        telefono: proveedor.telefono ?? null,
        condicion_pago: proveedor.condicionPago ?? null,
        cuenta_bancaria: proveedor.cuentaBancaria ?? null,
        observaciones: proveedor.observaciones ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return {
      id: (data as Record<string, unknown>).id as string,
      nombre: (data as Record<string, unknown>).nombre as string,
    };
  },

  async softDelete(id: string, motivo: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('proveedores')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id ?? null,
        delete_reason: motivo,
        updated_by: user?.id ?? null,
      })
      .eq('id', id);
    if (error) return false;
    await logAudit({
      modulo: 'proveedores',
      accion: 'eliminar',
      registroId: id,
      registroTipo: 'proveedor',
      motivo,
    });
    return true;
  },
};

// ============================================================
// PROVEEDOR FACTURAS (múltiples facturas por línea/carpeta)
// ============================================================

// Helper: update historial_cheques from proveedor side
// A single e-cheq can be linked to multiple cobros (multiple client invoices).
// The proveedor side is always one-to-one per e-cheq number.
// This updates ALL cobro rows with the same n_echeq to add the proveedor info,
// OR inserts a new "pendiente-vinculo" row if no cobro rows exist yet.
async function upsertHistorialChequePago(
  supabase: ReturnType<typeof createClient>,
  nEcheq: string,
  data: {
    proveedorNombre: string | null;
    nFacturaProveedor: string | null;
    proveedorFacturaId: string | null;
    monto: number | null;
    fecha: string | null;
    userId: string | null;
  }
) {
  try {
    // Find ALL existing rows for this n_echeq (there may be multiple cobro rows)
    const { data: existingRows } = await supabase
      .from('historial_cheques')
      .select('id, estado')
      .eq('n_echeq', nEcheq);

    if (existingRows && existingRows.length > 0) {
      // Update ALL cobro rows with the proveedor info
      await supabase.from('historial_cheques').update({
        proveedor_nombre: data.proveedorNombre,
        n_factura_proveedor: data.nFacturaProveedor,
        proveedor_factura_id: data.proveedorFacturaId,
        estado: 'aplicado-a-proveedor',
        updated_at: new Date().toISOString(),
        updated_by: data.userId,
      }).eq('n_echeq', nEcheq);
    } else {
      // No cobro rows yet — insert a new record with only proveedor side known
      await supabase.from('historial_cheques').insert({
        n_echeq: nEcheq,
        monto: data.monto,
        fecha: data.fecha,
        proveedor_nombre: data.proveedorNombre,
        n_factura_proveedor: data.nFacturaProveedor,
        proveedor_factura_id: data.proveedorFacturaId,
        estado: 'pendiente-vinculo',
        created_by: data.userId,
        updated_by: data.userId,
      });
    }
  } catch (e) {
    console.warn('[upsertHistorialChequePago] non-blocking error:', e);
  }
}

export interface ProveedorFactura {
  id: string;
  lineaId: string;
  nFactura?: string;
  fechaFactura?: string;
  moneda: string;
  montoMoneda: number;
  tcEmision: number;
  costoEstimadoPesos: number;
  fechaPago?: string;
  tcPago: number;
  costoRealPesos: number;
  nRecibo?: string;
  fechaRecibo?: string;
  tipoPago?: string;       // 'Transferencia' | 'E-cheq' | 'Ambos' — legacy single payment
  nEcheqPago?: string;     // N° de e-cheq — legacy single payment
  mediosPago?: MedioPago[]; // Multiple payment lines (new)
  estado: string; // pendiente | pagada | anulada
  observaciones?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MedioPago {
  tipo: 'Transferencia' | 'E-cheq';
  monto?: number;
  nEcheq?: string;
}

function rowToProveedorFactura(row: Record<string, unknown>): ProveedorFactura {
  // Safely parse medios_pago — Supabase JSONB may return a parsed array OR a JSON string
  let mediosPago: MedioPago[] | undefined;
  if (row.medios_pago) {
    if (typeof row.medios_pago === 'string') {
      try { mediosPago = JSON.parse(row.medios_pago) as MedioPago[]; } catch { mediosPago = undefined; }
    } else if (Array.isArray(row.medios_pago)) {
      mediosPago = row.medios_pago as MedioPago[];
    } else {
      mediosPago = undefined;
    }
  }
  return {
    id: row.id as string,
    lineaId: row.linea_id as string,
    nFactura: row.n_factura as string | undefined,
    fechaFactura: row.fecha_factura as string | undefined,
    moneda: (row.moneda as string) ?? 'ARS',
    montoMoneda: Number(row.monto_moneda ?? 0),
    tcEmision: Number(row.tc_emision ?? 1),
    costoEstimadoPesos: Number(row.costo_estimado_pesos ?? 0),
    fechaPago: row.fecha_pago as string | undefined,
    tcPago: Number(row.tc_pago ?? 1),
    costoRealPesos: Number(row.costo_real_pesos ?? 0),
    nRecibo: row.n_recibo as string | undefined,
    fechaRecibo: row.fecha_recibo as string | undefined,
    tipoPago: row.tipo_pago as string | undefined,
    nEcheqPago: row.n_echeq_pago as string | undefined,
    mediosPago,
    estado: (row.estado as string) ?? 'pendiente',
    observaciones: row.observaciones as string | undefined,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

export const proveedorFacturasService = {
  async getByLinea(lineaId: string): Promise<ProveedorFactura[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('proveedor_facturas')
        .select('*')
        .eq('linea_id', lineaId)
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data ?? []).map(rowToProveedorFactura);
    } catch {
      return [];
    }
  },

  async create(factura: Omit<ProveedorFactura, 'id' | 'createdAt' | 'updatedAt'>): Promise<ProveedorFactura | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('proveedor_facturas')
      .insert({
        linea_id: factura.lineaId,
        n_factura: factura.nFactura ?? null,
        fecha_factura: factura.fechaFactura ?? null,
        moneda: factura.moneda,
        monto_moneda: factura.montoMoneda,
        tc_emision: factura.tcEmision,
        costo_estimado_pesos: factura.costoEstimadoPesos,
        fecha_pago: factura.fechaPago ?? null,
        tc_pago: factura.tcPago,
        costo_real_pesos: factura.costoRealPesos,
        n_recibo: factura.nRecibo ?? null,
        fecha_recibo: factura.fechaRecibo ?? null,
        tipo_pago: factura.tipoPago ?? null,
        n_echeq_pago: factura.nEcheqPago ?? null,
        medios_pago: factura.mediosPago && factura.mediosPago.length > 0 ? factura.mediosPago : null,
        estado: factura.estado,
        observaciones: factura.observaciones ?? null,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) { console.error('proveedorFacturasService.create error:', error); return null; }
    let result = rowToProveedorFactura(data as Record<string, unknown>);
    // ── Upsert historial_cheques for each e-cheq in mediosPago ────────────
    if (result) {
      const echeqsToLink: string[] = [];
      // New multi-payment lines
      if (factura.mediosPago && factura.mediosPago.length > 0) {
        for (const medio of factura.mediosPago) {
          if (medio.tipo === 'E-cheq' && medio.nEcheq?.trim()) {
            echeqsToLink.push(medio.nEcheq.trim());
          }
        }
      }
      // Legacy single echeq
      if (factura.nEcheqPago?.trim() && !echeqsToLink.includes(factura.nEcheqPago.trim())) {
        echeqsToLink.push(factura.nEcheqPago.trim());
      }
      for (const nEcheq of echeqsToLink) {
        await upsertHistorialChequePago(supabase, nEcheq, {
          proveedorNombre: null,
          nFacturaProveedor: factura.nFactura ?? null,
          proveedorFacturaId: result.id,
          monto: factura.costoRealPesos || factura.costoEstimadoPesos || null,
          fecha: factura.fechaPago ?? null,
          userId: user?.id ?? null,
        });
      }
    }
    return result;
  },

  async update(id: string, updates: Partial<ProveedorFactura>): Promise<ProveedorFactura | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const dbUpdates: Record<string, unknown> = { updated_by: user?.id ?? null, updated_at: new Date().toISOString() };
    if (updates.nFactura !== undefined) dbUpdates.n_factura = updates.nFactura ?? null;
    if (updates.fechaFactura !== undefined) dbUpdates.fecha_factura = updates.fechaFactura ?? null;
    if (updates.moneda !== undefined) dbUpdates.moneda = updates.moneda;
    if (updates.montoMoneda !== undefined) dbUpdates.monto_moneda = updates.montoMoneda;
    if (updates.tcEmision !== undefined) dbUpdates.tc_emision = updates.tcEmision;
    if (updates.costoEstimadoPesos !== undefined) dbUpdates.costo_estimado_pesos = updates.costoEstimadoPesos;
    if (updates.fechaPago !== undefined) dbUpdates.fecha_pago = updates.fechaPago ?? null;
    if (updates.tcPago !== undefined) dbUpdates.tc_pago = updates.tcPago;
    if (updates.costoRealPesos !== undefined) dbUpdates.costo_real_pesos = updates.costoRealPesos;
    if (updates.nRecibo !== undefined) dbUpdates.n_recibo = updates.nRecibo ?? null;
    if (updates.fechaRecibo !== undefined) dbUpdates.fecha_recibo = updates.fechaRecibo ?? null;
    if (updates.tipoPago !== undefined) dbUpdates.tipo_pago = updates.tipoPago ?? null;
    if (updates.nEcheqPago !== undefined) dbUpdates.n_echeq_pago = updates.nEcheqPago ?? null;
    if (updates.mediosPago !== undefined) dbUpdates.medios_pago = updates.mediosPago && updates.mediosPago.length > 0 ? updates.mediosPago : null;
    if (updates.estado !== undefined) dbUpdates.estado = updates.estado;
    if (updates.observaciones !== undefined) dbUpdates.observaciones = updates.observaciones ?? null;
    const { data, error } = await supabase
      .from('proveedor_facturas')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    if (error) return null;
    let result = rowToProveedorFactura(data as Record<string, unknown>);
    // ── Upsert historial_cheques for each e-cheq in mediosPago on update ──
    if (result) {
      const echeqsToLink: string[] = [];
      if (updates.mediosPago && updates.mediosPago.length > 0) {
        for (const medio of updates.mediosPago) {
          if (medio.tipo === 'E-cheq' && medio.nEcheq?.trim()) {
            echeqsToLink.push(medio.nEcheq.trim());
          }
        }
      }
      if (updates.nEcheqPago?.trim() && !echeqsToLink.includes(updates.nEcheqPago.trim())) {
        echeqsToLink.push(updates.nEcheqPago.trim());
      }
      for (const nEcheq of echeqsToLink) {
        await upsertHistorialChequePago(supabase, nEcheq, {
          proveedorNombre: null,
          nFacturaProveedor: updates.nFactura ?? result.nFactura ?? null,
          proveedorFacturaId: result.id,
          monto: updates.costoRealPesos ?? result.costoRealPesos ?? null,
          fecha: updates.fechaPago ?? result.fechaPago ?? null,
          userId: user?.id ?? null,
        });
      }
    }
    return result;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('proveedor_facturas').delete().eq('id', id);
    return !error;
  },
};

// ============================================================
// REMITO EXTRAS (campos manuales solo en Remitos)
// ============================================================

export interface RemitoExtra {
  id: string;
  operacionId: string;
  quienLlevo?: string;
  monto: number;
  pago: boolean;
  observaciones?: string;
  createdAt?: string;
  updatedAt?: string;
}

function rowToRemitoExtra(row: Record<string, unknown>): RemitoExtra {
  return {
    id: row.id as string,
    operacionId: row.operacion_id as string,
    quienLlevo: row.quien_llevo as string | undefined,
    monto: Number(row.monto ?? 0),
    pago: Boolean(row.pago),
    observaciones: row.observaciones as string | undefined,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

export const remitoExtrasService = {
  async getByOperacion(operacionId: string): Promise<RemitoExtra | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('remito_extras')
        .select('*')
        .eq('operacion_id', operacionId)
        .maybeSingle();
      if (error || !data) return null;
      return rowToRemitoExtra(data as Record<string, unknown>);
    } catch {
      return null;
    }
  },

  async upsert(operacionId: string, updates: Partial<Omit<RemitoExtra, 'id' | 'operacionId' | 'createdAt' | 'updatedAt'>>): Promise<RemitoExtra | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('remito_extras')
      .upsert({
        operacion_id: operacionId,
        quien_llevo: updates.quienLlevo ?? null,
        monto: updates.monto ?? 0,
        pago: updates.pago ?? false,
        observaciones: updates.observaciones ?? null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'operacion_id' })
      .select()
      .single();
    if (error) { console.error('remitoExtrasService.upsert error:', error); return null; }
    return rowToRemitoExtra(data as Record<string, unknown>);
  },
};

// ============================================================
// ECHEQS EN CARTERA — Service for cobranzas form
// ============================================================

export interface EcheqCartera {
  id: string;
  banco: string;
  bancoEmisor: string;
  nEcheq: string;
  clienteNombre: string;
  monto: number;
  montoUsado: number;
  montoDisponible: number;
  fechaEmision?: string;
  fechaVencimiento?: string;
  cuitLibrador?: string;
  estado: string;
  moneda: string;
  facturasAsociadas?: string;
  origenEcheq?: string; // 'cobranza' | 'historico' | 'manual'
  librador?: string;
}

function normalizeBank(banco: string): string {
  return (banco ?? '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizeEcheqNum(nEcheq: string): string {
  return (nEcheq ?? '')
    .trim()
    .replace(/[\s\-\.]/g, '');
}

function rowToEcheqCartera(row: Record<string, unknown>): EcheqCartera {
  const monto = Number(row.monto ?? row.importe ?? 0);
  const montoUsado = Number(row.monto_usado ?? 0);
  const montoDisponible = Math.max(0, monto - montoUsado);
  const banco = (row.banco_emisor ?? row.banco ?? '') as string;
  return {
    id: row.id as string,
    banco,
    bancoEmisor: banco,
    nEcheq: (row.n_echeq ?? '') as string,
    clienteNombre: (row.cliente_nombre ?? row.librador ?? '') as string,
    monto,
    montoUsado,
    montoDisponible,
    fechaEmision: row.fecha_emision as string | undefined,
    fechaVencimiento: row.fecha_vencimiento as string | undefined,
    cuitLibrador: row.cuit_librador as string | undefined,
    estado: (row.estado ?? 'en-cartera') as string,
    moneda: (row.moneda ?? 'ARS') as string,
    facturasAsociadas: row.factura_id ? String(row.factura_id) : undefined,
  };
}

export const echeqsCarteraService = {
  /**
   * Returns ALL e-cheqs visible in cartera (same query as caja-bancos page).
   * Excludes only: rechazado, anulado, cedido — and soft-deleted rows.
   * disponible_financiero = monto - SUM(aplicaciones financieras) — NOT cobranza applications.
   */
  async getAll(): Promise<EcheqCartera[]> {
    const supabase = createClient();
    try {
      const [echeqsResult, aplicacionesResult] = await Promise.all([
        supabase
          .from('echeqs')
          .select('*')
          .not('estado', 'in', '("rechazado","anulado","cedido")')
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('echeq_aplicaciones')
          .select('echeq_id, monto_aplicado, tipo_aplicacion')
          .not('tipo_aplicacion', 'eq', 'cobranza'),
      ]);
      const data = echeqsResult.data;
      const error = echeqsResult.error;
      const aplicacionesData = aplicacionesResult.data;
      if (error) {
        console.error('echeqsCarteraService.getAll error:', error);
        return [];
      }
      // Build financial usado map
      const financialUsadoMap: Record<string, number> = {};
      for (const ap of (aplicacionesData ?? []) as Array<{ echeq_id: string; monto_aplicado: number; tipo_aplicacion: string }>) {
        const eid = ap.echeq_id as string;
        if (!eid) continue;
        financialUsadoMap[eid] = (financialUsadoMap[eid] ?? 0) + Number(ap.monto_aplicado ?? 0);
      }
      return (data ?? []).map(r => {
        const row = r as Record<string, unknown>;
        const monto = Number(row.monto ?? row.importe ?? 0);
        const financialUsado = financialUsadoMap[row.id as string] ?? 0;
        const montoDisponible = Math.max(0, monto - financialUsado);
        const banco = (row.banco_emisor ?? row.banco ?? '') as string;
        return {
          id: row.id as string,
          banco,
          bancoEmisor: banco,
          nEcheq: (row.n_echeq ?? '') as string,
          clienteNombre: (row.cliente_nombre ?? row.librador ?? '') as string,
          monto,
          montoUsado: financialUsado,
          montoDisponible,
          fechaEmision: row.fecha_emision as string | undefined,
          fechaVencimiento: row.fecha_vencimiento as string | undefined,
          cuitLibrador: row.cuit_librador as string | undefined,
          estado: (row.estado ?? 'en-cartera') as string,
          moneda: (row.moneda ?? 'ARS') as string,
          facturasAsociadas: row.factura_id ? String(row.factura_id) : undefined,
        };
      });
    } catch (e) {
      console.error('echeqsCarteraService.getAll exception:', e);
      return [];
    }
  },

  /**
   * Returns ALL e-cheqs including cedido/depositado for full traceability view.
   */
  async getAllConHistorial(): Promise<EcheqCartera[]> {
    const supabase = createClient();
    try {
      const [echeqsResult, aplicacionesResult] = await Promise.all([
        supabase
          .from('echeqs')
          .select('*')
          .not('estado', 'in', '("rechazado","anulado")')
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('echeq_aplicaciones')
          .select('echeq_id, monto_aplicado, tipo_aplicacion')
          .not('tipo_aplicacion', 'eq', 'cobranza'),
      ]);
      const data = echeqsResult.data;
      const error = echeqsResult.error;
      const aplicacionesData = aplicacionesResult.data;
      if (error) {
        console.error('echeqsCarteraService.getAllConHistorial error:', error);
        return [];
      }
      // Build financial usado map
      const financialUsadoMap: Record<string, number> = {};
      for (const ap of (aplicacionesData ?? []) as Array<{ echeq_id: string; monto_aplicado: number; tipo_aplicacion: string }>) {
        const eid = ap.echeq_id as string;
        if (!eid) continue;
        financialUsadoMap[eid] = (financialUsadoMap[eid] ?? 0) + Number(ap.monto_aplicado ?? 0);
      }
      return (data ?? []).map(r => {
        const row = r as Record<string, unknown>;
        const monto = Number(row.monto ?? row.importe ?? 0);
        const financialUsado = financialUsadoMap[row.id as string] ?? 0;
        const montoDisponible = Math.max(0, monto - financialUsado);
        const banco = (row.banco_emisor ?? row.banco ?? '') as string;
        return {
          id: row.id as string,
          banco,
          bancoEmisor: banco,
          nEcheq: (row.n_echeq ?? '') as string,
          clienteNombre: (row.cliente_nombre ?? row.librador ?? '') as string,
          monto,
          montoUsado: financialUsado,
          montoDisponible,
          fechaEmision: row.fecha_emision as string | undefined,
          fechaVencimiento: row.fecha_vencimiento as string | undefined,
          cuitLibrador: row.cuit_librador as string | undefined,
          estado: (row.estado ?? 'en-cartera') as string,
          moneda: (row.moneda ?? 'ARS') as string,
          facturasAsociadas: row.factura_id ? String(row.factura_id) : undefined,
        };
      });
    } catch (e) {
      console.error('echeqsCarteraService.getAllConHistorial exception:', e);
      return [];
    }
  },

  /**
   * Adds a historical/initial e-cheq to cartera without requiring a cobranza.
   * These are e-cheqs the user already had before the system was set up.
   */
  async addHistorico(data: {
    banco: string;
    nEcheq: string;
    librador: string;
    cuitLibrador?: string;
    fechaEmision?: string;
    fechaVencimiento?: string;
    monto: number;
    moneda?: string;
    observaciones?: string;
  }): Promise<EcheqCartera> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    const { data: inserted, error } = await supabase
      .from('echeqs')
      .insert({
        banco: data.banco,
        banco_emisor: data.banco,
        n_echeq: data.nEcheq,
        librador: data.librador,
        cliente_nombre: data.librador,
        cuit_librador: data.cuitLibrador ?? null,
        fecha_emision: data.fechaEmision ?? null,
        fecha_vencimiento: data.fechaVencimiento ?? null,
        monto: data.monto,
        importe: data.monto,
        monto_usado: 0,
        moneda: data.moneda ?? 'ARS',
        estado: 'en-cartera',
        origen_echeq: 'historico',
        observaciones: data.observaciones ?? 'E-cheq histórico / inicial cargado manualmente',
        created_by: user?.id ?? null,
      })
      .select('*')
      .single();

    if (error) throw error;

    // Audit
    try {
      await supabase.from('auditoria').insert({
        modulo: 'echeqs',
        accion: 'alta_historico',
        registro_id: inserted.id,
        registro_tipo: 'echeq',
        motivo: `E-cheq histórico cargado manualmente: ${data.banco} N°${data.nEcheq} $${data.monto.toLocaleString('es-AR')}`,
        usuario_nombre: user?.email ?? 'Sistema',
      });
    } catch (_) { /* non-blocking audit */ }

    return rowToEcheqCartera(inserted as Record<string, unknown>);
  },

  /**
   * Finds an existing e-cheq by banco + nEcheq with full normalization.
   * Returns null if not found or if saldo is 0 (but still returns with montoDisponible=0).
   * Normalization: Galicia = galicia = GALICIA, 00616684 preserved as string.
   */
  async findExisting(banco: string, nEcheq: string): Promise<EcheqCartera | null> {
    if (!banco?.trim() || !nEcheq?.trim()) return null;
    try {
      const all = await echeqsCarteraService.getAll();
      const normBanco = normalizeBank(banco);
      const normNum = normalizeEcheqNum(nEcheq);

      const found = all.find(e => {
        const eBanco = normalizeBank(e.banco);
        const eNum = normalizeEcheqNum(e.nEcheq);
        // Match by normalized bank + normalized number
        // Also try matching without leading zeros on either side
        const numMatch =
          eNum === normNum ||
          eNum.replace(/^0+/, '') === normNum.replace(/^0+/, '');
        return eBanco === normBanco && numMatch;
      });

      return found ?? null;
    } catch (e) {
      console.error('echeqsCarteraService.findExisting exception:', e);
      return null;
    }
  },
};
export type EstadoManual = 'automatico' | 'cerrada' | 'cancelada' | 'abierta' | 'en_curso' | 'lista_para_cerrar';

// ============================================================
// REMITOS SERVICE — Full implementation
// ============================================================

export interface Remito {
  id: string;
  fecha?: string | null;
  mesAnio: string;
  cliente?: string | null;
  nDespacho?: string | null;
  carpetaN?: string | null;
  carpetaTransporte?: string | null;
  nRemito?: string | null;
  referencia?: string | null;
  bultos?: number | null;
  kilos?: number | null;
  cbm?: number | null;
  quienLlevo?: string | null;
  precio?: number | null;
  pagado: boolean;
  observaciones?: string | null;
  estado: string; // 'activo' | 'anulado' | 'revisar' | 'sin-vinculo'
  operacionId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

function rowToRemito(row: Record<string, unknown>): Remito {
  return {
    id: row.id as string,
    fecha: row.fecha as string | null,
    mesAnio: (row.mes_anio as string) ?? '',
    cliente: row.cliente as string | null,
    nDespacho: row.n_despacho as string | null,
    carpetaN: row.carpeta_n as string | null,
    carpetaTransporte: row.carpeta_transporte as string | null,
    nRemito: row.n_remito as string | null,
    referencia: row.referencia as string | null,
    bultos: row.bultos != null ? Number(row.bultos) : null,
    kilos: row.kilos != null ? Number(row.kilos) : null,
    cbm: row.cbm != null ? Number(row.cbm) : null,
    quienLlevo: row.quien_llevo as string | null,
    precio: row.precio != null ? Number(row.precio) : null,
    pagado: Boolean(row.pagado),
    observaciones: row.observaciones as string | null,
    estado: (row.estado as string) ?? 'activo',
    operacionId: row.operacion_id as string | null,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

export const remitosService = {
  async getAll(): Promise<Remito[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('remitos')
        .select('*')
        .order('mes_anio', { ascending: true })
        .order('n_remito', { ascending: true });
      if (error) {
        console.error('remitosService.getAll error:', error);
        return [];
      }
      return (data ?? []).map(row => rowToRemito(row as Record<string, unknown>));
    } catch (e) {
      console.error('remitosService.getAll exception:', e);
      return [];
    }
  },

  async upsert(remito: Partial<Remito> & { id?: string }): Promise<Remito | null> {
    const supabase = createClient();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const payload: Record<string, unknown> = {
        mes_anio: remito.mesAnio,
        fecha: remito.fecha ?? null,
        cliente: remito.cliente ?? null,
        n_despacho: remito.nDespacho ?? null,
        carpeta_n: remito.carpetaN ?? null,
        carpeta_transporte: remito.carpetaTransporte ?? null,
        n_remito: remito.nRemito ?? null,
        referencia: remito.referencia ?? null,
        bultos: remito.bultos ?? null,
        kilos: remito.kilos ?? null,
        cbm: remito.cbm ?? null,
        quien_llevo: remito.quienLlevo ?? null,
        precio: remito.precio ?? null,
        pagado: remito.pagado ?? false,
        observaciones: remito.observaciones ?? null,
        estado: remito.estado ?? 'activo',
        operacion_id: remito.operacionId ?? null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      let result;
      if (remito.id) {
        const { data, error } = await supabase
          .from('remitos')
          .update(payload)
          .eq('id', remito.id)
          .select()
          .single();
        if (error) { console.error('remitosService.upsert update error:', error); return null; }
        result = data;
      } else {
        payload.created_by = user?.id ?? null;
        payload.created_at = new Date().toISOString();
        const { data, error } = await supabase
          .from('remitos')
          .insert(payload)
          .select()
          .single();
        if (error) { console.error('remitosService.upsert insert error:', error); return null; }
        result = data;
      }
      return result ? rowToRemito(result as Record<string, unknown>) : null;
    } catch (e) {
      console.error('remitosService.upsert exception:', e);
      return null;
    }
  },
};
const ESTADO_MANUAL_OPTIONS: any = null;

export { ESTADO_MANUAL_OPTIONS };

// ============================================================
// ARCHIVOS MODULE — v2 with Carpetas (manual folders)
// ============================================================

export const TIPOS_ARCHIVO = [
  'Proforma',
  'Packing List',
  'Factura de proveedor',
  'Guía',
  'Cotización',
  'Presupuesto',
  'Otro',
] as const;

export type TipoArchivo = typeof TIPOS_ARCHIVO[number] | string;

export interface Carpeta {
  id: string;
  clienteId?: string | null;
  parentId?: string | null;
  nombre: string;
  descripcion?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Archivo {
  id: string;
  clienteId?: string | null;
  operacionId?: string | null;
  carpetaId?: string | null;
  nCarpeta?: string | null;
  nombre: string;
  tipo: TipoArchivo;
  storagePath: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  subidoPor?: string | null;
  subfolderPath?: string | null;
  driveFileId?: string | null;
  driveWebLink?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ImportDecision {
  id: string;
  folderName: string;
  decisionType: 'cliente' | 'operacion' | 'ignorar';
  clienteId?: string | null;
  operacionId?: string | null;
  subfolderName?: string | null;
  createdAt?: string;
}

function rowToCarpeta(row: Record<string, unknown>): Carpeta {
  return {
    id: row.id as string,
    clienteId: row.cliente_id as string | null,
    parentId: row.parent_id as string | null,
    nombre: (row.nombre as string) ?? '',
    descripcion: row.descripcion as string | null,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

function rowToArchivo(row: Record<string, unknown>): Archivo {
  return {
    id: row.id as string,
    clienteId: row.cliente_id as string | null,
    operacionId: row.operacion_id as string | null,
    carpetaId: row.carpeta_id as string | null,
    nCarpeta: row.n_carpeta as string | null,
    nombre: (row.nombre as string) ?? '',
    tipo: (row.tipo as string) ?? 'Otro',
    storagePath: (row.storage_path as string) ?? '',
    mimeType: row.mime_type as string | null,
    sizeBytes: row.size_bytes != null ? Number(row.size_bytes) : null,
    subidoPor: row.subido_por as string | null,
    subfolderPath: row.subfolder_path as string | null,
    driveFileId: row.drive_file_id as string | null,
    driveWebLink: row.drive_web_link as string | null,
    createdAt: row.created_at as string | undefined,
    updatedAt: row.updated_at as string | undefined,
  };
}

function rowToImportDecision(row: Record<string, unknown>): ImportDecision {
  return {
    id: row.id as string,
    folderName: row.folder_name as string,
    decisionType: row.decision_type as 'cliente' | 'operacion' | 'ignorar',
    clienteId: row.cliente_id as string | null,
    operacionId: row.operacion_id as string | null,
    subfolderName: row.subfolder_name as string | null,
    createdAt: row.created_at as string | undefined,
  };
}

const ARCHIVOS_BUCKET = 'ercomex-archivos';

// ============================================================
// CARPETAS SERVICE
// ============================================================
export const carpetasService = {
  async getByCliente(clienteId: string): Promise<Carpeta[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('carpetas')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('nombre', { ascending: true });
      if (error) { console.error('carpetasService.getByCliente error:', error); return []; }
      return (data ?? []).map(r => rowToCarpeta(r as Record<string, unknown>));
    } catch (e) { console.error('carpetasService.getByCliente exception:', e); return []; }
  },

  /** Get all General/Interno carpetas (cliente_id IS NULL) */
  async getGeneral(): Promise<Carpeta[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('carpetas')
        .select('*')
        .is('cliente_id', null)
        .order('nombre', { ascending: true });
      if (error) { console.error('carpetasService.getGeneral error:', error); return []; }
      return (data ?? []).map(r => rowToCarpeta(r as Record<string, unknown>));
    } catch (e) { console.error('carpetasService.getGeneral exception:', e); return []; }
  },

  async create(clienteId: string | null, nombre: string, parentId?: string | null, descripcion?: string): Promise<Carpeta | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('carpetas')
        .insert({
          cliente_id: clienteId ?? null,
          parent_id: parentId ?? null,
          nombre: nombre.trim(),
          descripcion: descripcion ?? null,
        })
        .select()
        .single();
      if (error) { console.error('carpetasService.create error:', error); return null; }
      return data ? rowToCarpeta(data as Record<string, unknown>) : null;
    } catch (e) { console.error('carpetasService.create exception:', e); return null; }
  },

  async rename(id: string, nombre: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('carpetas')
        .update({ nombre: nombre.trim(), updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) { console.error('carpetasService.rename error:', error); return false; }
      return true;
    } catch (e) { console.error('carpetasService.rename exception:', e); return false; }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('carpetas').delete().eq('id', id);
      if (error) { console.error('carpetasService.delete error:', error); return false; }
      return true;
    } catch (e) { console.error('carpetasService.delete exception:', e); return false; }
  },
};

// ============================================================
// ARCHIVOS SERVICE
// ============================================================
export const archivosService = {
  /** Get all archivos for a specific operacion */
  async getByOperacion(operacionId: string): Promise<Archivo[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('archivos')
        .select('*')
        .eq('operacion_id', operacionId)
        .order('created_at', { ascending: false });
      if (error) { console.error('archivosService.getByOperacion error:', error); return []; }
      return (data ?? []).map(r => rowToArchivo(r as Record<string, unknown>));
    } catch (e) { console.error('archivosService.getByOperacion exception:', e); return []; }
  },

  /** Get all archivos for a specific manual carpeta */
  async getByCarpeta(carpetaId: string): Promise<Archivo[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('archivos')
        .select('*')
        .eq('carpeta_id', carpetaId)
        .order('created_at', { ascending: false });
      if (error) { console.error('archivosService.getByCarpeta error:', error); return []; }
      return (data ?? []).map(r => rowToArchivo(r as Record<string, unknown>));
    } catch (e) { console.error('archivosService.getByCarpeta exception:', e); return []; }
  },

  /** Get all archivos for a specific cliente (including all their operations and manual folders) */
  async getByCliente(clienteId: string): Promise<Archivo[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('archivos')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('created_at', { ascending: false });
      if (error) { console.error('archivosService.getByCliente error:', error); return []; }
      return (data ?? []).map(r => rowToArchivo(r as Record<string, unknown>));
    } catch (e) { console.error('archivosService.getByCliente exception:', e); return []; }
  },

  /** Search archivos by nombre, cliente name (via join), or n_carpeta */
  async search(query: string): Promise<Archivo[]> {
    const supabase = createClient();
    try {
      const q = query.trim().toLowerCase();
      if (!q) {
        const { data, error } = await supabase
          .from('archivos')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(500);
        if (error) return [];
        return (data ?? []).map(r => rowToArchivo(r as Record<string, unknown>));
      }
      const { data, error } = await supabase
        .from('archivos')
        .select('*')
        .or(`nombre.ilike.%${q}%,n_carpeta.ilike.%${q}%,tipo.ilike.%${q}%`)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) { console.error('archivosService.search error:', error); return []; }
      return (data ?? []).map(r => rowToArchivo(r as Record<string, unknown>));
    } catch (e) { console.error('archivosService.search exception:', e); return []; }
  },

  /** Upload one or more files to Supabase Storage and insert metadata records */
  async upload(files: File[], meta: {
    clienteId?: string;
    operacionId?: string;
    carpetaId?: string;
    folderId?: string;
    nCarpeta?: string;
    tipo: TipoArchivo;
    subidoPor?: string;
  }): Promise<Archivo[]> {
    // A partir de esta versión, los archivos se suben a Google Drive
    // (a la carpeta real del cliente/operación) en vez de a Supabase Storage.
    // La ruta /api/archivos/upload-drive hace el trabajo del lado del servidor,
    // porque las credenciales de Drive nunca deben viajar al navegador.
    const results: Archivo[] = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (meta.clienteId) formData.append('clienteId', meta.clienteId);
        if (meta.operacionId) formData.append('operacionId', meta.operacionId);
        if (meta.carpetaId) formData.append('carpetaId', meta.carpetaId);
        if (meta.folderId) formData.append('folderId', meta.folderId);
        if (meta.nCarpeta) formData.append('nCarpeta', meta.nCarpeta);
        formData.append('tipo', meta.tipo);
        if (meta.subidoPor) formData.append('subidoPor', meta.subidoPor);

        const res = await fetch('/api/archivos/upload-drive', {
          method: 'POST',
          body: formData,
        });
        const json = await res.json();

        if (!res.ok) {
          console.error('archivosService.upload drive error:', json?.error);
          continue;
        }
        if (json?.archivo) results.push(rowToArchivo(json.archivo as Record<string, unknown>));
      } catch (e) {
        console.error('archivosService.upload exception for file:', file.name, e);
      }
    }
    return results;
  },

  /** Upload a file from a File object with a custom storage path (for bulk import) */
  async uploadWithPath(file: File, storagePath: string, meta: {
    clienteId?: string;
    operacionId?: string;
    carpetaId?: string;
    nCarpeta?: string;
    tipo: TipoArchivo;
    subidoPor?: string;
    nombre?: string;
    subfolderPath?: string;
  }): Promise<{ archivo: Archivo | null; error: string | null }> {
    const supabase = createClient();
    try {
      const { error: uploadError } = await supabase.storage
        .from(ARCHIVOS_BUCKET)
        .upload(storagePath, file, { upsert: false });

      if (uploadError) {
        console.error('archivosService.uploadWithPath storage error:', uploadError);
        return { archivo: null, error: `Storage: ${uploadError.message}` };
      }

      const { data: inserted, error: insertError } = await supabase
        .from('archivos')
        .insert({
          cliente_id: meta.clienteId ?? null,
          operacion_id: meta.operacionId ?? null,
          carpeta_id: meta.carpetaId ?? null,
          n_carpeta: meta.nCarpeta ?? null,
          nombre: meta.nombre ?? file.name,
          tipo: meta.tipo,
          storage_path: storagePath,
          mime_type: file.type || null,
          size_bytes: file.size,
          subido_por: meta.subidoPor ?? null,
          subfolder_path: meta.subfolderPath ?? null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (insertError) {
        console.error('archivosService.uploadWithPath insert error:', insertError);
        // File was uploaded to storage but DB insert failed — try to clean up storage
        try {
          await supabase.storage.from(ARCHIVOS_BUCKET).remove([storagePath]);
        } catch (cleanupErr) {
          console.error('archivosService.uploadWithPath cleanup error:', cleanupErr);
        }
        return { archivo: null, error: `DB: ${insertError.message} (code: ${insertError.code})` };
      }
      return { archivo: inserted ? rowToArchivo(inserted as Record<string, unknown>) : null, error: null };
    } catch (e) {
      console.error('archivosService.uploadWithPath exception:', e);
      return { archivo: null, error: String(e) };
    }
  },

  /**
   * Fast indexed check: does a file with this (clienteId, subfolderPath, nombre) already exist?
   * Uses the composite index idx_archivos_resume_check for O(log n) lookup.
   */
  async checkExists(params: {
    clienteId: string | null;
    subfolderPath: string | null;
    nombre: string;
  }): Promise<boolean> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('archivos')
        .select('id', { count: 'exact', head: true })
        .eq('nombre', params.nombre);

      if (params.clienteId) {
        query = query.eq('cliente_id', params.clienteId);
      } else {
        query = query.is('cliente_id', null);
      }

      if (params.subfolderPath) {
        query = query.eq('subfolder_path', params.subfolderPath);
      } else {
        query = query.is('subfolder_path', null);
      }

      const { count, error } = await query;
      if (error) { console.error('archivosService.checkExists error:', error); return false; }
      return (count ?? 0) > 0;
    } catch (e) {
      console.error('archivosService.checkExists exception:', e);
      return false;
    }
  },

  /** Get a signed download URL for a file */
  async getDownloadUrl(storagePath: string): Promise<string | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase.storage
        .from(ARCHIVOS_BUCKET)
        .createSignedUrl(storagePath, 60 * 60); // 1 hour
      if (error) { console.error('archivosService.getDownloadUrl error:', error); return null; }
      return data?.signedUrl ?? null;
    } catch (e) { console.error('archivosService.getDownloadUrl exception:', e); return null; }
  },

  /** Delete an archivo record and its storage object */
  async delete(archivo: Archivo): Promise<boolean> {
    const supabase = createClient();
    try {
      await supabase.storage.from(ARCHIVOS_BUCKET).remove([archivo.storagePath]);
      const { error } = await supabase.from('archivos').delete().eq('id', archivo.id);
      if (error) { console.error('archivosService.delete error:', error); return false; }
      return true;
    } catch (e) { console.error('archivosService.delete exception:', e); return false; }
  },

  /** Move a file to a different carpeta (or to root if carpetaId is null) */
  async moveToFolder(archivoId: string, carpetaId: string | null): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('archivos')
        .update({ carpeta_id: carpetaId, updated_at: new Date().toISOString() })
        .eq('id', archivoId);
      if (error) { console.error('archivosService.moveToFolder error:', error); return false; }
      return true;
    } catch (e) { console.error('archivosService.moveToFolder exception:', e); return false; }
  },

  /** Get all archivos for General/Interno (cliente_id IS NULL) */
  async getGeneral(): Promise<Archivo[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('archivos')
        .select('*')
        .is('cliente_id', null)
        .order('created_at', { ascending: false });
      if (error) { console.error('archivosService.getGeneral error:', error); return []; }
      return (data ?? []).map(r => rowToArchivo(r as Record<string, unknown>));
    } catch (e) { console.error('archivosService.getGeneral exception:', e); return []; }
  },
};

// ============================================================
// IMPORT DECISIONS SERVICE
// ============================================================
export const importDecisionsService = {
  async getAll(): Promise<ImportDecision[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('import_decisions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) { console.error('importDecisionsService.getAll error:', error); return []; }
      return (data ?? []).map(r => rowToImportDecision(r as Record<string, unknown>));
    } catch (e) { console.error('importDecisionsService.getAll exception:', e); return []; }
  },

  async upsert(folderName: string, decision: {
    decisionType: 'cliente' | 'operacion' | 'ignorar';
    clienteId?: string;
    operacionId?: string;
    subfolderName?: string;
  }): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('import_decisions')
        .upsert({
          folder_name: folderName,
          decision_type: decision.decisionType,
          cliente_id: decision.clienteId ?? null,
          operacion_id: decision.operacionId ?? null,
          subfolder_name: decision.subfolderName ?? null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'folder_name' });
      if (error) { console.error('importDecisionsService.upsert error:', error); return false; }
      return true;
    } catch (e) { console.error('importDecisionsService.upsert exception:', e); return false; }
  },
};